(function () {
  'use strict';

  /* ---------- Content ----------
     Camera specs: AutoChimp catalogue. Trailer styles, coils and cables: the Trailer Camera Reference (2 Nov 2026),
     which marks its coil and cable picks as recommended, not yet confirmed. */
  const RAD = Math.PI / 180;
  const CAM_SCALE = 2; // cameras are drawn at 2x so they show up next to a 5 m vehicle
  const CAMS = [
    { id: 'reverse', chip: 'Reverse', name: 'Reverse camera', mirror: true, guides: true },
    { id: 'front', chip: 'Front', name: 'Front camera', mirror: false },
    { id: 'tray', chip: 'Tray', name: 'Tray camera', mirror: true },
    { id: 'side', chip: 'Side', name: 'Side cameras', mirror: true },
    { id: 'under', chip: 'Under', chipLong: 'body', name: 'Underbody cameras', mirror: false },
    { id: 'trailer', chip: 'Trailer', name: 'Trailer camera', mirror: true, guides: true },
    { id: 'tside', chip: 'Trailer sides', name: 'Trailer side cameras', mirror: true, noChip: true }
  ];
  // Every camera position in the scene. Side cameras come in pairs: L is the passenger (kerb) side, R the driver's side.
  const UNITS = [
    { id: 'reverse', cam: 'reverse', label: 'Reverse camera' },
    { id: 'front', cam: 'front', label: 'Front camera' },
    { id: 'tray', cam: 'tray', label: 'Tray camera' },
    { id: 'sideL', cam: 'side', side: 'L', label: 'Passenger side' },
    { id: 'sideR', cam: 'side', side: 'R', label: 'Driver side' },
    { id: 'underB', cam: 'under', side: 'B', label: 'Rear axle' },
    { id: 'underF', cam: 'under', side: 'F', label: 'Front axle' },
    { id: 'trailer', cam: 'trailer', label: 'Trailer camera' },
    { id: 'tsideL', cam: 'tside', side: 'L', label: 'Trailer side · soon' },
    { id: 'tsideR', cam: 'tside', side: 'R', label: 'Trailer side · soon' }
  ];
  const DESC = {
    reverse: 'Under the back of the tray. Comes on when you reverse.',
    reverseWagon: 'On the rear door, above the number plate. Comes on when you reverse.',
    front: 'On the bullbar, showing what’s just ahead of the bonnet.',
    tray: 'High on the headboard, looking back over your load.',
    trayToolbox: 'On the back of the toolbox, looking back over the tray.',
    trayCanopy: 'Inside the canopy, up front, so you can keep an eye on your gear.',
    side: 'Hanging under each mirror, looking back along the sides.',
    under: 'Under the vehicle, watching the axles, suspension and the ground you’re driving over.',
    tside: 'Near the front of the trailer, looking back along each side. Side cameras are on the way, so this preview uses a 120° lens.'
  };
  const TYPES = {
    pro: { label: 'Pro HD', spec: '170°', fov: [170], note: '170° lens' },
    hd: { label: 'Heavy Duty', spec: '120° · IR', fov: [120], note: '120° lens with infrared night vision' },
    twin: { label: 'Twin', spec: '120° + 60°', fov: [120, 60], note: 'Two lenses: 120° wide and 60° long' },
    soon: { label: 'Side camera', spec: 'Coming soon', fov: [120], note: 'Preview with a 120° lens until the specs are final' }
  };
  const TYPE_KEYS = ['pro', 'hd', 'twin'];
  const SIDE_TYPES = ['pro', 'hd'];
  const COILS = {
    S: { label: 'Small', sku: 'AC-SUZI-COIL-KIT-S', coiled: '150 mm', stretch: 1.5, gap: 0.9, turns: 20 },
    M: { label: 'Medium', sku: 'AC-SUZI-COIL-KIT-M', coiled: '300 mm', stretch: 3, gap: 1.8, turns: 34 },
    L: { label: 'Large', sku: 'AC-SUZI-COIL-KIT-L', coiled: '500 mm', stretch: 5, gap: 3, turns: 50 }
  };
  const COIL_RULE = 'To size one for your trailer: measure from the tow-bar socket to the drawbar socket with the trailer straight, and pick a coil that stretches to at least 1.7× that gap.';
  const GROUPS = ['Open trailers', 'Enclosed, floats, campers and caravans', 'Big caravans'];
  const OPEN_SETUP = {
    coil: ['None', 'Plug break point in the kit. Small if you go Heavy Duty.'], carSide: ['7 m', 'Camera cable']
  };
  const HD_SETUP = { carSide: ['5 m', 'To the screen adapter'], extra: ['Nothing', '5 m + 10 m in the kit'] };
  const TRAILERS = [
    { id: 'box', short: 'Box trailer', g: 0, label: 'Box trailer', size: '6×4 to 8×5 ft · about 3–4 m overall', kind: 'box', L: 2.13, W: 1.52, bar: 1.30, socket: 0.36, axles: 1,
      type: 'pro', coil: null, pos: ['high', 'low'], def: 'high',
      setup: { camera: ['Pro HD', 'AC-PRO-CAM-KIT-TRAILER'], coil: OPEN_SETUP.coil, trailer: ['8 m', 'About a 5 m run'], car: OPEN_SETUP.carSide, extra: ['Nothing', 'Second 8 m is spare'] } },
    { id: 'trade', short: 'Tool trailer', g: 0, label: 'Tool and trade trailer', size: '8×5 to 10×6 ft cage · about 4–5 m overall', kind: 'cage', L: 2.74, W: 1.65, bar: 1.50, socket: 0.36, axles: 2,
      type: 'pro', coil: null, pos: ['high', 'low'], def: 'high',
      setup: { camera: ['Pro HD', 'AC-PRO-CAM-KIT-TRAILER'], coil: OPEN_SETUP.coil, trailer: ['8 m', 'About a 6–7 m run'], car: OPEN_SETUP.carSide, extra: ['Nothing', 'Second 8 m is spare'] } },
    { id: 'boat', short: 'Boat trailer', g: 0, label: 'Boat trailer', size: 'Boats 4.5–6.5 m · about 5–8 m overall', kind: 'boat', L: 5.4, W: 2.05, bar: 1.0, socket: 0.36, axles: 1,
      type: 'pro', coil: null, pos: ['low'], def: 'low',
      setup: { camera: ['Pro HD', 'AC-PRO-CAM-KIT-TRAILER'], coil: OPEN_SETUP.coil, trailer: ['8 m', 'Both 8 m over about 7.5 m overall'], car: OPEN_SETUP.carSide, extra: ['Nothing', 'Vehicle trailer kits ship one 8 m: add AC-PRO-EXTN-8M over 7.5 m'] } },
    { id: 'enclosed', short: 'Enclosed trailer', g: 1, label: 'Enclosed trailer', size: 'Van or cargo · about 3–5 m overall · camera inside', kind: 'enclosed', L: 3.0, W: 1.75, H: 2.05, bar: 1.35, socket: 0.36, axles: 2,
      type: 'hd', coil: 'S', pos: ['inside', 'high', 'low'], def: 'inside',
      setup: { camera: ['Heavy Duty', 'IR night vision for inside'], coil: ['Small', 'Stretches to 1.5 m · socket right behind the coupling'], trailer: ['10 m', '5 m up to about 3.5 m long'], car: HD_SETUP.carSide, extra: HD_SETUP.extra } },
    { id: 'float', short: 'Horse float', g: 1, label: 'Horse float', size: '2-horse · about 4.5–5.5 m overall · camera inside', kind: 'float', L: 3.7, W: 1.9, H: 2.35, bar: 1.50, socket: 0.78, axles: 2,
      type: 'hd', coil: 'M', pos: ['inside', 'high', 'low'], def: 'inside',
      setup: { camera: ['Heavy Duty', 'IR night vision for inside'], coil: ['Medium', 'Stretches to 3 m · socket sits back past the jockey wheel'], trailer: ['10 m', 'About a 7–8 m run'], car: HD_SETUP.carSide, extra: HD_SETUP.extra } },
    { id: 'camper', short: 'Camper trailer', g: 1, label: 'Camper trailer', size: 'Soft or hard floor · about 3.5–5 m overall', kind: 'camper', L: 3.2, W: 1.85, H: 1.30, bar: 1.70, socket: 0.75, axles: 1,
      type: 'hd', coil: 'M', pos: ['high', 'low'], def: 'high',
      setup: { camera: ['Heavy Duty', 'AC-HEAVY-CAM-KIT-TRAILER'], coil: ['Medium', 'Stretches to 3 m · off-road hitch and drawbar toolbox'], trailer: ['10 m', '5 m up to about 4 m long'], car: HD_SETUP.carSide, extra: HD_SETUP.extra } },
    { id: 'vanS', short: 'Small caravan', g: 1, label: 'Small caravan or pop-top', size: '14–18 ft body · about 6–7.5 m overall', kind: 'van', L: 4.9, W: 2.2, H: 2.75, bar: 1.75, socket: 0.85, axles: 1,
      type: 'hd', coil: 'M', pos: ['high', 'low'], def: 'high',
      setup: { camera: ['Heavy Duty', 'AC-HEAVY-CAM-KIT-TRAILER'], coil: ['Medium', 'Stretches to 3 m · gas bottles and stone guard on the A-frame'], trailer: ['10 m', 'About a 9–10 m run'], car: HD_SETUP.carSide, extra: HD_SETUP.extra } },
    { id: 'vanL', short: 'Tandem caravan', g: 2, label: 'Large tandem caravan', size: '19–22 ft body · about 8–9.5 m overall', kind: 'van', L: 6.4, W: 2.35, H: 2.95, bar: 2.20, socket: 1.75, axles: 2,
      type: 'twin', coil: 'L', pos: ['high', 'low'], def: 'high',
      setup: { camera: ['Two Heavy Duty', 'AC-HEAVY-TWIN-CAM-KIT'], coil: ['Large', 'Twin channel, stretches to 5 m · long A-frame, toolbox, often a weight-distribution hitch'], trailer: ['15 m each', '10 m + 5 m joined · 10 m under about 8 m'], car: ['5 m each', 'To the screen adapters'], extra: ['5 m per camera', 'AC-HEAVY-5M × 2'] } },
    { id: 'offroad', short: 'Off-road caravan', g: 2, label: 'Premium off-road caravan', size: '20–23 ft body · about 8.5–10 m overall', kind: 'van', L: 6.7, W: 2.45, H: 3.05, bar: 2.40, socket: 1.95, axles: 2, offroad: true,
      type: 'hd', coil: 'L', pos: ['high', 'low'], def: 'high',
      setup: { camera: ['Heavy Duty or two', 'One camera or the twin kit'], coil: ['Large', 'Stretches to 5 m · off-road hitch, toolbox and jerry holders'], trailer: ['15 m each', '10 m + 5 m joined · about an 11–13 m run'], car: ['5 m each', 'To the screen adapter'], extra: ['5 m per camera', 'AC-HEAVY-5M'] } }
  ];
  const SETUP_ROWS = [['camera', 'Camera'], ['coil', 'Coil'], ['trailer', 'Trailer side'], ['car', 'Car side'], ['extra', 'Extra to buy']];
  const POS_LABEL = { high: 'High', low: 'Low', inside: 'Inside' };
  const BODIES = [
    { id: 'single', label: 'Single cab · tray', tray: true },
    { id: 'dual', label: 'Dual cab · tray', tray: true },
    { id: 'toolbox', label: 'Dual cab · toolbox', tray: true },
    { id: 'canopy', label: 'Dual cab · canopy', tray: true },
    { id: 'wagon', label: '4WD wagon', tray: false }
  ];

  const byId = {}; CAMS.forEach(function (c) { byId[c.id] = c; });
  const unitById = {}; UNITS.forEach(function (u) { unitById[u.id] = u; });
  const bodyById = {}; BODIES.forEach(function (b) { bodyById[b.id] = b; });
  const trailerById = {}; TRAILERS.forEach(function (t) { trailerById[t.id] = t; });
  const $ = function (id) { return document.getElementById(id); };
  const reduced = !!(window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches);
  const state = {
    selected: new Set(['reverse']), focused: null, focusSide: 'L', zoom: false, towing: 'none', screen: {}, moved: false,
    body: 'dual', trailer: 'vanL', night: false,
    sides: 'both', tsides: 'none', unders: 'B', trailerPos: 'high', coil: 'L', tilt: {},
    type: { reverse: 'pro', front: 'pro', tray: 'pro', side: 'pro', under: 'pro', trailer: 'twin' },
    paint: 'sand'   // Daniel, 3 Nov 2026: camel is the default paint
  };
  /* Daniel, 3 Nov 2026: let people paint the vehicle. Looks only: it changes nothing in the parts list. */
  // swatch = how the paint looks on the lit side of the vehicle; mat and env are the 3D material that gives that look
  // (measured on the door panel, 3 Nov 2026: the scene's lighting brightens colours, so the materials are much darker)
  const PAINTS = [
    { id: 'sand', label: 'Camel', sw: '#D3C299', mat: '#5F421D', env: 0.55 },
    { id: 'white', label: 'White', sw: '#EFEFED', mat: '#F2EFDF', env: 0.55 },
    { id: 'silver', label: 'Silver', sw: '#D0D4D6', mat: '#586167', env: 0.55 },
    { id: 'grey', label: 'Grey', sw: '#606767', mat: '#0F1111', env: 0.4 },
    { id: 'black', label: 'Black', sw: '#202626', mat: '#030404', env: 0.22 },
    { id: 'red', label: 'Red', sw: '#9D2923', mat: '#2B0301', env: 0.4 },
    { id: 'blue', label: 'Blue', sw: '#25476A', mat: '#040B16', env: 0.22 },
    { id: 'green', label: 'Green', sw: '#505C41', mat: '#0B0E07', env: 0.4 }
  ];
  const PAINT = {};
  PAINTS.forEach(function (p) { PAINT[p.id] = p; });
  let three = null, noteTimer = 0, note = '';

  function trl() { return trailerById[state.trailer]; }
  function aFor(label) { const l = label.toLowerCase(); return (/^[aeiou]/.test(l) ? 'an ' : 'a ') + l; }
  function descFor(id) {
    if (id === 'reverse' && state.body === 'wagon') return DESC.reverseWagon;
    if (id === 'tray' && state.body === 'toolbox') return DESC.trayToolbox;
    if (id === 'tray' && state.body === 'canopy') return DESC.trayCanopy;
    if (id === 'trailer') return trailerDesc();
    return DESC[id];
  }
  function trailerDesc() {
    const t = trl(), p = state.trailerPos;
    const what = { box: 'trailer', cage: 'trailer', boat: 'trailer', enclosed: 'trailer', float: 'float', camper: 'camper', van: 'van' }[t.kind];
    if (p === 'inside') return t.kind === 'float' ? 'Inside the float, up front, so you can watch your horses.' : 'Inside, up front, so you can watch your load.';
    if (p === 'low') return 'Low on the back' + (t.kind === 'van' || t.kind === 'camper' ? ', on the bumper' : '') + ': a close view for reversing the ' + what + '.';
    return 'High on the back of the ' + what + ': the long view of the road behind.';
  }
  function trayAllowed() { return bodyById[state.body].tray; }
  /* Screen settings per camera: every camera can mirror; the Pro HD can also flip upside down and show guidelines */
  function scr(cam) {
    return state.screen[cam] || (state.screen[cam] = { mirror: !!byId[cam].mirror, flip: false, guides: cam === 'reverse' || cam === 'trailer' });
  }
  function camName(id) { return id === 'tray' && state.body === 'canopy' ? 'Canopy camera' : byId[id].name; }
  function hasSide(sides, s) { return sides === 'both' || sides === s; }
  /* Cameras that come in pairs: which ones are on is kept as 'both' or one letter */
  const PAIRS = {
    side: { key: 'sides', first: 'L', opts: [['L', 'Passenger', 'Kerb side', 'Passenger'], ['R', 'Driver', 'Right side', 'Driver'], ['both', 'Both', 'Two cameras']] },
    tside: { key: 'tsides', first: 'L', opts: [['L', 'Passenger', 'Kerb side', 'Passenger'], ['R', 'Driver', 'Right side', 'Driver'], ['both', 'Both', 'Two cameras']] },
    under: { key: 'unders', first: 'B', opts: [['B', 'Rear axle', 'Looks forward', 'Rear'], ['F', 'Front axle', 'Looks back', 'Front'], ['both', 'Both', 'Two cameras']] }
  };
  function pairTag(cam, s) { return PAIRS[cam].opts.filter(function (o) { return o[0] === s; })[0][3]; }
  function unitOn(uid) {
    const u = unitById[uid];
    if (u.cam === 'side' || u.cam === 'under') return state.selected.has(u.cam) && hasSide(state[PAIRS[u.cam].key], u.side);
    if (u.cam === 'tside') return state.selected.has('trailer') && hasSide(state.tsides, u.side);
    return state.selected.has(uid);
  }
  function typeFor(uid) {
    const u = unitById[uid];
    if (u.cam === 'tside') return 'soon';
    return state.type[u.cam];
  }
  function sideCount(sides) { return sides === 'both' ? 2 : sides === 'none' ? 0 : 1; }
  /* Camera signals across the hitch. The Pro HD has its own plug break point; everything else rides the Suzi coil. */
  function coilChannels(tsides, rearType) {
    rearType = rearType || state.type.trailer;
    return (rearType === 'twin' ? 2 : rearType === 'hd' ? 1 : 0) + sideCount(tsides === undefined ? state.tsides : tsides);
  }
  function hitchMode() {
    if (!state.selected.has('trailer')) return null;
    if (coilChannels() > 0) return 'coil';
    return 'break';
  }
  function recCoil() { return trl().coil || 'S'; }
  function coilName(ch) { return ch >= 3 ? '3-channel Suzi coil' : ch === 2 ? 'Twin-channel Suzi coil' : 'Suzi coil'; }
  function coilSku(ch, size) { return ch >= 3 ? '' : ch === 2 ? 'AC-SUZI-COIL-KIT-2CH-' + size : COILS[size].sku; }
  function addSides(cur, s) { if (cur === 'none') return s; if (cur === 'both' || cur === s) return cur; return 'both'; }
  function dropSide(cur, s) { if (cur === 'both') return s === 'L' ? 'R' : 'L'; if (cur === s) return 'none'; return cur; }
  const AIM = '<svg viewBox="0 0 10 10" aria-hidden="true"><path d="M2.5 4L5 1.5 7.5 4M2.5 6L5 8.5 7.5 6" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  /* ---------- UI ---------- */
  const PLUS = '<svg class="i-plus" viewBox="0 0 16 16" aria-hidden="true"><path d="M8 3v10M3 8h10" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/></svg>';
  const TICK = '<svg class="i-tick" viewBox="0 0 16 16" aria-hidden="true"><path d="M3.5 8.5l3 3 6-7" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  const COIL = '<svg viewBox="0 0 16 12" aria-hidden="true"><path d="M1 6c1.2-4 2.3-4 2.6 0s1.6 4 2.7 0 2-4 2.7 0 1.6 4 2.7 0 1.6-4 3.3 0" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>';
  const esc = function (s) { return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;'); };

  function buildUI() {
    uiZoom();
    window.addEventListener('resize', uiZoom);   // added before the 3D view's own resize handler, so it measures the new size
    $('bodySel').innerHTML = BODIES.map(function (b) { return '<option value="' + b.id + '">' + b.label + '</option>'; }).join('');
    $('bodySel').value = state.body;
    $('bodySel').addEventListener('change', function (e) { setBody(e.target.value); });
    $('towSel').innerHTML = '<option value="none">Nothing</option>' + GROUPS.map(function (g, gi) {
      return '<optgroup label="' + g + '">' + TRAILERS.filter(function (t) { return t.g === gi; }).map(function (t) { return '<option value="' + t.id + '">' + t.label + '</option>'; }).join('') + '</optgroup>';
    }).join('');
    $('towSel').value = state.towing;
    $('towSel').addEventListener('change', function (e) { setTowing(e.target.value); });
    ['mirror', 'flip', 'guides'].forEach(function (k) {
      $({ mirror: 'mirrorBtn', flip: 'flipBtn', guides: 'guideBtn' }[k]).addEventListener('click', function () {
        const f = state.focused;
        if (!f || !byId[f]) return;
        scr(f)[k] = !scr(f)[k];
        if (three) three.refreshInset();
        say((k === 'guides' ? 'Guidelines ' : k === 'flip' ? 'Flip ' : 'Mirror ') + (scr(f)[k] ? 'on' : 'off'));
        syncUI();
      });
    });

    $('chips').innerHTML = CAMS.filter(function (c) { return !c.noChip; }).map(function (c) {
      return '<button type="button" class="chip" id="chip-' + c.id + '" data-id="' + c.id + '" aria-pressed="false">' +
        '<span class="chip-icon">' + PLUS + TICK + '</span>' +
        '<span class="chip-name">' + c.chip + (c.chipLong ? '<span class="chip-long">' + c.chipLong + '</span>' : '') + '</span></button>';
    }).join('');
    $('chips').addEventListener('click', function (e) {
      const b = e.target.closest('.chip');
      if (!b) return;
      if (b.getAttribute('aria-disabled') === 'true') { flashNote('The tray camera needs an open tray. Pick a single cab, dual cab or toolbox body.'); return; }
      const id = b.dataset.id;
      if (state.selected.has(id)) remove(id); else add(id);
    });

    $('typeSeg').innerHTML = TYPE_KEYS.map(function (k) {
      return '<button type="button" role="radio" aria-checked="false" data-type="' + k + '"><span class="type-pic" id="pic-' + k + '" aria-hidden="true"></span>' + TYPES[k].label + '<span>' + TYPES[k].spec + '</span></button>';
    }).join('');
    $('typeSeg').addEventListener('click', function (e) {
      const b = e.target.closest('button');
      if (b && state.focused && byId[state.focused] && b.getAttribute('aria-disabled') !== 'true') setType(state.focused, b.dataset.type);
    });
    $('sideSeg').addEventListener('click', function (e) {
      const b = e.target.closest('button');
      if (b) setSides(state.focused, b.dataset.side);
    });
    $('posSeg').addEventListener('click', function (e) {
      const b = e.target.closest('button');
      if (b) setPos(b.dataset.pos);
    });
    $('tsidePills').innerHTML = [['L', 'Passenger'], ['R', 'Driver']].map(function (o) {
      return '<button type="button" class="pill" aria-pressed="false" data-side="' + o[0] + '">' + o[1] + '</button>';
    }).join('');
    $('tsidePills').addEventListener('click', function (e) {
      const b = e.target.closest('button');
      if (!b) return;
      const s = b.dataset.side;
      if (hasSide(state.tsides, s)) setTsides(dropSide(state.tsides, s), true); else addTside(s);
    });
    $('coilSeg').innerHTML = ['S', 'M', 'L'].map(function (k) {
      return '<button type="button" role="radio" aria-checked="false" data-size="' + k + '">' + COILS[k].label + '<span>Stretches to ' + COILS[k].stretch + ' m</span></button>';
    }).join('');
    $('coilSeg').addEventListener('click', function (e) {
      const b = e.target.closest('button');
      if (b) setCoil(b.dataset.size);
    });

    $('hotspots').innerHTML = UNITS.map(function (u) {
      return '<button type="button" class="hs is-hidden" id="hs-' + u.id + '" data-id="' + u.id + '"><span class="hs-dot">' + PLUS + TICK + '</span>' +
        '<span class="hs-knob" aria-hidden="true">' + AIM + '</span><span class="hs-label">' + u.label + '<span class="hs-ang"></span></span>' +
        '<span class="hs-tip" aria-hidden="true">Drag the ring to aim</span></button>';
    }).join('') + '<button type="button" class="coil-tag is-hidden" id="coilTag" aria-pressed="false">' + COIL + '<span id="coilTagText">Suzi coil</span></button>';
    $('hotspots').addEventListener('click', function (e) {
      if (e.target.closest('#coilTag')) { if (state.focused === 'hitch') overview(); else focus('hitch'); return; }
      const b = e.target.closest('.hs');
      if (b) tapUnit(b.dataset.id);
    });

    $('paintSw').innerHTML = PAINTS.map(function (p) {
      return '<button type="button" class="sw" role="radio" aria-checked="false" data-paint="' + p.id + '" title="' + p.label + '" aria-label="' + p.label + '" style="--c:' + p.sw + '"></button>';
    }).join('');
    $('paintSw').addEventListener('click', function (e) {
      const b = e.target.closest('button');
      if (!b || state.paint === b.dataset.paint) return;
      state.paint = b.dataset.paint;
      if (three) three.paint(PAINT[state.paint].mat, PAINT[state.paint].env);
      say('Vehicle colour: ' + b.title);
      syncUI();
    });
    $('colourSeg').innerHTML = [['BLACK', 'Black'], ['WHITE', 'White']].map(function (o) {
      return '<button type="button" class="pill" role="radio" aria-checked="false" data-colour="' + o[0] + '"><span class="swatch swatch-' + o[0].toLowerCase() + '" aria-hidden="true"></span>' + o[1] + '</button>';
    }).join('');
    $('colourSeg').addEventListener('click', function (e) {
      const b = e.target.closest('button');
      if (!b || state.twinColour === b.dataset.colour) return;
      state.twinColour = b.dataset.colour;
      if (three) three.recolour();
      say('Twin camera in ' + b.textContent.toLowerCase());
      syncUI();
    });
    $('btnWhole').addEventListener('click', overview);
    $('btnRemove').addEventListener('click', function () { if (state.focused && byId[state.focused]) remove(state.focused); });
    initPanelGrip();
    initChat();
    $('card').addEventListener('scroll', cardMore, { passive: true });
    // phone: the handle tucks the options away (tap, or swipe down) and brings them back (tap, or swipe up)
    (function () {
      const g = $('sheetGrab');
      let y0 = null;
      g.addEventListener('pointerdown', function (e) { y0 = e.clientY; });
      g.addEventListener('pointerup', function (e) {
        if (y0 == null) return;
        const dy = e.clientY - y0;
        y0 = null;
        sheetMin(Math.abs(dy) > 24 ? dy > 0 : !document.body.classList.contains('sheet-min'));
      });
      g.addEventListener('click', function (e) { if (e.detail === 0) sheetMin(!document.body.classList.contains('sheet-min')); });
    })();
    $('nightBtn').addEventListener('click', function () {
      state.night = !state.night;
      if (three) three.refreshInset();
      syncUI();
    });
    $('go').addEventListener('click', function () { goNext(); });
    $('back').addEventListener('click', function () { goBack(); });
    $('stepPane').addEventListener('click', onPane);
    initFlow();
  }

  function sheetMin(on) {
    const g = $('sheetGrab');
    if (document.body.classList.contains('sheet-min') === !!on) return;
    document.body.classList.toggle('sheet-min', !!on);
    requestAnimationFrame(function () { window.dispatchEvent(new Event('resize')); });   // refit the 3D view to the new space
    g.setAttribute('aria-expanded', on ? 'false' : 'true');
    g.setAttribute('aria-label', on ? 'Show the options' : 'Hide the options for a bigger 3D view');
  }
  /* Desktop panel: drag the grip to make the panel wider or narrower, click it to tuck the panel away and bring it back */
  /* Daniel, 3 Nov 2026: on a big desktop screen the builder was too small to read. Scale the panel, labels and buttons
     with the window: 1 up to 1280 x 800, growing with the smaller of width and height, at most 2. Phones stay at 1. */
  function uiZoom() {
    const W = window.innerWidth, H = window.innerHeight;
    const z = window.matchMedia('(min-width: 900px)').matches ? Math.max(1, Math.min(2, W / 1280, H / 800)) : 1;
    document.documentElement.style.setProperty('--z', z.toFixed(3));
  }
  function zoomNow() { return parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--z')) || 1; }
  /* Shopify chat (Daniel, 4 Nov 2026: its floating bubble sat over Back, Next and the bottom of the builder).
     The bubble is hidden on this page unless the chat is open (CSS); the Chat button in the header opens it,
     and only shows once the chat has loaded. */
  function initChat() {
    const btn = $('sbChat');
    if (!btn) return;
    const chat = function () { const c = document.querySelector('shopify-chat'); return c && typeof c.show === 'function' ? c : null; };
    const check = function () { if (chat()) { btn.hidden = false; return true; } return false; };
    if (!check()) {
      const mo = new MutationObserver(function () { if (check()) mo.disconnect(); });
      mo.observe(document.body, { childList: true });
      if (window.customElements) customElements.whenDefined('shopify-chat').then(function () { if (check()) mo.disconnect(); });
      setTimeout(function () { mo.disconnect(); }, 30000);
    }
    btn.addEventListener('click', function () { const c = chat(); if (c) c.show(); });
  }
  function initPanelGrip() {
    const grip = $('panelGrip'), root = document.documentElement, body = document.body;
    let drag = null;
    // panel widths are kept in unscaled pixels; the screen width it takes is that times the zoom
    const clampW = function (w) { return Math.round(Math.max(340, Math.min(w, 760, (window.innerWidth - 380) / zoomNow()))); };
    const panelW = function () { return $('ui').getBoundingClientRect().width / zoomNow(); };
    const relayout = function () { window.dispatchEvent(new Event('resize')); };
    function setOpen(open) {
      body.classList.toggle('panel-closed', !open);
      grip.setAttribute('aria-label', open ? 'Hide the panel. Drag to resize it.' : 'Show the panel');
      grip.title = open ? 'Drag to resize · click to hide' : 'Show the panel';
      relayout();
      setTimeout(relayout, 320);   // again once the slide has finished
    }
    const toggle = function () { setOpen(body.classList.contains('panel-closed')); };
    const stop = function () { drag = null; body.classList.remove('panel-resizing'); grip.classList.remove('is-drag'); };
    grip.addEventListener('pointerdown', function (e) {
      if (e.button !== 0) return;
      drag = { x: e.clientX, w: panelW(), moved: false };
      try { grip.setPointerCapture(e.pointerId); } catch (err) { /* old browser */ }
    });
    grip.addEventListener('pointermove', function (e) {
      if (!drag || body.classList.contains('panel-closed')) return;
      const dx = e.clientX - drag.x;
      if (!drag.moved && Math.abs(dx) < 4) return;
      if (!drag.moved) { drag.moved = true; body.classList.add('panel-resizing'); grip.classList.add('is-drag'); }
      root.style.setProperty('--panel-w', clampW(drag.w - dx / zoomNow()) + 'px');
      relayout();
    });
    grip.addEventListener('pointerup', function () { if (!drag) return; const moved = drag.moved; stop(); if (!moved) toggle(); });
    grip.addEventListener('pointercancel', stop);
    grip.addEventListener('click', function (e) { if (e.detail === 0) toggle(); });   // Enter or Space
    grip.addEventListener('keydown', function (e) {
      if (body.classList.contains('panel-closed') || (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight')) return;
      e.preventDefault();
      root.style.setProperty('--panel-w', clampW(panelW() + (e.key === 'ArrowLeft' ? 30 : -30)) + 'px');
      relayout();
    });
    window.addEventListener('resize', function () {
      const cur = parseFloat(root.style.getPropertyValue('--panel-w'));
      if (cur && clampW(cur) !== cur) root.style.setProperty('--panel-w', clampW(cur) + 'px');
    });
  }
  function say(msg) { $('status').textContent = msg; }
  function flashNote(msg) {
    note = msg; say(msg); syncUI();
    clearTimeout(noteTimer);
    noteTimer = setTimeout(function () { note = ''; syncUI(); }, 3600);
  }

  function tapUnit(uid) {
    const u = unitById[uid];
    // on the later steps a camera on the car takes you back to step 1 to change it
    if (state.step !== 'see') { state.editing = false; goStep('see'); if (unitOn(uid)) focus(u.cam, u.side); return; }
    if (!unitOn(uid)) {
      if (u.cam === 'side' || u.cam === 'under') addPair(u.cam, u.side);
      else if (u.cam === 'tside') addTside(u.side);
      else add(u.cam);
      return;
    }
    if (state.focused === u.cam && (!u.side || state.focusSide === u.side)) {
      state.zoom = !state.zoom;
      if (three) three.focus(u.cam);
      say(state.zoom ? 'Zoomed in on the camera' : 'Zoomed out');
      syncUI();
      return;
    }
    focus(u.cam, u.side);
  }
  function add(id) {
    if (id === 'tray' && !trayAllowed()) return;
    if (id === 'side' || id === 'under') { addPair(id, null); return; }
    if (id === 'trailer' && state.towing === 'none') { setTowing(state.trailer, true); }
    if (id === 'reverse') state.revAdded = false;
    state.selected.add(id);
    if (three) three.sync();
    say(byId[id].name + ' added');
    focus(id);
  }
  function addPair(cam, s) {
    const P = PAIRS[cam];
    if (!state.selected.has(cam)) { state.selected.add(cam); state[P.key] = s || (cam === 'side' ? 'both' : P.first); }
    else state[P.key] = addSides(state[P.key], s || 'both');
    if (three) three.sync();
    say((s ? unitById[cam + s].label + ' camera' : byId[cam].name) + ' added');
    focus(cam, s || (state[P.key] === 'both' ? P.first : state[P.key]));
  }
  function addTside(s) {
    const next = addSides(state.tsides, s);
    if (coilChannels(next) > 3) {
      flashNote('The 3-channel Suzi coil carries up to 3 camera signals across the hitch, and the Twin uses two of them. Pick a single camera on the back to add both sides.');
      return;
    }
    state.tsides = next;
    if (three) three.sync();
    say('Trailer side camera added');
    focus('tside', s);
  }
  function setTsides(v, stay) {
    state.tsides = v;
    if (three) three.sync();
    if (v === 'none' && state.focused === 'tside') { focus('trailer'); return; }
    if (!stay && v !== 'both' && v !== 'none') state.focusSide = v;
    if (state.focused && three) three.focus(state.focused);
    syncUI();
  }
  function setSides(cam, v) {
    if (cam === 'tside') {
      if (coilChannels(v) > 3) { flashNote('That would need 4 signals across the hitch. The 3-channel Suzi coil carries 3, and the Twin uses two.'); return; }
      state.tsides = v;
    } else state[PAIRS[cam].key] = v;
    if (v !== 'both') state.focusSide = v;
    state.zoom = false;
    if (three) { three.sync(); three.focus(cam); }
    syncUI();
  }
  function remove(id) {
    if (id === 'tside') { state.tsides = 'none'; if (three) three.sync(); say('Trailer side cameras removed'); focus('trailer'); return; }
    if (id === 'reverse') { state.revAdded = false; if (state.revcheck === 'no') state.revcheck = null; }
    state.selected.delete(id);
    if (three) three.sync();
    say(byId[id].name + ' removed');
    if (state.focused === id || (id === 'trailer' && (!state.focused || state.focused === 'hitch' || state.focused === 'tside'))) overview();
    else syncUI();
  }
  function clearNote() { note = ''; clearTimeout(noteTimer); }
  function focus(id, side) {
    clearNote();
    if (state.focused !== id || (side && side !== state.focusSide)) state.zoom = false;
    state.focused = id;
    sheetMin(false);   // the camera's options are in the sheet
    if (side) state.focusSide = side;
    else if (PAIRS[id]) { const cur = state[PAIRS[id].key]; state.focusSide = cur === 'both' || cur === 'none' ? PAIRS[id].first : cur; }
    if (three) three.focus(id);
    syncUI();
  }
  function overview() {
    clearNote();
    state.focused = null; state.zoom = false; state.moved = false;
    if (three) three.overview();
    syncUI();
  }
  function setBody(id) {
    state.body = id;
    if (!trayAllowed() && state.selected.has('tray')) {
      state.selected.delete('tray');
      flashNote('Tray camera removed: a wagon has no tray.');
    }
    if (state.focused === 'tray' && !trayAllowed()) state.focused = null;
    if (three) { three.setBody(id); if (state.focused) three.focus(state.focused); else three.overview(); }
    syncUI();
  }
  /* What you tow is part of your vehicle setup; the trailer camera is added separately */
  function setTowing(v, quiet) {
    if (v === 'none') {
      state.towing = 'none';
      state.selected.delete('trailer'); state.tsides = 'none';
      if (three) three.sync();
      say('Not towing');
      if (state.focused === 'trailer' || state.focused === 'tside' || state.focused === 'hitch' || !state.focused) overview(); else syncUI();
      return;
    }
    state.towing = v;
    if (quiet) { if (state.trailer !== v) setTrailer(v, true); return; }
    setTrailer(v);
  }
  function markMoved() { if (!state.moved) { state.moved = true; syncUI(); } }
  function setTrailer(id, quiet) {
    const t = trailerById[id];
    state.trailer = id;
    if (state.towing !== 'none') state.towing = id;
    delete state.tilt.trailer; delete state.tilt.tsideL; delete state.tilt.tsideR;
    state.type.trailer = t.type;
    state.trailerPos = t.def;
    state.coil = recCoil();
    if (coilChannels() > 3) state.tsides = 'L';
    state.zoom = false;
    if (three) { three.setTrailer(id); if (quiet) return; if (state.focused) three.focus(state.focused); else three.overview(); }
    say(t.label + ': ' + TYPES[t.type].label + ' recommended');
    syncUI();
  }
  function setType(cam, type) {
    if (state.type[cam] === type) return;
    clearNote();
    if (cam === 'trailer' && coilChannels(state.tsides, type) > 3) {
      flashNote('The Twin uses two of the coil’s three channels. Drop one trailer side camera first.');
      return;
    }
    state.type[cam] = type;
    if (three) { three.setType(cam); three.focus(cam); }
    syncUI();
  }
  function setPos(p) {
    if (state.trailerPos === p) return;
    clearNote();
    state.trailerPos = p;
    delete state.tilt.trailer;
    state.zoom = false;
    if (three) { three.setType('trailer'); three.focus('trailer'); }
    syncUI();
  }
  function setCoil(k) {
    if (state.coil === k) return;
    clearNote();
    state.coil = k;
    if (three) three.sync();
    syncUI();
  }

  function cameraCount() {
    let n = 0;
    state.selected.forEach(function (id) { n += PAIRS[id] ? sideCount(state[PAIRS[id].key]) : 1; });
    if (state.selected.has('trailer')) n += sideCount(state.tsides);
    return n;
  }
  function chosenNames() {
    const out = [];
    CAMS.forEach(function (c) {
      if (c.id === 'tside') { if (state.selected.has('trailer') && state.tsides !== 'none') out.push('Trailer sides'); return; }
      if (!state.selected.has(c.id)) return;
      if (c.id === 'side') out.push(state.sides === 'both' ? 'Both sides' : state.sides === 'L' ? 'Passenger side' : 'Driver side');
      else if (c.id === 'under') out.push(state.unders === 'both' ? 'Under both axles' : state.unders === 'B' ? 'Under the rear axle' : 'Under the front axle');
      else out.push(c.chip);
    });
    return out;
  }
  function setupHTML(t) {
    return SETUP_ROWS.map(function (r) {
      const v = t.setup[r[0]];
      return '<dt>' + r[1] + '</dt><dd>' + esc(v[0]) + '<small>' + esc(v[1]) + '</small></dd>';
    }).join('');
  }

  function syncUI() {
    const tOk = trayAllowed();
    CAMS.forEach(function (c) {
      if (c.noChip) return;
      const chip = $('chip-' + c.id);
      chip.setAttribute('aria-pressed', state.selected.has(c.id) ? 'true' : 'false');
      if (c.id === 'tray') chip.setAttribute('aria-disabled', tOk ? 'false' : 'true');
      chip.title = c.id === 'tray' && !tOk ? 'Needs an open tray.' : c.id === 'trailer' ? 'On the back of your trailer, van or float.' : descFor(c.id);
    });
    UNITS.forEach(function (u) {
      const on = unitOn(u.id);
      const hs = $('hs-' + u.id);
      hs.classList.toggle('is-on', on);
      const foc = state.focused === u.cam && (!u.side || state.focusSide === u.side || state[PAIRS[u.cam].key] === 'both');
      hs.classList.toggle('is-focus', foc);
      hs.setAttribute('aria-label', (on ? (foc ? (state.zoom ? 'Zoom out from the ' : 'Zoom in on the ') : 'Show the ') : 'Add the ') + u.label.replace(' · soon', '').toLowerCase() + (u.side ? ' camera' : '') + (on && foc ? '. Up and down arrow keys change its angle.' : ''));
    });
    const mode = hitchMode(), ch = coilChannels();
    $('coilTagText').textContent = mode === 'break' ? 'Break point' : COILS[state.coil].label + ' Suzi coil';
    $('coilTag').setAttribute('aria-pressed', state.focused === 'hitch' ? 'true' : 'false');
    $('bodySel').value = state.body;
    $('towSel').value = state.towing;
    $('bodyVal').textContent = bodyById[state.body].label;
    Array.prototype.forEach.call($('paintSw').children, function (b) { b.setAttribute('aria-checked', b.dataset.paint === state.paint ? 'true' : 'false'); });
    $('towVal').textContent = state.towing === 'none' ? 'Nothing' : trailerById[state.towing].short;
    $('towPick').classList.toggle('is-on', state.towing !== 'none');
    $('chip-tray').querySelector('.chip-name').textContent = state.body === 'canopy' ? 'Canopy' : 'Tray';
    $('hs-tray').querySelector('.hs-label').firstChild.textContent = state.body === 'canopy' ? 'Canopy camera' : 'Tray camera';

    const f = state.focused, cam = f && byId[f], t = trl();
    const isSide = !!(f && PAIRS[f]);
    document.body.classList.toggle('is-focused', !!f);
    $('cardActions').hidden = !f && !state.moved;
    $('btnRemove').hidden = !cam;
    $('typeSeg').hidden = !cam || f === 'tside';
    const twinHere = !!(cam && f !== 'tside' && state.type[f] === 'twin');
    $('colourRow').hidden = !twinHere;
    if (twinHere) Array.prototype.forEach.call($('colourSeg').children, function (b) { b.setAttribute('aria-checked', b.dataset.colour === state.twinColour ? 'true' : 'false'); });
    $('sideSeg').hidden = !isSide;
    $('posRow').hidden = f !== 'trailer';
    $('tsideRow').hidden = f !== 'trailer';
    $('coilSeg').hidden = !(f === 'hitch' && mode === 'coil');
    $('setup').hidden = f !== 'trailer' && f !== 'hitch';
    let title = '', body = '';
    if (cam) {
      title = camName(f);
      body = descFor(f);
      const allowed = f === 'side' || f === 'under' ? SIDE_TYPES : TYPE_KEYS;
      Array.prototype.forEach.call($('typeSeg').children, function (b) {
        const k = b.dataset.type;
        b.hidden = allowed.indexOf(k) < 0;
        b.setAttribute('aria-checked', k === state.type[f] ? 'true' : 'false');
        b.setAttribute('aria-disabled', f === 'trailer' && k === 'twin' && coilChannels(state.tsides, 'twin') > 3 ? 'true' : 'false');
      });
      $('typeSeg').classList.toggle('two', f === 'side' || f === 'under');
      if (isSide) {
        const cur = state[PAIRS[f].key];
        $('sideSeg').setAttribute('aria-label', f === 'under' ? 'Which axle' : 'Which side');
        $('sideSeg').innerHTML = PAIRS[f].opts.map(function (o) {
          return '<button type="button" role="radio" aria-checked="' + (o[0] === cur ? 'true' : 'false') + '" data-side="' + o[0] + '">' + o[1] + '<span>' + o[2] + '</span></button>';
        }).join('');
      }
      if (f === 'tside') {
        title = cam.name;
        body = DESC.tside + ' Signals across the hitch: ' + ch + ' of 3.';
      }
      if (f === 'trailer') {
        $('posSeg').innerHTML = t.pos.map(function (p) {
          return '<button type="button" class="pill" role="radio" data-pos="' + p + '" aria-checked="' + (p === state.trailerPos ? 'true' : 'false') + '">' + POS_LABEL[p] + '</button>';
        }).join('');
        Array.prototype.forEach.call($('tsidePills').children, function (b) {
          const on = hasSide(state.tsides, b.dataset.side);
          b.setAttribute('aria-pressed', on ? 'true' : 'false');
          b.setAttribute('aria-label', (on ? 'Remove ' : 'Add ') + b.textContent.toLowerCase() + '-side trailer camera (coming soon)');
        });
        const rec = t.setup.camera[0] + (t.coil ? ' · ' + COILS[t.coil].label + ' coil' : ' · no coil');
        body = esc(trailerDesc()) + '<span class="rec"> Recommended for ' + esc(aFor(t.label)) + ': ' + esc(rec) + '.</span>';
      }
    } else if (f === 'hitch') {
      if (mode === 'break') {
        title = 'Plug break point';
        body = 'The Pro HD trailer kit unplugs at the hitch, so you don’t need a coil. Go Heavy Duty and it’s a Small Suzi coil.';
      } else {
        const r = recCoil(), c = COILS[state.coil], sku = coilSku(ch, state.coil);
        title = coilName(ch);
        body = 'Carries ' + (ch === 1 ? 'the camera signal' : ch + ' camera signals') + ' across the hitch and stretches as you turn. ' + c.label + ': ' + c.coiled + ' coiled, stretches to ' + c.stretch + ' m' + (sku ? ' (' + sku + ')' : '') + '.';
        if ('SML'.indexOf(state.coil) < 'SML'.indexOf(r)) body += ' <span class="warn">Shorter than we recommend for ' + esc(aFor(t.label)) + '.</span>';
        else body += ' Recommended for ' + esc(aFor(t.label)) + ': ' + COILS[r].label + '.';
        if (state.type.trailer === 'pro') body += ' The Pro HD keeps its own plug break point.';
        Array.prototype.forEach.call($('coilSeg').children, function (b) {
          b.setAttribute('aria-checked', b.dataset.size === state.coil ? 'true' : 'false');
          b.classList.toggle('is-rec', b.dataset.size === r);
        });
      }
    } else {
      title = 'Your cameras';
      const names = chosenNames();
      body = names.length ? names.join(', ') + '. Tap a camera on the car to see where it sits and what it sees.' : 'None yet. Tap + on the car to add one.';
    }
    $('cardTitle').innerHTML = esc(title) + (f === 'tside' ? '<em class="soon">Coming soon</em>' : '');
    $('cardBody').hidden = false;
    if (note) $('cardBody').textContent = note;
    else if ((f === 'hitch' && mode === 'coil') || f === 'trailer') $('cardBody').innerHTML = body;
    else $('cardBody').textContent = body;
    if (!$('setup').hidden) {
      $('setupFor').textContent = 'for ' + aFor(t.label);
      $('setupList').innerHTML = setupHTML(t);
      $('setup').querySelector('.fine').textContent = f === 'hitch' ? COIL_RULE + ' Recommended, not yet confirmed.' : 'Recommended, not yet confirmed. For a real trailer, measure the socket gap and the cable run.';
    }
    $('nightBtn').setAttribute('aria-pressed', state.night ? 'true' : 'false');
    if (cam) {
      const sc = scr(f), pro = f !== 'tside' && state.type[f] === 'pro';
      $('mirrorBtn').setAttribute('aria-pressed', sc.mirror ? 'true' : 'false');
      $('flipBtn').hidden = !pro; $('flipBtn').setAttribute('aria-pressed', sc.flip ? 'true' : 'false');
      $('guideBtn').hidden = !pro; $('guideBtn').setAttribute('aria-pressed', sc.guides ? 'true' : 'false');
    }
    const n = cameraCount();
    $('go').disabled = !n;
    $('go').textContent = n ? 'Continue with ' + n + ' camera' + (n > 1 ? 's' : '') : 'Pick at least one camera';
    syncFlow();
    cardMore();
  }
  /* Phone: when a camera's options run past the bottom of the panel, the last line fades so it reads as "scroll for more" */
  function cardMore() {
    const c = $('card');
    if (c) c.classList.toggle('more', c.scrollHeight - c.scrollTop - c.clientHeight > 4);
  }

  /* ---------- Steps 2 onward: catalogue ----------
     Products and prices come from the AutoChimp Shopify catalogue (the v5 builder snapshot of 1 Nov 2026, re-checked
     against Shopify on 2 Nov 2026, plus the trailer parts the 3D step 1 can land on). p = storefront price inc. GST
     (admin price x 1.1); null = price not set yet. st: live = active in Shopify, draft = not on sale yet. */
  const P = {};
  function def(sku, o) { P[sku] = Object.assign({ st: 'live', mp: 0, mg: 0 }, o); }
  function kits(list, o) { list.forEach(function (x) { def(x[0], Object.assign({}, o, x[1])); }); }

  // Reverse camera kits
  def('AC-TOY-RC8-RC9', { t: 'Reverse camera kit', p: 240, id: 46912081887482, h: 'autochimp-reverse-camera-kit-landcruiser-70-series', v: 'HIk1cEDxAdk', k: 'rev', box: 'REV', mp: 1 });
  def('AC-TOY-RC8-RC9-HEAVY', { t: 'Heavy Duty reverse camera kit', p: 330, id: 48369120510202, h: 'heavy-duty-reverse-camera-toyota-landcruiser-70-series-2024-2026', v: 'HIk1cEDxAdk', k: 'rev', hd: true, box: 'REVHD' });
  def('AC-TOY-RC7', { t: 'Reverse camera kit', p: 240, id: 44237264847098, h: 'autochimp-reverse-camera-kit-toyota-landcruiser-70-series-2014-2023', v: '_Ct2BdJkn3o', k: 'rev', box: 'REV', mp: 1 });
  def('AC-TOY-RC7-HEAVY', { t: 'Heavy Duty reverse camera kit', p: 330, id: 48601620447482, h: 'heavy-duty-reverse-camera-toyota-landcruiser-70-series-2014-2023', v: '_Ct2BdJkn3o', k: 'rev', hd: true, box: 'REVHD' });
  def('AC-TOY-RC6', { t: 'Reverse camera kit', p: 240, id: 43492411212026, h: 'autochimp-reverse-camera-kit-toyota-hilux-2020-2025', v: 'O9IvUYUjBdo', k: 'rev', box: 'REV', mp: 1 });
  def('AC-TOY-RC4', { t: 'Reverse camera kit', p: 240, id: 43492408426746, h: 'autochimp-reverse-camera-kit-toyota-hilux-2014-2020', v: 'ubsz2gbNxUk', k: 'rev', box: 'REV', mp: 1 });
  def('AC-TOY-RC3', { t: 'Reverse camera kit', p: 240, id: 50153955197178, h: 'autochimp-reverse-camera-kit-toyota-hilux-n70-2014-2015', k: 'rev', box: 'REV', mp: 1 });
  def('AC-TOY-RC2', { t: 'Reverse camera kit', p: 240, id: 43492406329594, h: 'autochimp-reverse-camera-kit-toyota-hilux-2006-2013', k: 'rev', box: 'REV', mp: 1 });
  def('AC-TOY-RC1', { t: 'Reverse camera kit', p: 180, id: 43777552777466, h: 'autochimp-reverse-camera-kit-for-toyota-hilux-non-touchscreen-2006-2013', k: 'rev', box: 'REV1', nomount: true });
  def('AC-TOY-RC10', { t: 'Reverse camera kit', p: 240, id: 47561500983546, h: 'autochimp-reverse-camera-kit-toyota-landcruiser-gx-2014-2020', v: 'ubsz2gbNxUk', k: 'rev', box: 'REV', mp: 1 });
  def('AC-ISUZU-RC1', { t: 'Reverse camera kit', p: 160, id: 47615823151354, h: 'autochimp-reverse-camera-kit-isuzu-dmax-mux-2012-2024', v: 'YV1gRrhM62o', k: 'rev', box: 'REV', mp: 1 });
  kits([['AC-TOY-RC13', { p: 240 }], ['AC-TOY-RC12', { p: 240 }], ['AC-TOY-RC11', { p: 240 }], ['AC-MAZ-RC1', { p: 160 }], ['AC-SUZ-RC2', { p: null }], ['AC-SUZ-RC1', { p: null }]], { t: 'Reverse camera kit', st: 'draft', k: 'rev', mp: 1, mg: 0 });

  // Front camera kits and the LC70 bundle
  def('AC-FRONT-70SERIES', { t: 'Front camera kit', p: 350, id: 46865885266170, h: 'autochimp-front-camera-kit-70series-2024', v: 'k1p1EXUKfWE', k: 'front', box: 'FRONT', mg: 1, sw: 'S22FR_W', qc: true });
  def('AC-FRONT-HILUX-2020', { t: 'Front camera kit', p: 350, id: 45972988821754, h: 'autochimp-front-camera-kit-hilux-2020', v: '3VujvJdGjIg', k: 'front', box: 'FRONT', mp: 1, sw: 'D32FR_B', qc: true });
  def('AC-FRONT-HILUX-2016', { t: 'Front camera kit', p: 350, id: 46037639528698, h: 'autochimp-front-camera-kit-hilux-2016', v: '3VujvJdGjIg', k: 'front', box: 'FRONT', mp: 1, sw: 'D32FR_B', qc: true });
  def('AC-FRONT-70SERIES-2014', { t: 'Front camera kit', p: 360, st: 'draft', k: 'front', box: 'FRONT', mp: 1, sw: 'D37C12_G', qc: true });
  kits([['AC-FRONT-PRADO-2020', {}], ['AC-FRONT-PRADO-2014', {}], ['AC-FRONT-FORTUNER-2020', {}], ['AC-FRONT-HIACE-2020', {}], ['AC-FRONT-KLUGER-2020', {}], ['AC-FRONT-KLUGER-2014', {}]], { t: 'Front camera kit', p: 360, st: 'draft', k: 'front', box: 'FRONT', mp: 1, mg: 0, sw: 'D32FR_B', qc: true });
  kits([['AC-FRONT-FORTUNER-2016', { p: 360 }], ['AC-FRONT-HIACE-2014', { p: 360 }], ['AC-FRONT-LANDCRUISER-2016', { p: 360 }], ['AC-FRONT-MAZ-2021', { p: 360 }], ['AC-FRONT-HILUX-2014', { p: 565.02 }], ['AC-FRONT-JIMNY-2025', { p: 508.01 }], ['AC-FRONT-JIMNY-2018', { p: null }]], { t: 'Front camera kit', st: 'draft', k: 'front', mp: 1, mg: 0, qc: true });
  def('AC-FRONT-DMAX-2012', { t: 'Front camera kit', p: 360, st: 'draft', k: 'front', box: 'FRONT', mp: 1, sw: 'ISU_FR', qc: true });
  def('AC-FRONTREAR-70SERIES-2024', { t: 'Front & Rear Camera Kit', p: 550, id: 46884905582842, h: 'autochimp-front-rear-camera-kit-70series-2024', v: 'Mb191Y02p1Q', k: 'fr', box: 'FR', mg: 1, mp: 1, sw: 'S22FR_W', qc: true, img: 'kitFR70' });

  // Dual camera kits (QuadCam module + wiring + switches, no cameras)
  const DUAL = { t: 'Dual camera kit', p: 240, k: 'dual', box: 'QC', qc: true, mp: 0, mg: 0 };
  kits([
    ['AC-DUAL-70SERIES-2024-22MM', { id: 47311479800058, h: 'dual-camera-kit-landcruiser-70-2023-2025', v: '3VujvJdGjIg', swv: '22', sw: 'S22FR_W' }],
    ['AC-DUAL-70SERIES-2024-32MM', { id: 47311479996666, h: 'dual-camera-kit-landcruiser-70-2023-2025', v: '3VujvJdGjIg', swv: '32' }],
    ['AC-DUAL-70SERIES-2024-37MM', { id: 47482104414458, h: 'dual-camera-kit-landcruiser-70-2023-2025', v: '3VujvJdGjIg', swv: '37' }],
    ['AC-DUAL-70SERIES-2014', { id: 47462149325050, h: 'autochimp-dual-camera-kit-landcruiser-70-series-2014-2023', v: '3VujvJdGjIg', sw: 'D37C12_G' }],
    ['AC-DUAL-HILUX-2020-32MM', { id: 47309181288698, h: 'autochimp-dual-camera-kit-hilux-2020', v: '9noIiDFx0kI', swv: '32' }],
    ['AC-DUAL-HILUX-2020-22MM', { id: 47309178634490, h: 'autochimp-dual-camera-kit-hilux-2020', v: '9noIiDFx0kI', swv: '22', sw: 'S22FR_B' }],
    ['AC-DUAL-HILUX-2016-22MM', { id: 47310247559418, h: 'dual-camera-kit-toyota-hilux-2016', v: 'aK1s0B5vDV4', swv: '22', sw: 'S22C12_B' }],
    ['AC-DUAL-HILUX-2016-32MM', { id: 47310247952634, h: 'dual-camera-kit-toyota-hilux-2016', v: 'aK1s0B5vDV4', swv: '32' }],
    ['AC-DUAL-HILUX-2014', { id: 48568433541370, h: 'autochimp-dual-camera-kit-toyota-hilux-2014-2015', sw: 'D37C12_G' }],
    ['AC-DUAL-LANDCRUISER-2016-32MM', { id: 47459343532282, h: 'autochimp-dual-camera-kit-for-toyota-landcuiser-prado-2016-to-2021', v: '3VujvJdGjIg', swv: '32', sw: 'D32C12_B' }],
    ['AC-DUAL-LANDCRUISER-2016-37MM', { id: 47459343565050, h: 'autochimp-dual-camera-kit-for-toyota-landcuiser-prado-2016-to-2021', v: '3VujvJdGjIg', swv: '37' }],
    ['AC-DUAL-LANDCRUISER-2016-22MM', { id: 47459343597818, h: 'autochimp-dual-camera-kit-for-toyota-landcuiser-prado-2016-to-2021', v: '3VujvJdGjIg', swv: '22' }],
    ['AC-DUAL-PRADO-2020-32MM', { id: 47426920382714, h: 'autochimp-dual-camera-kit-toyota-prado-2020-2024', swv: '32', sw: 'D32C12_B' }],
    ['AC-DUAL-PRADO-2020-22MM', { id: 47426920415482, h: 'autochimp-dual-camera-kit-toyota-prado-2020-2024', swv: '22' }],
    ['AC-DUAL-PRADO-2014-32MM', { id: 47436263686394, h: 'dual-camera-kit-toyota-prado-2014', swv: '32' }],
    ['AC-DUAL-PRADO-2014-37MM', { id: 47436263719162, h: 'dual-camera-kit-toyota-prado-2014', swv: '37' }],
    ['AC-DUAL-PRADO-2014-22MM', { id: 47436263751930, h: 'dual-camera-kit-toyota-prado-2014', swv: '22' }],
    ['AC-DUAL-FORTUNER-2020-32MM', { id: 47427681911034, h: 'dual-camera-kit-toyota-fortuner-2020', v: '3VujvJdGjIg', swv: '32' }],
    ['AC-DUAL-FORTUNER-2020-22MM', { id: 47427681943802, h: 'dual-camera-kit-toyota-fortuner-2020', v: '3VujvJdGjIg', swv: '22' }],
    ['AC-DUAL-FORTUNER-2016-32MM', { id: 47457979564282, h: 'dual-camera-kit-toyota-fortuner-2016-2020', v: '3VujvJdGjIg', swv: '32' }],
    ['AC-DUAL-FORTUNER-2016-37MM', { id: 47457979597050, h: 'dual-camera-kit-toyota-fortuner-2016-2020', v: '3VujvJdGjIg', swv: '37' }],
    ['AC-DUAL-FORTUNER-2016-22MM', { id: 47457979629818, h: 'dual-camera-kit-toyota-fortuner-2016-2020', v: '3VujvJdGjIg', swv: '22' }],
    ['AC-DUAL-HIACE-2020-32MM', { id: 47385338249466, h: 'autochimp-dual-camera-kit-toyota-hiace-2020-2025', v: 'NONrtZ_xOyo', swv: '32' }],
    ['AC-DUAL-HIACE-2020-22MM', { id: 47385338282234, h: 'autochimp-dual-camera-kit-toyota-hiace-2020-2025', v: 'NONrtZ_xOyo', swv: '22' }],
    ['AC-DUAL-HIACE-2017', { id: 47435734221050, h: 'autochimp-dual-camera-kit-toyota-hiace-2014-2020', swv: '32' }],
    ['AC-DUAL-HIACE-2018', { id: 47435734286586, h: 'autochimp-dual-camera-kit-toyota-hiace-2014-2020', swv: '37' }],
    ['AC-DUAL-HIACE-2016', { id: 47435734253818, h: 'autochimp-dual-camera-kit-toyota-hiace-2014-2020', swv: '22' }],
    ['AC-DUAL-KLUGER-2020-32MM', { id: 47429111054586, h: 'dual-camera-kit-toyota-kluger-2020', swv: '32' }],
    ['AC-DUAL-KLUGER-2020-22MM', { id: 47429111087354, h: 'dual-camera-kit-toyota-kluger-2020', swv: '22' }],
    ['AC-DUAL-KLUGER-2014-32MM', { id: 47458838511866, h: 'dual-camera-kit-toyota-kluger-2014-2019', v: '3VujvJdGjIg', swv: '32' }],
    ['AC-DUAL-KLUGER-2014-37MM', { id: 47458838544634, h: 'dual-camera-kit-toyota-kluger-2014-2019', v: '3VujvJdGjIg', swv: '37' }],
    ['AC-DUAL-KLUGER-2014-22MM', { id: 47458838577402, h: 'dual-camera-kit-toyota-kluger-2014-2019', v: '3VujvJdGjIg', swv: '22' }],
    ['AC-DUAL-DMAX-23-C12', { id: 47828382646522, h: 'autochimp-dual-camera-kit-isuzu-dmax-2012-2024', v: 'YV1gRrhM62o', sw: 'ISU_C12' }],
    ['AC-DUAL-SUZUKI-2025', { id: 48695575412986, h: 'autochimp-dual-camera-kit-suzuki-jimny-2025', sw: 'SUZ_FR' }],
    ['AC-DUAL-MAZ-23-C12', { st: 'draft', box: null }],
    ['AC-DUAL-DMAX-2025', { st: 'draft', box: null }],
    ['AC-DUAL-SUZUKI-2015', { st: 'draft', box: null, p: null }]
  ], DUAL);

  // Quad camera kits (four camera inputs, no cameras)
  const QUAD = { t: 'Quad camera kit', p: null, st: 'draft', k: 'quad', qc: true, mp: 0, mg: 0 };
  kits([
    ['AC-QUAD-HILUX-2020', { p: 270, st: 'live', id: 45383039516922, h: 'autochimp-quad-camera-kit-hilux-2020', v: 'qpJe52qinYM', box: 'QC', sw: 'Q32_B' }],
    ['AC-QUAD-HILUX-2014', { p: 270, st: 'live', id: 45907090637050, h: 'autochimp-quad-camera-kit-hilux-2014', v: '3VujvJdGjIg', box: 'QC' }],
    ['AC-QUAD-HIACE-2020', { p: 270, st: 'live', id: 45392009199866, h: 'autochimp-quad-camera-kit-toyota-hiace-2020-2024', v: '3VujvJdGjIg', box: 'QC', sw: 'Q32_B' }],
    ['AC-QUAD-HILUX-N70', { p: 270 }],
    ['AC-QUAD-70SERIES-2024', {}], ['AC-QUAD-70SERIES-2014', {}], ['AC-QUAD-LANDCRUISER-2016', {}], ['AC-QUAD-PRADO-2020', {}], ['AC-QUAD-PRADO-2014', {}],
    ['AC-QUAD-FORTUNER-2020', {}], ['AC-QUAD-FORTUNER-2016', {}], ['AC-QUAD-HIACE-2014', {}], ['AC-QUAD-KLUGER-2020', {}], ['AC-QUAD-KLUGER-2014', {}],
    ['AC-QUAD-DMAX-2012', {}], ['AC-QUAD-MAZ-2021', {}], ['AC-QUAD-JIMNY-2025', {}], ['AC-QUAD-SUZUKI-2015', {}]
  ], QUAD);

  // Trailer camera kits (camera, extension cables, QuadCam, switches)
  const TRAILER = { t: 'Trailer camera kit', p: null, st: 'draft', k: 'trailer', qc: true, mp: 1, mg: 0 };
  kits([
    ['AC-TRAILER-70SERIES-2024', { p: 350, st: 'live', id: 47029598322938, h: 'autochimp-trailer-camera-kit-toyota-70-series-landcruiser-2024', v: 'yIP0YbUwSJ0', box: 'TRAILER', ext: 2, sw: 'S22RT_W' }],
    ['AC-TRAILER-HILUX-2020', { p: 350, st: 'live', id: 47966522474746, h: 'autochimp-trailer-camera-kit-for-toyota-hilux-2020-2025-plug-play-system', v: 'ESPY5CQTXkE', box: 'TRAILER', ext: 1, sw: 'D32C12_B' }],
    ['AC-TRAILER-DMAX-2012', { p: 360, box: 'TRAILER', ext: 2, sw: 'ISU_RT' }],
    ['AC-TRAILER-MAZ-2021', { p: 360 }],
    ['AC-TRAILER-HILUX-2016-32MM', { swv: '32' }], ['AC-TRAILER-HILUX-2016-22MM', { swv: '22' }],
    ['AC-TRAILER-70SERIES-2014', {}], ['AC-TRAILER-HILUX-2014', {}], ['AC-TRAILER-LANDCRUISER-2016', {}], ['AC-TRAILER-PRADO-2020', {}], ['AC-TRAILER-PRADO-2014', {}],
    ['AC-TRAILER-FORTUNER-2020', {}], ['AC-TRAILER-FORTUNER-2016', {}], ['AC-TRAILER-HIACE-2020', {}], ['AC-TRAILER-HIACE-2014', {}], ['AC-TRAILER-KLUGER-2020', {}], ['AC-TRAILER-KLUGER-2014', {}]
  ], TRAILER);

  // Universal kits, cameras, mounts and parts
  def('AC-PRO-CAM-KIT-REAR', { t: 'Pro HD rear camera kit', p: 165, id: 45919153979642, h: 'autochimp-pro-hd-rear-camera-kit', v: 'WI1yO-7CFko', k: 'ukit', box: 'PRO', mp: 1 });
  def('AC-PRO-CAM-KIT-FRONT', { t: 'Pro HD front camera kit', p: 165, id: 45917541007610, h: 'autochimp-pro-hd-front-camera-kit', v: 'WI1yO-7CFko', k: 'ukit', box: 'PRO', mp: 1 });
  def('AC-PRO-CAM-KIT', { t: 'Pro HD camera kit', p: 165, id: 45371689795834, h: 'autochimp-pro-hd-camera-kit', v: 'WI1yO-7CFko', k: 'ukit', box: 'PRO', mp: 1 });
  def('AC-PRO-CAM-KIT-TRAILER', { t: 'Pro HD trailer camera kit', p: 220, id: 43746974073082, h: 'autochimp-pro-hd-trailer-camera-kit', v: 'ESPY5CQTXkE', k: 'ukit', box: 'PROTR', mp: 1 });
  def('AC-HEAVY-CAM-KIT-5M', { t: 'Heavy Duty camera kit', p: 180, id: 45371118059770, h: 'autochimp-heavy-duty-camera-kit', v: 'WI1yO-7CFko', k: 'ukit', hd: true, box: 'HD', len: 5 });
  def('AC-HEAVY-CAM-KIT-10M', { t: 'Heavy Duty camera kit', p: 180, id: 45371118092538, h: 'autochimp-heavy-duty-camera-kit', v: 'WI1yO-7CFko', k: 'ukit', hd: true, box: 'HD', len: 10 });
  def('AC-HEAVY-CAM-KIT-15M', { t: 'Heavy Duty camera kit', p: 180, id: 45371118125306, h: 'autochimp-heavy-duty-camera-kit', v: 'WI1yO-7CFko', k: 'ukit', hd: true, box: 'HD', len: 15 });
  def('AC-HEAVY-CAM-KIT-TRAILER', { t: 'Heavy Duty trailer camera kit', p: 240, id: 47805674782970, h: 'autochimp-heavy-duty-trailer-camera-kit', v: 'ESPY5CQTXkE', k: 'ukit', hd: true, box: 'HDTR' });
  def('AC-DUAL-CAM-KIT', { t: 'QuadCam Universal Kit', p: 180, id: 44974555136250, h: 'autochimp-dual-camera-kit-universal', v: '3VujvJdGjIg', k: 'uqc', box: 'UQC' });
  def('AC-PRO-CAM', { t: 'Pro HD camera', p: 110, id: 47050736468218, h: 'autochimp-pro-series-hd-camera-universal-front-rear', k: 'cam' });
  def('AC-PRO-MOUNTING', { t: 'Adjustable Pro mount', p: 20, id: 46258824577274, h: 'autochimp-pro-series-camera-mounting-bracket-ac-mounting-bracket', k: 'mount', ic: 'plate', d: 'Adjustable, no-drill mount for a Pro HD camera.' });
  def('AC-PRO-MOUNTING-TOY', { t: 'Factory tray bracket', p: 15, id: 46975912542458, h: 'autochimp-pro-camera-mount-toyota', k: 'mount', ic: 'traymount', d: 'Pro HD camera bracket that fits Toyota factory trays.' });
  def('AC-MOUNT-HILUX-TAILGATE', { t: 'Tailgate mount', p: 15, id: 46534347096314, h: 'autochimp-tailgate-camera-mount-kit-for-toyota-hilux-2014-2024', k: 'mount', ic: 'tailgate', d: 'Two brackets for the HiLux tailgate (2014–2024).' });
  def('AC-MOUNT-70SERIES-FRONTCAM', { t: 'Grille mount', p: 30, id: 46855631896826, h: 'autochimp-70-series-front-camera-mounting-kit-adjustable-no-drill-mounting-ac-mounting-70series', k: 'mount', ic: 'grille', d: 'Presses into the 70 Series grille. No drilling.' });
  def('AC-MOUNT-SUZUKI-FRONTCAM', { t: 'Jimny bumper mount', p: 30, id: 49712064659706, h: 'autochimp-front-camera-mount-for-suzuki-jimny-2019-2026-universal-mounting-bracket', k: 'mount', ic: 'plate', d: 'Fits the original Jimny bumper. No drilling.' });
  def('AC-HITCH-MOUNT-KIT', { t: 'Hitch plug brackets', p: 22, id: 47125046526202, h: 'autochimp-hitch-mount-kit-universal-camera-bracket', k: 'part', ic: 'hitch', d: 'Left and right brackets with dust covers for the hitch plugs.' });
  def('AC-RETROFIT-KIT', { t: 'Aftermarket stereo adapter', p: 15, id: 48055065575674, h: 'autochimp-camera-interface-retrofit-adapter-kit', k: 'part', ic: 'aftermarket', box: 'RETRO', d: 'Connects your camera system to your stereo’s camera input.' });
  def('AC-TOOL-KIT', { t: 'Install tool kit', p: 35, id: 43770236535034, h: 'autochimp-tool-kit', k: 'addon', ic: 'plug', d: 'Tools for cameras, stereos and dash trim.' });

  const SWD = {
    S22FR_W: '22 mm FRONT + REAR switches, white LED',
    S22FR_B: '22 mm FRONT + REAR switches, blue LED',
    S22C12_B: '22 mm CAM 1 + CAM 2 switches, blue LED',
    S22RT_W: '22 mm REAR + TRAILER switches, white LED',
    D32FR_B: 'a 32 mm dual-button FRONT / REAR switch, blue LED',
    D32C12_B: 'a 32 mm dual-button CAM 1 / CAM 2 switch, blue LED',
    D37C12_G: 'a 37 mm dual-button CAM 1 / CAM 2 switch, green LED, upright and sideways',
    Q32_B: 'two 32 mm dual-button switches for CAM 1–4, blue LED',
    ISU_C12: '23 mm Isuzu-style CAM 1 + CAM 2 switches',
    ISU_FR: 'Isuzu-style FRONT + REAR switches',
    ISU_RT: 'Isuzu-style REAR + TRAILER switches',
    SUZ_FR: 'a Suzuki-style dual-button FRONT / REAR switch'
  };
  const SWV = {
    '22': { label: '2 buttons · 22 mm', hint: 'Two single switches, one per camera.', desc: '2 single-button switches, 22 mm' },
    '32': { label: '1 dual button · 32 mm', hint: 'One switch with a button per camera.', desc: 'a 32 mm dual-button switch' },
    '37': { label: '1 dual button · 37 mm', hint: 'One switch with a button per camera.', desc: 'a 37 mm dual-button switch' }
  };

  const VEH = [
    { id: 'lc70-24', make: 'Toyota', model: 'LandCruiser 70', short: '70', years: '2024–2026', img: 'lc70_24', rev: 'AC-TOY-RC8-RC9', revHD: 'AC-TOY-RC8-RC9-HEAVY', front: 'AC-FRONT-70SERIES', fr: 'AC-FRONTREAR-70SERIES-2024', dual: ['AC-DUAL-70SERIES-2024-22MM', 'AC-DUAL-70SERIES-2024-32MM', 'AC-DUAL-70SERIES-2024-37MM'], quad: 'AC-QUAD-70SERIES-2024', trailer: 'AC-TRAILER-70SERIES-2024', tray: true, grille: true },
    { id: 'lc70-14', make: 'Toyota', model: 'LandCruiser 70', short: '70', years: '2014–2023', img: 'lc70_14', rev: 'AC-TOY-RC7', revHD: 'AC-TOY-RC7-HEAVY', front: 'AC-FRONT-70SERIES-2014', dual: ['AC-DUAL-70SERIES-2014'], quad: 'AC-QUAD-70SERIES-2014', trailer: 'AC-TRAILER-70SERIES-2014', tray: true },
    { id: 'hilux-20', make: 'Toyota', model: 'HiLux', short: 'HiLux', years: '2020–2025', img: 'hilux_20', rev: 'AC-TOY-RC6', front: 'AC-FRONT-HILUX-2020', dual: ['AC-DUAL-HILUX-2020-32MM', 'AC-DUAL-HILUX-2020-22MM'], quad: 'AC-QUAD-HILUX-2020', trailer: 'AC-TRAILER-HILUX-2020', tray: true, tailgate: true },
    { id: 'hilux-16', make: 'Toyota', model: 'HiLux', short: 'HiLux', years: '2016–2020', img: 'hilux_16', rev: 'AC-TOY-RC4', front: 'AC-FRONT-HILUX-2016', dual: ['AC-DUAL-HILUX-2016-22MM', 'AC-DUAL-HILUX-2016-32MM'], quad: 'AC-QUAD-HILUX-2014', trailer: ['AC-TRAILER-HILUX-2016-32MM', 'AC-TRAILER-HILUX-2016-22MM'], tray: true, tailgate: true },
    { id: 'hilux-14', make: 'Toyota', model: 'HiLux', short: 'HiLux', years: '2014–2015', rev: 'AC-TOY-RC3', front: 'AC-FRONT-HILUX-2014', dual: ['AC-DUAL-HILUX-2014'], quad: 'AC-QUAD-HILUX-N70', trailer: 'AC-TRAILER-HILUX-2014', tray: true, tailgate: true },
    { id: 'hilux-06t', make: 'Toyota', model: 'HiLux', short: 'HiLux', years: '2006–2013', sub: 'Touchscreen stereo', rev: 'AC-TOY-RC2', tray: true },
    { id: 'hilux-06', make: 'Toyota', model: 'HiLux', short: 'HiLux', years: '2006–2013', sub: 'Stereo without a touchscreen', rev: 'AC-TOY-RC1', tray: true },
    { id: 'prado-20', make: 'Toyota', model: 'Prado 150', short: 'Prado', years: '2020–2024', sub: 'GX & GXL · 9-inch screen', img: 'prado', rev: 'AC-TOY-RC13', front: 'AC-FRONT-PRADO-2020', dual: ['AC-DUAL-PRADO-2020-32MM', 'AC-DUAL-PRADO-2020-22MM'], quad: 'AC-QUAD-PRADO-2020', trailer: 'AC-TRAILER-PRADO-2020' },
    { id: 'prado-17', make: 'Toyota', model: 'Prado 150', short: 'Prado', years: '2017–2020', sub: 'GX & GXL · 8-inch screen', rev: 'AC-TOY-RC12', front: 'AC-FRONT-PRADO-2014', dual: ['AC-DUAL-PRADO-2014-32MM', 'AC-DUAL-PRADO-2014-37MM', 'AC-DUAL-PRADO-2014-22MM'], quad: 'AC-QUAD-PRADO-2014', trailer: 'AC-TRAILER-PRADO-2014' },
    { id: 'prado-14', make: 'Toyota', model: 'Prado 150', short: 'Prado', years: '2014–2017', sub: 'GX & GXL · 7-inch screen', rev: 'AC-TOY-RC11', front: 'AC-FRONT-PRADO-2014', dual: ['AC-DUAL-PRADO-2014-32MM', 'AC-DUAL-PRADO-2014-37MM', 'AC-DUAL-PRADO-2014-22MM'], quad: 'AC-QUAD-PRADO-2014', trailer: 'AC-TRAILER-PRADO-2014' },
    { id: 'lc200', make: 'Toyota', model: 'LandCruiser 200', short: '200', years: '2015–2021', sub: 'GX & GXL', rev: 'AC-TOY-RC10', front: 'AC-FRONT-LANDCRUISER-2016', dual: ['AC-DUAL-LANDCRUISER-2016-32MM', 'AC-DUAL-LANDCRUISER-2016-37MM', 'AC-DUAL-LANDCRUISER-2016-22MM'], quad: 'AC-QUAD-LANDCRUISER-2016', trailer: 'AC-TRAILER-LANDCRUISER-2016' },
    { id: 'fortuner-20', make: 'Toyota', model: 'Fortuner', short: 'Fortuner', years: '2020–2023', rev: 'PLANNED', front: 'AC-FRONT-FORTUNER-2020', dual: ['AC-DUAL-FORTUNER-2020-32MM', 'AC-DUAL-FORTUNER-2020-22MM'], quad: 'AC-QUAD-FORTUNER-2020', trailer: 'AC-TRAILER-FORTUNER-2020' },
    { id: 'fortuner-16', make: 'Toyota', model: 'Fortuner', short: 'Fortuner', years: '2016–2020', rev: 'PLANNED', front: 'AC-FRONT-FORTUNER-2016', dual: ['AC-DUAL-FORTUNER-2016-32MM', 'AC-DUAL-FORTUNER-2016-37MM', 'AC-DUAL-FORTUNER-2016-22MM'], quad: 'AC-QUAD-FORTUNER-2016', trailer: 'AC-TRAILER-FORTUNER-2016' },
    { id: 'hiace-20', make: 'Toyota', model: 'HiAce', short: 'HiAce', years: '2020–2025', sub: 'Also fits Granvia', rev: 'PLANNED', front: 'AC-FRONT-HIACE-2020', dual: ['AC-DUAL-HIACE-2020-32MM', 'AC-DUAL-HIACE-2020-22MM'], quad: 'AC-QUAD-HIACE-2020', trailer: 'AC-TRAILER-HIACE-2020' },
    { id: 'hiace-14', make: 'Toyota', model: 'HiAce', short: 'HiAce', years: '2014–2019', rev: 'PLANNED', front: 'AC-FRONT-HIACE-2014', dual: ['AC-DUAL-HIACE-2017', 'AC-DUAL-HIACE-2018', 'AC-DUAL-HIACE-2016'], quad: 'AC-QUAD-HIACE-2014', trailer: 'AC-TRAILER-HIACE-2014' },
    { id: 'kluger-20', make: 'Toyota', model: 'Kluger', short: 'Kluger', years: '2020–2023', rev: 'PLANNED', front: 'AC-FRONT-KLUGER-2020', dual: ['AC-DUAL-KLUGER-2020-32MM', 'AC-DUAL-KLUGER-2020-22MM'], quad: 'AC-QUAD-KLUGER-2020', trailer: 'AC-TRAILER-KLUGER-2020' },
    { id: 'kluger-14', make: 'Toyota', model: 'Kluger', short: 'Kluger', years: '2014–2019', rev: 'PLANNED', front: 'AC-FRONT-KLUGER-2014', dual: ['AC-DUAL-KLUGER-2014-32MM', 'AC-DUAL-KLUGER-2014-37MM', 'AC-DUAL-KLUGER-2014-22MM'], quad: 'AC-QUAD-KLUGER-2014', trailer: 'AC-TRAILER-KLUGER-2014' },
    { id: 'dmax-12', make: 'Isuzu', model: 'D-MAX & MU-X', short: 'D-MAX', years: '2012–2024', rev: 'AC-ISUZU-RC1', front: 'AC-FRONT-DMAX-2012', dual: ['AC-DUAL-DMAX-23-C12'], quad: 'AC-QUAD-DMAX-2012', trailer: 'AC-TRAILER-DMAX-2012' },
    { id: 'dmax-25', make: 'Isuzu', model: 'D-MAX', short: 'D-MAX', years: '2025 on', dual: ['AC-DUAL-DMAX-2025'] },
    { id: 'bt50-21', make: 'Mazda', model: 'BT-50', short: 'BT-50', years: '2021–2024', rev: 'AC-MAZ-RC1', front: 'AC-FRONT-MAZ-2021', dual: ['AC-DUAL-MAZ-23-C12'], quad: 'AC-QUAD-MAZ-2021', trailer: 'AC-TRAILER-MAZ-2021' },
    { id: 'jimny-25', make: 'Suzuki', model: 'Jimny / Jimny XL', short: 'Jimny', years: '2025 on', rev: 'AC-SUZ-RC2', front: 'AC-FRONT-JIMNY-2025', dual: ['AC-DUAL-SUZUKI-2025'], quad: 'AC-QUAD-JIMNY-2025', jimnyMount: true },
    { id: 'suzuki-15', make: 'Suzuki', model: 'Jimny & Vitara', short: 'Suzuki', years: '2015–2024', sub: 'Also Grand Vitara', rev: 'AC-SUZ-RC1', front: 'AC-FRONT-JIMNY-2018', dual: ['AC-DUAL-SUZUKI-2015'], quad: 'AC-QUAD-SUZUKI-2015' }
  ];
  const VMAP = {};
  VEH.forEach(function (v) { VMAP[v.id] = v; });
  const MAKES = ['Toyota', 'Isuzu', 'Mazda', 'Suzuki'];

  const MOUNT = {
    kit: { val: 'kit', label: 'Adjustable Pro mount', hint: 'Rear bar, plate or tray. No drilling.', icon: 'plate' },
    pro: { val: 'pro', label: 'Bumper or plate', hint: 'Adjustable Pro mount. No drilling.', icon: 'plate' },
    grille: { val: 'grille', label: 'Grille mount', hint: 'Presses into the grille. No drilling.', icon: 'grille' },
    jimny: { val: 'jimny', label: 'Bumper mount', hint: 'Fits the original Jimny bumper. No drilling.', icon: 'plate' },
    traybracket: { val: 'traybracket', label: 'Factory tray bracket', hint: 'Fits Toyota factory trays.', icon: 'traymount' },
    tailgate: { val: 'tailgate', label: 'Tailgate mount', hint: 'Two brackets for the HiLux tailgate.', icon: 'tailgate' }
  };
  // Trailer parts the 3D step 1 can land on (Shopify, 2 Nov 2026)
  def('AC-SUZI-COIL-KIT-S', { t: 'Suzi coil kit · Small', p: 120, id: 49722789921018, h: 'autochimp-suzi-coil-camera-connection-kit', k: 'coil', ic: 'cable', d: '150 mm coiled, stretches to 1.5 m. Carries one camera across the hitch.' });
  def('AC-SUZI-COIL-KIT-M', { t: 'Suzi coil kit · Medium', p: 120, id: 47972891984122, h: 'autochimp-suzi-coil-camera-connection-kit', k: 'coil', ic: 'cable', d: '300 mm coiled, stretches to 3 m. Carries one camera across the hitch.' });
  def('AC-SUZI-COIL-KIT-L', { t: 'Suzi coil kit · Large', p: 120, id: 47972892016890, h: 'autochimp-suzi-coil-camera-connection-kit', k: 'coil', ic: 'cable', d: '500 mm coiled, stretches to 5 m. Carries one camera across the hitch.' });
  def('AC-SUZI-COIL-KIT-2CH-S', { t: 'Twin-channel Suzi coil kit · Small', p: 130, st: 'draft', k: 'coil', ic: 'cable', d: 'Stretches to 1.5 m. Carries two camera signals across the hitch.' });
  def('AC-SUZI-COIL-KIT-2CH-M', { t: 'Twin-channel Suzi coil kit · Medium', p: 130, st: 'draft', k: 'coil', ic: 'cable', d: 'Stretches to 3 m. Carries two camera signals across the hitch.' });
  def('AC-SUZI-COIL-KIT-2CH-L', { t: 'Twin-channel Suzi coil kit · Large', p: 130, st: 'draft', k: 'coil', ic: 'cable', d: 'Stretches to 5 m. Carries two camera signals across the hitch.' });
  def('AC-SUZI-COIL-KIT-3CH', { t: '3-channel Suzi coil kit', p: null, st: 'draft', k: 'coil', ic: 'cable', d: 'Carries three camera signals across the hitch. No size options in Shopify yet.' });
  [['BLACK', 'Black'], ['WHITE', 'White']].forEach(function (c) {
    ['5', '10', '15'].forEach(function (m) {
      def('AC-HEAVY-TWIN-CAM-KIT-' + c[0] + '-' + m + 'M', { t: 'Heavy Duty Twin camera kit', p: 290, st: 'draft', k: 'ukit', hd: true, twin: true, colour: c[1], len: +m, ic: 'twin' });
    });
  });
  def('AC-PRO-EXTN-8M', { t: 'Pro HD extension cable · 8 m', p: 30, id: 43492364255482, h: 'autochimp-pro-series-extension-cable-8m', k: 'part', ic: 'cable', d: 'Adds 8 m to a Pro HD camera run.' });
  def('AC-HEAVY-5M', { t: 'Heavy Duty extension cable · 5 m', p: 55, id: 46975851462906, h: 'autochimp-heavy-duty-camera-extension-cable', k: 'part', ic: 'cable', d: 'Adds 5 m to a Heavy Duty camera run.' });
  def('AC-HEAVY-10M', { t: 'Heavy Duty extension cable · 10 m', p: 55, id: 46975851495674, h: 'autochimp-heavy-duty-camera-extension-cable', k: 'part', ic: 'cable', d: 'Adds 10 m to a Heavy Duty camera run.' });
  def('AC-HEAVY-15M', { t: 'Heavy Duty extension cable · 15 m', p: 55, id: 46975851528442, h: 'autochimp-heavy-duty-camera-extension-cable', k: 'part', ic: 'cable', d: 'Adds 15 m to a Heavy Duty camera run.' });

  /* ---------- Steps 2 onward: screen, vehicle, reverse check, whose cameras, mounts, switches, your system ----------
     Same questions and rules as the v5 builder (Build Your Camera System · v5), driven by the cameras picked in the 3D view.
     Every result is an existing Shopify SKU. Draft items show as "Draft in Shopify" and stay out of the cart. */
  const SITE = 'https://autochimp.com.au', SMS_NUM = '+61411101737', SMS_SHOW = '0411 101 737';
  // On the website the theme passes its own asset links in window.AC_CB; in the prototype they sit in folders next to the page
  const CB = window.AC_CB || {};
  const IC = function (n) { return (CB.icon && CB.icon[n]) || 'icons/ic-' + n + '.svg'; };
  const PHOTO = Object.assign({ lc70_24: 'img/veh-lc70-2024.webp', lc70_14: 'img/veh-lc70-2014.webp', hilux_20: 'img/veh-hilux-2020.webp', hilux_16: 'img/veh-hilux-2016.webp', prado: 'img/veh-prado150.webp', kitFR70: 'img/kit-frontrear-70-2024.webp', swFront: 'img/sw-front-22.webp', swRear: 'img/sw-rear-22.webp' }, CB.img || {});
  // Running on autochimp.com.au: prices, stock and variant ids are read from the store itself, and only products on sale are offered
  const ON_STORE = !!(window.Shopify && window.Shopify.shop) || /(^|\.)autochimp\.com\.au$/.test(location.hostname);
  let LIVE = false;
  const VIEW = {
    rear: { label: 'Reverse camera', short: 'reverse', icon: 'reverse' },
    front: { label: 'Front camera', short: 'front', icon: 'front' },
    tray: { label: 'Tray camera', short: 'tray', icon: 'tray' },
    side: { label: 'Side camera', short: 'side', icon: 'side' },
    under: { label: 'Underbody camera', short: 'underbody', icon: 'underbody' },
    trailer: { label: 'Trailer camera', short: 'trailer', icon: 'trailer' },
    tside: { label: 'Trailer side camera', short: 'trailer side', icon: 'trailer' }
  };
  const SCR = { factory: 'Factory screen', aftermarket: 'Aftermarket stereo', monitor: 'Monitor or other screen' };
  const STEP_TEXT = {
    see: ['What do you want to see?', 'Tap + on the car or pick below. Drag to look around.'],
    screen: ['What type of display are you using?', 'Or the one you plan to use. It decides how your cameras connect.'],
    vehicle: ['What do you drive?', 'We only show kits that plug straight in.'],
    revcheck: ['Got a reverse camera?', 'Your new cameras share the screen with it. If you don’t have one yet, we can add it.'],
    whose: ['Which cameras are you using?', 'Use AutoChimp cameras, or connect cameras you already have.'],
    setup: ['Set up each camera', 'Mounts and cables for each one.'],
    switches: ['Your switches', 'Pick the size that matches the blank switch covers in your dash. Same price either way.'],
    result: ['Your system', '']
  };
  const TICK_SVG = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M3.5 8.5l3 3 6-7" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  const CHEV_SVG = '<svg viewBox="0 0 12 8" aria-hidden="true"><path d="M1 1.5l5 5 5-5" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  const EXT_SVG = '<svg viewBox="0 0 12 12" aria-hidden="true"><path d="M4.5 2.5H2.5v7h7v-2M6.5 1.5h4v4M10.5 1.5L5.5 6.5" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  Object.assign(state, {
    step: 'see', watch: null, vehicle: null, make: 'Toyota', openModel: null, revcheck: null, revAdded: false, whose: null,
    rearMount: 'kit', frontMount: null, trayMount: 'pro', hitchBrackets: false, twinColour: 'BLACK', lens: {}, swSize: null,
    addTools: false, box: {}, editing: false
  });
  let autoTimer = 0, copyTimer = 0, copied = false;

  /* Daniel, 3 Nov 2026: the vehicle choices read in title case ("GX & GXL · 7-Inch Screen", "Plug & Play") */
  const SMALL_WORDS = ['a', 'an', 'and', 'or', 'of', 'on', 'the', 'to', 'in', 'for', 'at', 'by', 'with'];
  function titleCase(s) {
    return String(s).split(' ').map(function (w, i) {
      if (i > 0 && SMALL_WORDS.indexOf(w) >= 0) return w;
      return w.split('-').map(function (p) { return p ? p.charAt(0).toUpperCase() + p.slice(1) : p; }).join('-');
    }).join(' ');
  }
  function money(n) {
    if (n == null) return '[PRICE]';
    const parts = (Math.round(n * 100) / 100).toFixed(2).split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return '$' + parts.join('.');
  }
  // In the prototype draft kits are shown (marked as drafts); on the store only what's on sale counts
  function avail(sku) { const p = P[sku]; return !!p && (!LIVE || p.st === 'live'); }
  function asList(x) { return x ? (Array.isArray(x) ? x : [x]) : []; }
  function pickVariant(list) {
    const ok = asList(list).filter(avail);
    if (!ok.length) return null;
    return ok.filter(function (x) { return P[x].swv && P[x].swv === state.swSize; })[0] || ok[0];
  }
  function vehKits(v) { return [v.rev, v.revHD, v.front, v.fr, v.quad].concat(asList(v.dual), asList(v.trailer)).filter(function (x) { return x && x !== 'PLANNED'; }); }
  function vehHasLive(v) { return vehKits(v).some(function (x) { return P[x] && P[x].st === 'live'; }); }
  function vehOf() { return state.watch && state.watch !== 'monitor' && state.vehicle && state.vehicle !== 'none' ? VMAP[state.vehicle] || null : null; }
  function bodyHasTray() { return state.body !== 'wagon'; }
  function defFront(veh) { return veh && veh.grille ? 'grille' : (veh && veh.jimnyMount ? 'jimny' : 'pro'); }
  function capFirst(t) { return t ? t.charAt(0).toUpperCase() + t.slice(1) : t; }
  function towKind() { return trl().kind; }
  /* Run fn with some answers swapped in (for "+$15" price chips), then put them back */
  function withState(over, fn) {
    const old = {};
    Object.keys(over).forEach(function (k) { old[k] = state[k]; state[k] = over[k]; });
    try { return fn(); } finally { Object.keys(old).forEach(function (k) { state[k] = old[k]; }); }
  }

  /* Every camera in the build, from the 3D picks. A Twin is one camera with two signals (it takes two inputs). */
  function camList() {
    const out = [], t = state.type;
    const push = function (view, cam, side, grade) { out.push({ view: view, cam: cam, side: side || '', key: view + (side || ''), grade: grade, inputs: grade === 'twin' ? 2 : 1 }); };
    if (state.selected.has('reverse')) push('rear', 'reverse', '', t.reverse);
    if (state.selected.has('front')) push('front', 'front', '', t.front);
    if (state.selected.has('tray') && trayAllowed()) push('tray', 'tray', '', t.tray);
    if (state.selected.has('side')) ['L', 'R'].forEach(function (s) { if (hasSide(state.sides, s)) push('side', 'side', s, t.side); });
    if (state.selected.has('under')) ['B', 'F'].forEach(function (s) { if (hasSide(state.unders, s)) push('under', 'under', s, t.under); });
    if (state.selected.has('trailer')) {
      push('trailer', 'trailer', '', t.trailer);
      ['L', 'R'].forEach(function (s) { if (hasSide(state.tsides, s)) push('tside', 'tside', s, 'soon'); });
    }
    return out;
  }
  function camLabel(c) {
    if (c.view === 'tray') return state.body === 'canopy' ? 'Canopy camera' : 'Tray camera';
    if (c.view === 'side') return (c.side === 'L' ? 'Passenger' : 'Driver') + ' side camera';
    if (c.view === 'under') return (c.side === 'B' ? 'Rear axle' : 'Front axle') + ' camera';
    if (c.view === 'trailer') { const k = towKind(); return (k === 'van' ? 'Caravan' : k === 'camper' ? 'Camper' : k === 'float' ? 'Float' : 'Trailer') + ' camera'; }
    if (c.view === 'tside') return (c.side === 'L' ? 'Passenger' : 'Driver') + '-side trailer camera';
    return VIEW[c.view].label;
  }
  function camIcon(c) {
    if (c.view === 'trailer' || c.view === 'tside') { const k = towKind(); return k === 'van' || k === 'camper' ? 'caravan' : k === 'float' ? 'float' : 'trailer'; }
    return VIEW[c.view].icon;
  }
  function viewsText(cams) {
    const seen = [];
    cams.forEach(function (c) { const s = c.view === 'tside' ? 'trailer sides' : c.view === 'side' ? 'side' : VIEW[c.view].short; if (seen.indexOf(s) < 0) seen.push(s); });
    return capFirst(seen.join(' + '));
  }
  /* Cable to the screen for Heavy Duty and Twin kits (they come in 5, 10 and 15 m).
     Daniel, 2 Nov 2026: 10 m as standard, 5 m for a small trailer, 15 m for a big one. Small = body up to 3.5 m
     (box, tool, enclosed, camper), big = 6 m and over (tandem and off-road vans). Cameras on the vehicle stay at 10 m. */
  function defLen(c) {
    if (c.view === 'trailer') { const L = trl().L; return L <= 3.5 ? '5' : L >= 6 ? '15' : '10'; }
    return '10';
  }
  function lenOf(c) {
    if (state.lens[c.key]) return state.lens[c.key];
    return defLen(c);
  }
  function twinSku(c) { return 'AC-HEAVY-TWIN-CAM-KIT-' + state.twinColour + '-' + lenOf(c) + 'M'; }
  function hdSku(c) { return 'AC-HEAVY-CAM-KIT-' + lenOf(c) + 'M'; }

  /* ---------- Resolver: answers → cart lines. Never invents a SKU: anything without a product becomes a gap. ---------- */
  function resolve() {
    const R = { lines: [], gaps: [], saving: 0, ready: false, universal: false, veh: null, am: false, swGroup: null, swSel: null, swDesc: null, fuNote: false };
    const cams = camList();
    if (!cams.length || !state.watch) return R;
    if (state.watch !== 'monitor' && !state.vehicle) return R;
    R.ready = true;
    const veh = vehOf(), own = state.whose === 'own';
    const add = function (sku, o) { const l = Object.assign({ sku: sku, qty: 1 }, o || {}); R.lines.push(l); return l; };
    const soon = cams.filter(function (c) { return c.view === 'tside'; });
    const real = cams.filter(function (c) { return c.view !== 'tside'; });
    const trailerCam = real.filter(function (c) { return c.view === 'trailer'; })[0];
    const inputs = function (list) { return list.reduce(function (n, c) { return n + c.inputs; }, 0); };
    // across the hitch: the Pro HD unplugs at its own break point; Heavy Duty and Twin ride a Suzi coil
    function hitchParts() {
      if (!trailerCam || own) return;
      const ch = trailerCam.grade === 'twin' ? 2 : trailerCam.grade === 'hd' ? 1 : 0;
      if (ch === 1) add('AC-SUZI-COIL-KIT-' + state.coil, { cam: trailerCam, coil: true });
      else if (ch === 2) add('AC-SUZI-COIL-KIT-2CH-' + state.coil, { cam: trailerCam, coil: true });
      else if (state.hitchBrackets) add('AC-HITCH-MOUNT-KIT', { cam: trailerCam });
    }
    function finish() {
      if (soon.length) R.gaps.push({ k: 'tside-soon', n: soon.length });
      if (R.am) {
        let any = false;
        R.lines.forEach(function (l) { if (l.qc) { l.amNote = true; any = true; } });
        if (any) add('AC-RETROFIT-KIT');
      }
      const qcLine = R.lines.filter(function (l) { return l.qc && R.veh; })[0];
      if (qcLine) {
        const grp = asList(R.veh.dual).indexOf(qcLine.sku) >= 0 ? asList(R.veh.dual) : (asList(R.veh.trailer).indexOf(qcLine.sku) >= 0 ? asList(R.veh.trailer) : null);
        if (grp) { const ok = grp.filter(avail); if (ok.length > 1) { R.swGroup = ok; R.swSel = qcLine.sku; } }
        const qp = P[qcLine.sku];
        R.swDesc = qp.sw ? SWD[qp.sw] : (qp.swv ? SWV[qp.swv].desc : null);
      }
      if (state.addTools && R.lines.length) add('AC-TOOL-KIT');
      return R;
    }

    if (!veh) {
      // Monitor, or a vehicle we don't list: universal kits for any screen with a camera (RCA) input
      R.universal = true;
      if (state.watch === 'factory') R.fuNote = true;
      const n = inputs(real);
      if (n > 4) { R.gaps.push({ k: 'many' }); return finish(); }
      if (own) {
        if (n >= 2) add('AC-DUAL-CAM-KIT', { own: true });
        else R.gaps.push({ k: 'own-single' });
        return finish();
      }
      if (n >= 2) add('AC-DUAL-CAM-KIT');
      real.forEach(function (c) {
        if (c.grade === 'twin') add(twinSku(c), { cam: c });
        else if (c.view === 'trailer') add(c.grade === 'hd' ? 'AC-HEAVY-CAM-KIT-TRAILER' : 'AC-PRO-CAM-KIT-TRAILER', { cam: c });
        else if (c.grade === 'hd') add(hdSku(c), { cam: c });
        else add(c.view === 'rear' ? 'AC-PRO-CAM-KIT-REAR' : c.view === 'front' ? 'AC-PRO-CAM-KIT-FRONT' : 'AC-PRO-CAM-KIT', { cam: c });
      });
      hitchParts();
      return finish();
    }

    // A vehicle we list: its plug-in kits, with a QuadCam (Dual or Quad kit) for cameras beyond the reverse
    R.veh = veh;
    R.am = state.watch === 'aftermarket';
    const rear = real.filter(function (c) { return c.view === 'rear'; })[0];
    const extras = real.filter(function (c) { return c.view !== 'rear'; });
    const n = inputs(extras);
    const need = { pro: 0, grille: 0 }, extraMounts = [];
    const fm = state.frontMount || defFront(veh);
    const rm = (state.rearMount === 'traybracket' && veh.tray && bodyHasTray()) || (state.rearMount === 'tailgate' && veh.tailgate) ? state.rearMount : 'kit';
    const tm = state.trayMount === 'traybracket' && veh.tray && bodyHasTray() ? 'traybracket' : 'pro';
    const dual = pickVariant(veh.dual);
    const quad = veh.quad && avail(veh.quad) ? veh.quad : null;
    function mountNeed(view) {
      if (view === 'rear') {
        if (rm === 'traybracket') extraMounts.push(['AC-PRO-MOUNTING-TOY', 'rear']);
        else if (rm === 'tailgate') extraMounts.push(['AC-MOUNT-HILUX-TAILGATE', 'rear']);
        else need.pro += 1;
      } else if (view === 'front') {
        if (fm === 'grille') need.grille += 1;
        else if (fm === 'jimny') extraMounts.push(['AC-MOUNT-SUZUKI-FRONTCAM', 'front']);
        else need.pro += 1;
      } else if (view === 'tray') {
        if (tm === 'traybracket') extraMounts.push(['AC-PRO-MOUNTING-TOY', 'tray']);
        else need.pro += 1;
      } else need.pro += 1;
    }
    function extraCamera(c) {
      if (c.grade === 'twin') add(twinSku(c), { cam: c });
      else if (c.view === 'trailer') { add(c.grade === 'hd' ? 'AC-HEAVY-CAM-KIT-TRAILER' : 'AC-PRO-CAM-KIT-TRAILER', { cam: c }); if (c.grade !== 'hd') need.pro += 1; }
      else if (c.grade === 'hd') add(hdSku(c), { cam: c });
      else { add('AC-PRO-CAM', { cam: c }); mountNeed(c.view); }
    }
    if (own) {
      if (n === 0) R.gaps.push({ k: 'own-rear' });
      else if (n > 3) R.gaps.push({ k: 'many' });
      else {
        const kit = n === 1 ? dual : quad;
        if (kit) add(kit, { qc: true, own: true });
        else R.gaps.push({ k: 'nokit', view: n === 1 ? extras[0].view : 'multi' });
      }
      return finish();
    }
    let rearLine = null;
    // Daniel, 2 Nov 2026: a Twin on the reverse is the Heavy Duty Twin kit (two cables and the connectors) into the vehicle's
    // QuadCam kit. Both lenses and any other cameras share the QuadCam: the Dual kit for the Twin alone, the Quad kit for more.
    if (rear && rear.grade === 'twin') {
      const total = 2 + n;
      if (total > 4) R.gaps.push({ k: 'many' });
      else {
        const kit = total === 2 ? dual : quad;
        if (kit) { add(kit, { qc: true }); add(twinSku(rear), { cam: rear }); extras.forEach(extraCamera); }
        else R.gaps.push({ k: 'nokit', view: total === 2 ? 'rear' : 'multi', twin: true });
      }
    } else {
    if (rear) {
      if (R.am && n === 0) rearLine = add(rear.grade === 'hd' ? hdSku(rear) : 'AC-PRO-CAM-KIT-REAR', { cam: rear, amUniversal: true });
      else if (rear.grade === 'hd' && !R.am && veh.revHD && avail(veh.revHD)) rearLine = add(veh.revHD, { cam: rear, added: state.revAdded });
      else if (veh.rev === 'PLANNED') R.gaps.push({ k: 'planned', view: 'rear' });
      else if (veh.rev && avail(veh.rev)) {
        rearLine = add(veh.rev, { cam: rear, added: state.revAdded, hdSwap: rear.grade === 'hd' });
        if (!P[veh.rev].nomount) mountNeed('rear');
      } else R.gaps.push({ k: 'nokit', view: 'rear' });
    }
    if (n > 3) R.gaps.push({ k: 'many' });
    else if (n === 1) {
      const x = extras[0];
      if (x.view === 'front' && x.grade === 'pro') {
        const canFR = rearLine && rearLine.sku === veh.rev && !!veh.fr && avail(veh.fr);
        if (canFR) {
          R.lines.splice(R.lines.indexOf(rearLine), 1);
          add(veh.fr, { cam: x, cams: [rear, x], qc: true });
          const pr = P[veh.rev].p, pf = veh.front && P[veh.front] ? P[veh.front].p : null, pb = P[veh.fr].p;
          if (pr != null && pf != null && pb != null) R.saving = Math.round((pr + pf - pb) * 100) / 100;
          mountNeed('front');
        } else if (veh.front && avail(veh.front)) { add(veh.front, { cam: x, qc: true }); mountNeed('front'); }
        else if (dual) { add(dual, { qc: true }); extraCamera(x); }
        else R.gaps.push({ k: 'nokit', view: 'front' });
      } else if (x.view === 'trailer' && x.grade === 'pro') {
        const tk = pickVariant(veh.trailer);
        if (tk) { add(tk, { cam: x, qc: true }); need.pro += 1; }
        else if (dual) { add(dual, { qc: true }); extraCamera(x); }
        else R.gaps.push({ k: 'nokit', view: 'trailer' });
      } else if (dual) { add(dual, { qc: true }); extraCamera(x); }
      else R.gaps.push({ k: 'nokit', view: x.view });
    } else if (n >= 2) {
      if (quad) { add(quad, { qc: true }); extras.forEach(extraCamera); }
      else R.gaps.push({ k: 'nokit', view: 'multi', twin: extras.some(function (c) { return c.grade === 'twin'; }) });
    }
    }
    // coil or hitch brackets only go with a trailer camera that's actually in the build
    if (R.lines.some(function (l) { return l.cam && l.cam.view === 'trailer'; })) hitchParts();
    const prov = { pro: 0, grille: 0 };
    R.lines.forEach(function (l) { const p = P[l.sku]; prov.pro += (p.mp || 0) * l.qty; prov.grille += (p.mg || 0) * l.qty; });
    const xp = Math.max(0, need.pro - prov.pro), xg = Math.max(0, need.grille - prov.grille);
    if (xg) add('AC-MOUNT-70SERIES-FRONTCAM', { qty: xg });
    if (xp) add('AC-PRO-MOUNTING', { qty: xp });
    const em = {};
    extraMounts.forEach(function (e) { em[e[0]] = (em[e[0]] || 0) + 1; });
    Object.keys(em).forEach(function (k) { add(k, { qty: em[k] }); });
    return finish();
  }
  function resolveAc() { return withState({ whose: state.whose || 'ac' }, resolve); }
  function totals(R) {
    let t = 0, pend = 0, buyT = 0;
    const buy = [];
    R.lines.forEach(function (l) {
      const p = P[l.sku];
      if (p.p == null) pend += 1; else t += p.p * l.qty;
      if (p.st === 'live' && p.id && p.p != null && !p.oos) { buy.push(p.id + ':' + l.qty); buyT += p.p * l.qty; }
    });
    R.gaps.forEach(function (g) { if (g.k === 'planned') pend += 1; });
    return { t: Math.round(t * 100) / 100, pend: pend, buy: buy, buyT: Math.round(buyT * 100) / 100, all: buy.length === R.lines.length && R.lines.length > 0 };
  }
  function totalLabel(R) { const T = totals(R); return !R.lines.length ? '' : (T.pend ? money(T.t) + ' + TBC' : money(T.t)); }

  /* ---------- Which questions this build needs ---------- */
  function needRevcheck() {
    const veh = vehOf();
    if (!veh || state.watch !== 'factory') return false;
    const cams = camList().filter(function (c) { return c.view !== 'tside'; });
    if (cams.some(function (c) { return c.view === 'rear'; }) && !state.revAdded) return false;
    return cams.some(function (c) { return c.view !== 'rear'; }) && !!veh.rev && veh.rev !== 'PLANNED' && avail(veh.rev);
  }
  function isFast() {
    const cams = camList();
    // a lone reverse camera skips the setup questions, except a Twin: it has a colour and a cable length to pick
    return !!vehOf() && cams.length === 1 && cams[0].view === 'rear' && cams[0].grade !== 'twin' && !state.revAdded;
  }
  const GROUP = {
    hitch: { field: 'hitchBrackets', label: 'Hitch plug brackets', def: false, opts: [{ val: false, label: 'No brackets' }, { val: true, label: 'Add brackets', hint: 'Left and right brackets with dust covers.', icon: 'hitch' }] },
    colour: { field: 'twinColour', label: 'Colour', def: 'BLACK', opts: [{ val: 'BLACK', label: 'Black' }, { val: 'WHITE', label: 'White', hint: 'Suits a white van.' }] }
  };
  function lenGroup(c) {
    return { field: 'len:' + c.key, label: 'Cable to your screen', def: defLen(c), note: 'Measure from the camera to the screen before you order.', opts: [{ val: '5', label: '5 m' }, { val: '10', label: '10 m' }, { val: '15', label: '15 m' }] };
  }
  function coilGroup() {
    return { field: 'coil', label: 'Suzi coil across the hitch', def: recCoil(), rec: recCoil(), opts: ['S', 'M', 'L'].map(function (k) { return { val: k, label: COILS[k].label, hint: 'Stretches to ' + COILS[k].stretch + ' m' }; }) };
  }
  function getField(f) { return f.indexOf('len:') === 0 ? lenOf(camList().filter(function (c) { return c.key === f.slice(4); })[0] || { key: f.slice(4), view: '', grade: '' }) : state[f]; }
  function setField(f, v) { if (f.indexOf('len:') === 0) state.lens[f.slice(4)] = v; else state[f] = v; }
  function overField(f, v) { if (f.indexOf('len:') === 0) { const L = Object.assign({}, state.lens); L[f.slice(4)] = v; return { lens: L }; } const o = {}; o[f] = v; return o; }
  /* Choices for each camera once the vehicle and screen are known: mounts that fit, cable lengths, hitch parts */
  function setupDefs() {
    const out = [];
    if (state.whose === 'own') return out;
    const R = resolveAc();
    if (!R.ready) return out;
    const veh = R.veh;
    camList().forEach(function (c) {
      if (c.view === 'tside') return;
      const g = [];
      const line = R.lines.filter(function (l) { return (l.cam && l.cam.key === c.key) || (l.cams && l.cams.some(function (x) { return x && x.key === c.key; })); })[0];
      const sku = line ? line.sku : '';
      if (veh && c.grade === 'pro' && line) {
        if (c.view === 'rear' && P[sku].k !== 'ukit' && !P[sku].nomount && !P[sku].hd) {
          const o = [MOUNT.kit];
          if (veh.tray && bodyHasTray()) o.push(MOUNT.traybracket);
          if (veh.tailgate) o.push(MOUNT.tailgate);
          if (o.length > 1) g.push({ field: 'rearMount', label: 'Mount', def: 'kit', kitChip: true, opts: o });
        } else if (c.view === 'front') {
          const o = [];
          if (veh.grille) o.push(MOUNT.grille);
          if (veh.jimnyMount) o.push(MOUNT.jimny);
          o.push(MOUNT.pro);
          if (o.length > 1) g.push({ field: 'frontMount', label: 'Mount', def: defFront(veh), kitChip: true, opts: o });
        } else if (c.view === 'tray' && veh.tray && bodyHasTray()) {
          g.push({ field: 'trayMount', label: 'Mount', def: 'pro', kitChip: false, opts: [MOUNT.pro, MOUNT.traybracket] });
        }
      }
      if (/^AC-HEAVY-CAM-KIT-\d+M$/.test(sku)) g.push(lenGroup(c));
      if (c.grade === 'twin' && line) { g.push(GROUP.colour); g.push(lenGroup(c)); }
      if (c.view === 'trailer' && line) g.push(c.grade === 'pro' ? GROUP.hitch : coilGroup());
      out.push({ cam: c, groups: g });
    });
    return out;
  }
  function stepsFor() {
    const st = ['see', 'screen'];
    if (state.watch === 'factory' || state.watch === 'aftermarket') st.push('vehicle');
    if (needRevcheck()) st.push('revcheck');
    const fast = isFast();
    if (!fast) st.push('whose');
    if (!fast && state.whose !== 'own' && setupDefs().some(function (d) { return d.groups.length > 0; })) st.push('setup');
    if (resolveAc().swGroup) st.push('switches');
    return st;
  }
  /* Before the screen is picked, count steps as if it's a factory screen on a listed vehicle */
  function stepsEstimate() {
    const w = state.watch || 'factory';
    if (state.watch && (w === 'monitor' || state.vehicle)) return stepsFor();
    return withState({ watch: w, vehicle: w === 'monitor' ? state.vehicle : (state.vehicle || 'lc70-24') }, stepsFor);
  }
  function complete(step) {
    if (step === 'see') return cameraCount() > 0;
    if (step === 'screen') return !!state.watch;
    if (step === 'vehicle') return !!state.vehicle;
    if (step === 'revcheck') return state.revcheck === 'yes' || state.revcheck === 'no';
    if (step === 'whose') return !!state.whose;
    return true;
  }

  /* ---------- Moving between steps ---------- */
  function goStep(step) {
    clearTimeout(autoTimer);
    clearNote();
    state.step = step;
    if (step !== 'see') { state.focused = null; state.zoom = false; }
    sheetMin(false);   // each step's questions are in the sheet
    syncUI();
    // fit the car to the space left once the new step is on screen
    if (three) three.overview();
    const pane = $('stepPane'); if (pane) pane.scrollTop = 0;
    const ui = $('ui'); if (ui) ui.scrollTop = 0;
    const st = stepsFor();
    track('builder_step_view', { step: step, step_number: step === 'result' ? st.length + 1 : st.indexOf(step) + 1, steps: st.length + 1 });
    if (step === 'result') { const R = resolveAc(), T = totals(R); track('builder_result', { skus: R.lines.map(function (l) { return l.sku; }), value: T.t, gaps: R.gaps.map(function (g) { return g.k; }), vehicle: state.vehicle || '', screen: state.watch || '' }); }
    say(step === 'result' ? 'Your system' : STEP_TEXT[step][0]);
  }
  function goNext() {
    const st = stepsFor();
    if (state.step === 'result') return;
    if (state.editing) {
      const miss = st.filter(function (x) { return !complete(x); })[0];
      if (miss && miss !== state.step) goStep(miss);
      else if (complete(state.step)) { state.editing = false; goStep('result'); }
      return;
    }
    if (!complete(state.step)) return;
    const i = st.indexOf(state.step);
    goStep(i >= 0 && i < st.length - 1 ? st[i + 1] : 'result');
  }
  function goBack() {
    const st = stepsFor();
    if (state.editing) { state.editing = false; goStep('result'); return; }
    if (state.step === 'result') { goStep(st[st.length - 1]); return; }
    const i = st.indexOf(state.step);
    if (i > 0) goStep(st[i - 1]);
  }
  function auto() { clearTimeout(autoTimer); autoTimer = setTimeout(goNext, 300); }
  function editStep(step) { state.editing = true; goStep(step); }
  function setRevcheck(v) {
    state.revcheck = v;
    if (v === 'no') { if (!state.selected.has('reverse')) { state.selected.add('reverse'); state.revAdded = true; } }
    else if (state.revAdded) { state.selected.delete('reverse'); state.revAdded = false; }
    if (three) three.sync();
  }
  function restart() {
    Object.assign(state, { watch: null, vehicle: null, make: 'Toyota', openModel: null, revcheck: null, whose: null, rearMount: 'kit', frontMount: null, trayMount: 'pro', hitchBrackets: false, twinColour: 'BLACK', lens: {}, swSize: null, addTools: false, box: {}, editing: false });
    if (state.revAdded) { state.selected.delete('reverse'); state.revAdded = false; }
    if (three) three.sync();
    goStep('see');
  }

  /* ---------- Rendering ---------- */
  function optHTML(o) {
    // one choice tile: icon, label, hint, optional chip (under the text on small tiles), tick when picked
    const chip = o.chip ? '<span class="opt-chip' + (o.chipRec ? ' is-rec' : '') + '">' + esc(o.chip) + '</span>' : '';
    return '<button type="button" class="opt' + (o.on ? ' is-on' : '') + (o.small ? ' opt-sm' : '') + '" data-act="' + o.act + '" data-val="' + esc(o.val) + '"' + (o.field ? ' data-field="' + esc(o.field) + '"' : '') + ' aria-pressed="' + (o.on ? 'true' : 'false') + '">' +
      (o.icon ? '<img class="opt-ic" src="' + o.icon + '" alt="" width="40" height="40">' : '') +
      '<span class="opt-tx"><span class="opt-l">' + esc(o.label) + '</span>' + (o.hint ? '<span class="opt-h">' + esc(o.hint) + '</span>' : '') + (o.small ? chip : '') + '</span>' +
      (o.small ? '' : chip) + '<span class="opt-tick">' + TICK_SVG + '</span></button>';
  }
  function helpHTML(title, text, sms) {
    return '<div class="help">' + (title ? '<strong>' + esc(title) + '</strong> ' : '') + esc(text) + (sms ? ' <a href="sms:' + SMS_NUM + '" class="lnk">' + esc(sms) + '</a>' : '') + '</div>';
  }
  function renderScreen() {
    const opts = [
      { val: 'factory', label: 'Factory screen', hint: 'The screen your vehicle came with.', icon: 'factory' },
      { val: 'aftermarket', label: 'Aftermarket stereo', hint: 'A replacement head unit, like CarPlay or Android Auto.', icon: 'aftermarket' },
      { val: 'monitor', label: 'Monitor or other screen', hint: 'A dash, mirror or standalone monitor.', icon: 'monitor', chip: 'Universal' }
    ];
    return '<div class="opts">' + opts.map(function (o) { return optHTML({ act: 'screen', val: o.val, label: o.label, hint: o.hint, icon: IC(o.icon), chip: o.chip, on: state.watch === o.val }); }).join('') + '</div>' +
      helpHTML('Not sure?', 'If the screen shows your vehicle maker’s logo and menus, it’s the factory screen.', 'SMS us a photo · ' + SMS_SHOW);
  }
  function renderVehicle() {
    const shown = VEH.filter(function (v) { return !LIVE || vehKits(v).some(avail); });
    const makes = MAKES.filter(function (m) { return shown.some(function (v) { return v.make === m; }); });
    const cur = makes.indexOf(state.make) >= 0 ? state.make : makes[0];
    const names = [];
    shown.filter(function (v) { return v.make === cur; }).forEach(function (v) { if (names.indexOf(v.model) < 0) names.push(v.model); });
    let h = '<div class="makes" role="group" aria-label="Make">' + makes.map(function (m) { return '<button type="button" class="pill" data-act="make" data-val="' + m + '" aria-pressed="' + (m === cur ? 'true' : 'false') + '">' + m + '</button>'; }).join('') + '</div><div class="models">';
    names.forEach(function (name) {
      const gens = shown.filter(function (v) { return v.make === cur && v.model === name; });
      const key = cur + '|' + name, single = gens.length === 1;
      const selGen = gens.filter(function (g) { return g.id === state.vehicle; })[0];
      const open = !single && state.openModel === key;
      const imgGen = selGen && selGen.img ? selGen : gens.filter(function (g) { return !!g.img; })[0];
      const yrs = []; gens.forEach(function (g) { if (yrs.indexOf(g.years) < 0) yrs.push(g.years); });
      const meta = selGen ? selGen.years + (selGen.sub ? ' · ' + selGen.sub : '') : (single ? gens[0].years + (gens[0].sub ? ' · ' + gens[0].sub : '') : yrs.join(' · '));
      const allDraft = gens.every(function (g) { return !vehHasLive(g); });
      h += '<div class="model' + (selGen ? ' is-on' : '') + (open ? ' is-open' : '') + '">' +
        '<button type="button" class="model-row" data-act="model" data-val="' + esc(key) + '"' + (single ? ' data-veh="' + gens[0].id + '"' : '') + ' aria-expanded="' + (open ? 'true' : 'false') + '">' +
        '<span class="model-img">' + (imgGen ? '<img src="' + PHOTO[imgGen.img] + '" alt="" loading="lazy">' : '<img class="model-ic" src="' + IC('side') + '" alt="">') + '</span>' +
        '<span class="opt-tx"><span class="opt-l">' + esc(name) + '</span><span class="opt-h">' + esc(titleCase(meta + (selGen || single ? ' · plug & play' : ''))) + '</span>' + (allDraft ? '<span class="draft-tag">Kits Not on Sale Yet</span>' : '') + '</span>' +
        (single ? '<span class="opt-tick">' + TICK_SVG + '</span>' : '<span class="model-chev">' + CHEV_SVG + '</span>') + '</button>';
      if (open) {
        h += '<div class="gens">' + gens.map(function (g) {
          const on = g.id === state.vehicle;
          return '<button type="button" class="gen' + (on ? ' is-on' : '') + '" data-act="veh" data-val="' + g.id + '" aria-pressed="' + (on ? 'true' : 'false') + '"><span class="opt-tx"><span class="opt-l">' + esc(titleCase(g.years)) + '</span>' + (g.sub ? '<span class="opt-h">' + esc(titleCase(g.sub)) + '</span>' : '') + '</span>' +
            (vehHasLive(g) ? '<span class="opt-chip">Plug & Play</span>' : '<span class="draft-tag">Not on Sale Yet</span>') + '<span class="opt-tick">' + TICK_SVG + '</span></button>';
        }).join('') + '</div>';
      }
      h += '</div>';
    });
    // Daniel, 3 Nov 2026: "not listed" is a tile like the vehicles, so it's easy to spot
    const uniOn = state.vehicle === 'none';
    h += '<div class="model model-uni' + (uniOn ? ' is-on' : '') + '"><button type="button" class="model-row" data-act="veh" data-val="none" aria-pressed="' + (uniOn ? 'true' : 'false') + '">' +
      '<span class="model-img"><img class="model-ic" src="' + IC('anyvehicle') + '" alt=""></span>' +
      '<span class="opt-tx"><span class="opt-l">Vehicle Not Listed?</span><span class="opt-h">Universal Kit for Any Screen With a Camera Input</span></span>' +
      '<span class="opt-chip">Universal</span><span class="opt-tick">' + TICK_SVG + '</span></button></div>';
    h += '</div>';
    return h;
  }
  /* Price of the build with or without a reverse camera kit (for the "No, add one" option) */
  function totalWithRear(on) {
    const had = state.selected.has('reverse');
    if (on) state.selected.add('reverse'); else state.selected.delete('reverse');
    const t = totals(resolveAc()).t;
    if (had) state.selected.add('reverse'); else state.selected.delete('reverse');
    return t;
  }
  function renderRevcheck() {
    const veh = vehOf();
    const delta = Math.round((totalWithRear(true) - totalWithRear(false)) * 100) / 100;
    const bundles = !!veh.fr && (function () { const had = state.selected.has('reverse'); state.selected.add('reverse'); const r = resolveAc().lines.some(function (l) { return l.sku === veh.fr; }); if (!had) state.selected.delete('reverse'); return r; })();
    const opts = [
      { val: 'yes', label: 'Yes, it has one', hint: 'We’ll build around it.', icon: 'reverse' },
      { val: 'no', label: 'No, add one', hint: 'Reverse camera kit for your ' + veh.short + (delta > 0 ? ' · +' + money(delta) : '') + (bundles ? ' (becomes the Front & Rear kit)' : ''), icon: 'add' },
      { val: 'unsure', label: 'Not sure', hint: 'Try the 10-second test.', icon: 'help' }
    ];
    return '<div class="opts">' + opts.map(function (o) { return optHTML({ act: 'rev', val: o.val, label: o.label, hint: o.hint, icon: IC(o.icon), on: state.revcheck === o.val }); }).join('') + '</div>' +
      (state.revcheck === 'unsure' ? helpHTML('Quick test:', 'turn the ignition on and select reverse. If the screen switches to a camera view, you’ve got one.', 'Still not sure? SMS us a photo') : '');
  }
  function renderWhose() {
    const veh = vehOf();
    // Daniel, 3 Nov 2026: say what the kit does for your own cameras, for the display they picked
    const onScreen = { factory: 'your factory screen', aftermarket: 'your aftermarket stereo', monitor: 'your monitor' }[state.watch] || 'your screen';
    const opts = [
      { val: 'ac', label: 'AutoChimp cameras', hint: 'The cameras you picked, with mounts and cables.', icon: 'prohd' },
      { val: 'own', label: 'I have my own', hint: 'An integration kit to show your own cameras on ' + onScreen + '.', icon: 'own' }
    ];
    let news = '';
    if (veh && veh.fr && state.whose !== 'own') {
      const R = resolveAc();
      if (R.lines.some(function (l) { return l.sku === veh.fr; })) news = helpHTML('Good news:', 'front + reverse on a ' + veh.model + ' ' + veh.years + ' comes as one kit, ' + money(P[veh.fr].p) + ' instead of ' + money(P[veh.rev].p + P[veh.front].p) + ' for two.');
    }
    return '<div class="opts">' + opts.map(function (o) { return optHTML({ act: 'whose', val: o.val, label: o.label, hint: o.hint, icon: IC(o.icon), on: state.whose === o.val }); }).join('') + '</div>' + news;
  }
  function renderSetup() {
    const defs = setupDefs().filter(function (d) { return d.groups.length; });
    return defs.map(function (d) {
      return '<section class="setup-cam"><h3 class="setup-h"><img src="' + IC(camIcon(d.cam)) + '" alt="" width="30" height="30">' + esc(camLabel(d.cam)) + '<span class="setup-type">' + esc(TYPES[d.cam.grade] ? TYPES[d.cam.grade].label : '') + '</span></h3>' +
        d.groups.map(function (g) {
          const cur = getField(g.field) == null ? g.def : getField(g.field);
          const ts = g.opts.map(function (o) { return withState(Object.assign({ whose: state.whose || 'ac' }, overField(g.field, o.val)), function () { return totals(resolve()).t; }); });
          const tMin = Math.min.apply(null, ts);
          return '<div class="grp"><p class="grp-l">' + esc(g.label) + '</p><div class="opts opts-2">' + g.opts.map(function (o, i) {
            const d2 = Math.round((ts[i] - tMin) * 100) / 100;
            const chip = g.rec === o.val ? 'Recommended' : d2 > 0 ? '+' + money(d2) : (o.val === g.def && g.kitChip ? 'In your kit' : '');
            return optHTML({ act: 'field', field: g.field, val: String(o.val), label: o.label, hint: o.hint, icon: o.icon ? IC(o.icon) : '', chip: chip, chipRec: g.rec === o.val, on: String(cur) === String(o.val), small: true });
          }).join('') + '</div>' + (g.note ? '<p class="grp-note">' + esc(g.note) + '</p>' : '') + '</div>';
        }).join('') + '</section>';
    }).join('');
  }
  function renderSwitches() {
    const R = resolveAc();
    const opts = (R.swGroup || []).map(function (sku) {
      const p = P[sku], v = SWV[p.swv] || { label: p.swv || 'Switches', hint: '' };
      return optHTML({ act: 'sw', val: p.swv, label: v.label, hint: v.hint, icon: IC('switch'), on: sku === R.swSel });
    }).join('');
    const sel = R.swSel ? P[R.swSel] : null;
    const photos = sel && sel.sw && sel.sw.indexOf('S22FR') === 0 ? '<div class="sw-photos"><img src="' + PHOTO.swFront + '" alt="22 mm FRONT switch"><img src="' + PHOTO.swRear + '" alt="22 mm REAR switch"></div>' : '';
    return '<div class="opts">' + opts + '</div>' + photos + helpHTML('Not sure?', 'Pop a blank cover out of the dash and measure its width: 22 mm, 32 mm or 37 mm.', 'SMS us a photo of your dash');
  }
  function boxFor(sku, veh) {
    const p = P[sku];
    const m = veh ? 'your ' + veh.model : 'your vehicle';
    const sw = p.sw ? SWD[p.sw] : (p.swv ? SWV[p.swv].desc : null);
    const swl = sw ? capFirst(sw) : null;
    const B = {
      REV: ['Pro HD camera', 'Adjustable Pro mount', 'Plug-in wiring for ' + m, 'Install pack: cable ties, split loom, sealing tape'],
      REV1: ['Pro HD camera', 'Plug-in wiring for ' + m, 'Install pack: cable ties, split loom, sealing tape'],
      REVHD: ['Heavy Duty camera with IR night vision', '10 m Heavy Duty cable', 'Plug-in wiring for ' + m, 'Install pack'],
      FRONT: ['Pro HD camera', p.mg ? 'Grille mount, no drilling' : 'Adjustable Pro mount', 'QuadCam switching module', 'Plug-in wiring for ' + m, swl, 'Install pack'],
      FR: ['2 Pro HD cameras', 'Grille mount and adjustable Pro mount', 'QuadCam switching module', 'Plug-in wiring for ' + m, swl, 'Install pack'],
      TRAILER: ['Pro HD camera', 'Adjustable Pro mount', p.ext === 2 ? '2 × 8 m extension cables' : '8 m extension cable', 'QuadCam switching module', 'Plug-in wiring for ' + m, swl, 'Install pack'],
      QC: ['QuadCam switching module', 'Plug-in wiring for ' + m, swl, 'Connectors and mounting tape'],
      PRO: ['Pro HD camera', 'Adjustable Pro mount', 'Screen (RCA) adapter', 'Install pack'],
      PROTR: ['Pro HD camera', 'Adjustable Pro mount', '2 × 8 m extension cables', 'Screen (RCA) adapter', 'Install pack'],
      HD: ['Heavy Duty camera with IR night vision', (p.len || 10) + ' m Heavy Duty cable', 'Screen (RCA) adapter'],
      HDTR: ['Heavy Duty camera with IR night vision', '5 m and 10 m Heavy Duty cables', 'Screen (RCA) adapter', 'Sealing tape'],
      UQC: ['QuadCam switching module', 'Power cable', 'Screen (RCA) adapter and lead', 'Connectors and mounting tape'],
      RETRO: ['Stereo (RCA) camera adapter', 'RCA video lead']
    };
    const list = p.box ? B[p.box] : null;
    return list ? list.filter(function (x) { return !!x; }) : null;
  }
  function lineTitle(l) {
    const p = P[l.sku];
    let t = p.t;
    const where = l.cam ? camLabel(l.cam).replace(/ camera$/, '').toLowerCase() : '';
    if (p.k === 'cam' && where) t = 'Pro HD camera · ' + where;
    else if ((l.sku === 'AC-PRO-CAM-KIT' || /^AC-HEAVY-CAM-KIT-\d+M$/.test(l.sku)) && where) t = p.t + ' · ' + where;
    else if (p.twin) t = p.t + ' · ' + p.colour.toLowerCase() + ' · ' + p.len + ' m';
    return (l.qty > 1 ? l.qty + ' × ' : '') + t;
  }
  function lineDesc(l, veh) {
    const p = P[l.sku];
    const m = veh ? veh.model : 'your vehicle';
    if (p.d) return p.d;
    if (p.k === 'rev') return p.hd ? 'Heavy Duty camera with IR night vision and plug-in wiring for your ' + m + '.' : 'Pro HD camera and plug-in wiring for your ' + m + '. Plugs in behind the stereo.';
    if (p.k === 'front') return 'Pro HD camera, ' + (p.mg ? 'grille mount' : 'adjustable mount') + ', QuadCam module and dash switch' + (p.sw && p.sw.indexOf('S22') === 0 ? 'es' : '') + '.';
    if (p.k === 'fr') return 'Front and reverse in one kit: two Pro HD cameras, QuadCam module and dash switches.';
    if (p.k === 'dual') return l.own ? 'QuadCam module, plug-in wiring and dash switches for your own cameras.' : 'QuadCam module, plug-in wiring and dash switches. Your cameras are listed below.';
    if (p.k === 'quad') return l.own ? 'Four camera inputs, plug-in wiring and switches for your own cameras.' : 'Four camera inputs, plug-in wiring and switches. Your cameras are listed below.';
    if (p.k === 'trailer') return 'Pro HD camera, extension cables, QuadCam module and dash switches.';
    if (p.twin) return 'Two lenses in one camera: 120° wide and 60° long, with a ' + p.len + ' m cable. Takes two camera inputs.';
    if (p.k === 'ukit') return p.hd ? (l.sku === 'AC-HEAVY-CAM-KIT-TRAILER' ? 'Heavy Duty camera with IR night vision, 5 m and 10 m cables and screen adapter.' : 'Heavy Duty camera with IR night vision, ' + (p.len || 10) + ' m cable and screen adapter.') : (l.sku === 'AC-PRO-CAM-KIT-TRAILER' ? 'Pro HD camera, mount and two 8 m extension cables with a plug break point at the hitch.' : 'Pro HD camera, adjustable mount and screen adapter.');
    if (p.k === 'uqc') return 'Switches between up to four cameras on one screen.';
    if (p.k === 'cam') return 'Plugs into the QuadCam module.';
    return '';
  }
  function lineIcon(l) {
    const p = P[l.sku];
    if (p.ic) return IC(p.ic);
    if (p.k === 'rev') return IC(p.hd ? 'heavy' : 'reverse');
    if (p.k === 'front') return IC('front');
    if (p.k === 'fr') return IC('multi');
    if (p.k === 'trailer') return IC(l.cam ? camIcon(l.cam) : 'trailer');
    if (p.k === 'dual' || p.k === 'quad' || p.k === 'uqc') return IC('switch');
    if (p.k === 'ukit') return IC(p.hd ? 'heavy' : (l.cam ? camIcon(l.cam) : 'prohd'));
    if (p.k === 'cam') return IC(l.cam ? camIcon(l.cam) : 'prohd');
    return IC('prohd');
  }
  function gapText(g, veh, hasLines) {
    const m = veh ? veh.model : 'your vehicle';
    if (g.k === 'planned') return { title: 'Reverse camera kit for the ' + m, text: 'We’re building this kit now. Text us and we’ll let you know when it’s ready' + (hasLines ? ', and the rest of your system works without it.' : '.'), cta: 'Text us to hear when it’s ready' };
    if (g.k === 'nokit' && g.twin) return { title: 'Twin camera for the ' + m, text: 'A Twin camera uses two camera inputs, so this build needs the ' + (g.view === 'multi' ? 'Quad (four-camera)' : 'Dual') + ' kit for the ' + m + ', and it isn’t on sale yet. Text us and we’ll plan it with you.', cta: 'SMS a technician' };
    if (g.k === 'nokit') {
      const what = g.view === 'multi' ? 'three or more cameras' : VIEW[g.view].label.toLowerCase();
      return { title: (g.view === 'multi' ? 'Three or more cameras' : VIEW[g.view].label) + ' for the ' + m, text: 'We don’t have a plug-in kit for ' + what + ' on the ' + m + ' yet. Text us a photo of your dash and screen and we’ll tell you what will work.', cta: 'SMS a technician' };
    }
    if (g.k === 'tside-soon') return { title: 'Trailer side camera' + (g.n > 1 ? 's' : '') + ' · coming soon', text: 'Side cameras for the trailer are on the way. They’ll cross the hitch on the 3-channel Suzi coil, which is coming too. Text us to hear when they’re ready.', cta: 'Text us to hear when they’re ready' };
    if (g.k === 'own-rear') return { title: 'Your own reverse camera', text: 'Text us a photo of your camera’s plug and we’ll match the right plug-in wiring for your screen.', cta: 'SMS us a photo' };
    if (g.k === 'own-single') return { title: 'Nothing else to buy', text: 'One camera plugs straight into your monitor’s camera input, so you don’t need a kit from us. Text us if you’d like a hand.', cta: 'SMS a technician' };
    return { title: 'More than four camera inputs', text: 'One QuadCam module switches up to four cameras (a Twin uses two). Let’s plan a bigger system together.', cta: 'SMS a technician' };
  }
  function partsText(R) {
    const veh = R.veh, cams = camList();
    const rows = ['AutoChimp camera build', viewsText(cams) + ' · ' + SCR[state.watch] + (veh ? ' · ' + veh.model + ' ' + veh.years : (state.watch !== 'monitor' ? ' · vehicle not listed' : ''))];
    R.lines.forEach(function (l) { const p = P[l.sku]; rows.push(l.qty + ' × ' + l.sku + ' · ' + lineTitle(l).replace(/^\d+ × /, '') + ' · ' + (p.p == null ? 'price TBC' : money(p.p * l.qty)) + (p.st === 'draft' ? ' (not on sale yet)' : '')); });
    R.gaps.forEach(function (g) { rows.push('To sort: ' + gapText(g, veh, R.lines.length > 0).title); });
    if (R.lines.length) rows.push('Total ' + totalLabel(R) + ' inc. GST');
    return rows.join('\n');
  }
  function renderResult() {
    const R = resolveAc(), T = totals(R), veh = R.veh, cams = camList();
    const kitLines = R.lines.filter(function (l) { return ['mount', 'part', 'addon', 'coil'].indexOf(P[l.sku].k) < 0; });
    let title = veh ? 'Your camera system for the ' + veh.model : 'Your camera system';
    if (kitLines.length === 1 && !R.gaps.length) title = P[kitLines[0].sku].t + (veh ? ' for ' + veh.model : '');
    if (!R.lines.length) title = R.gaps.length === 1 && R.gaps[0].k === 'planned' ? 'Reverse camera for the ' + (veh ? veh.model : 'your vehicle') : 'Let’s sort your system';
    const single = kitLines.length === 1 ? P[kitLines[0].sku] : null;
    const chips = [];
    if (veh) chips.push('<span class="rchip is-tick">' + TICK_SVG + 'Fits your ' + esc(veh.short) + '</span>');
    else chips.push('<span class="rchip">Universal</span>');
    chips.push('<span class="rchip">' + esc(SCR[state.watch]) + '</span>');
    const why = [];
    if (R.universal) why.push(state.watch === 'monitor' ? 'Works with any screen that has a camera (RCA) input.' : 'Plugs into a camera (RCA) input.');
    else if (R.am) why.push('Made for your vehicle, with a stereo adapter in place of the factory-screen plug.');
    else if (R.lines.length) why.push('Plugs in behind the stereo. No cutting.');
    if (veh && R.lines.length) why.push('Made for the ' + veh.model + ' · ' + veh.years + '.');
    if (R.saving > 0) why.push('Front + reverse in one kit: ' + money(R.saving) + ' less than buying both.');
    if (R.swDesc) why.push('Comes with ' + R.swDesc + '.');
    const coilLine = R.lines.filter(function (l) { return l.coil; })[0];
    if (coilLine) why.push('The trailer camera crosses the hitch on a ' + COILS[state.coil].label.toLowerCase() + ' Suzi coil that stretches to ' + COILS[state.coil].stretch + ' m.');
    else if (R.lines.some(function (l) { return l.cam && l.cam.view === 'trailer' && l.cam.grade === 'pro'; })) why.push('The Pro HD trailer camera unplugs at its own break point at the hitch.');
    const vid = kitLines.filter(function (l) { return !!P[l.sku].v; })[0];
    let h = '<div class="res-head">' + (single && single.img ? '<img class="res-photo" src="' + PHOTO[single.img] + '" alt="' + esc(single.t) + ' contents">' : '') +
      '<h2 class="res-title">' + esc(title) + '</h2><div class="rchips">' + chips.join('') + '</div>' +
      (R.lines.length ? '<p class="res-price"><strong>' + esc(totalLabel(R)) + '</strong> <span>' + (kitLines.length === 1 && R.lines.length === 1 ? esc(kitLines[0].sku) : R.lines.length + (R.lines.length === 1 ? ' item' : ' items') + ' · inc. GST') + '</span></p>' : '') +
      (R.saving > 0 ? '<p class="res-save">Two separate kits would be ' + money(P[veh.rev].p + P[veh.front].p) + '. You save ' + money(R.saving) + '.</p>' : '') + '</div>';
    if (R.fuNote) h += helpHTML('Heads up:', 'universal kits need a screen with a camera (RCA) input. Most factory screens don’t have one, so text us a photo of yours before you order.', 'SMS ' + SMS_SHOW);
    h += '<ul class="lines">' + R.lines.map(function (l, i) {
      const p = P[l.sku], key = l.sku + ':' + i, box = boxFor(l.sku, veh), open = !!state.box[key];
      let note = '';
      if (l.amNote) note = 'Ships without the factory-screen adapter. The stereo adapter below replaces it.';
      if (l.added) note = 'Added because your vehicle doesn’t have one yet.';
      if (l.amUniversal) note = 'A universal rear kit: it plugs straight into your stereo’s camera input.';
      if (l.hdSwap) note = 'You picked Heavy Duty, but there’s no Heavy Duty reverse kit for this setup yet, so this is the Pro HD kit. Text us if you need Heavy Duty.';
      return '<li class="line"><img class="line-ic" src="' + lineIcon(l) + '" alt="" width="38" height="38"><div class="line-main">' +
        '<div class="line-top"><span class="line-t">' + esc(lineTitle(l)) + '</span><span class="line-p' + (p.p == null ? ' is-pend' : '') + '">' + (p.p == null ? 'Price not set' : money(p.p * l.qty)) + '</span></div>' +
        '<p class="line-d">' + esc(lineDesc(l, veh)) + '</p>' +
        '<p class="line-meta"><span class="sku">' + esc(l.sku) + (l.qty > 1 ? ' × ' + l.qty : '') + '</span>' + (p.st === 'draft' ? '<span class="draft-tag">' + (LIVE ? 'Not on sale yet' : 'Draft in Shopify · not on sale') + '</span>' : '') + (p.oos ? '<span class="draft-tag">Sold out right now</span>' : '') +
        (p.st === 'live' && p.h ? '<a class="lnk" href="' + SITE + '/products/' + p.h + '" target="_blank" rel="noopener">View product ' + EXT_SVG + '</a>' : '') + '</p>' +
        (note ? '<p class="line-note">' + esc(note) + '</p>' : '') +
        (box ? '<button type="button" class="lnk box-tog" data-act="box" data-val="' + esc(key) + '" aria-expanded="' + (open ? 'true' : 'false') + '">' + (open ? 'Hide what’s in the box' : 'What’s in the box') + '</button>' + (open ? '<ul class="box">' + box.map(function (t) { return '<li>' + esc(t) + '</li>'; }).join('') + '</ul>' : '') : '') +
        '</div></li>';
    }).join('') + '</ul>';
    if (R.gaps.length) h += R.gaps.map(function (g) { const x = gapText(g, veh, R.lines.length > 0); return '<div class="gap"><strong>' + esc(x.title) + '</strong><p>' + esc(x.text) + '</p><a class="btn-ghost btn-sms" href="sms:' + SMS_NUM + '">' + esc(x.cta) + '</a></div>'; }).join('');
    if (why.length) h += '<div class="why"><p class="grp-l">Why this fits</p><ul>' + why.map(function (t) { return '<li>' + esc(t) + '</li>'; }).join('') + '</ul>' + (vid ? '<a class="lnk" href="https://youtu.be/' + P[vid.sku].v + '" target="_blank" rel="noopener">Watch the install video ' + EXT_SVG + '</a>' : '') + '</div>';
    if (R.lines.length) h += '<button type="button" class="tools-add' + (state.addTools ? ' is-on' : '') + '" data-act="tools" aria-pressed="' + (state.addTools ? 'true' : 'false') + '"><span class="tick-box">' + TICK_SVG + '</span><span class="opt-tx"><span class="opt-l">Add the install tool kit</span><span class="opt-h">Tools for cameras, stereos and dash trim.</span></span><span class="opt-chip">+' + money(P['AC-TOOL-KIT'].p) + '</span></button>';
    // your answers
    const ans = [['You’ll see', viewsText(cams), 'see']];
    ans.push(['Screen', SCR[state.watch], 'screen']);
    if (state.watch !== 'monitor') ans.push(['Vehicle', veh ? veh.model + ' · ' + veh.years : 'Not listed', 'vehicle']);
    if (state.revcheck && stepsFor().indexOf('revcheck') >= 0) ans.push(['Reverse camera', state.revcheck === 'no' ? 'Adding one' : 'Already fitted', 'revcheck']);
    if (!isFast()) ans.push(['Cameras', state.whose === 'own' ? 'My own' : 'AutoChimp', 'whose']);
    if (R.swGroup) ans.push(['Switches', (SWV[P[R.swSel].swv] || { label: '' }).label, 'switches']);
    h += '<div class="answers"><p class="grp-l">Your answers</p><dl>' + ans.map(function (a) { return '<div class="ans"><dt>' + esc(a[0]) + '</dt><dd>' + esc(a[1]) + '</dd><button type="button" class="lnk" data-act="edit" data-val="' + a[2] + '">Change</button></div>'; }).join('') + '</dl></div>';
    const link = shareLink();
    h += '<div class="share"><p class="grp-l">Share this build</p><div class="share-row">' + (link ? '<button type="button" class="btn-ghost" data-act="copylink">' + (copied === 'link' ? 'Link copied' : 'Copy link') + '</button>' : '') +
      '<button type="button" class="btn-ghost" data-act="copy">' + (copied === 'list' ? 'Copied' : 'Copy parts list') + '</button><a class="btn-ghost" data-act="sms" href="sms:' + SMS_NUM + '?&body=' + encodeURIComponent('Hi AutoChimp, here’s my camera build:\n' + partsText(R) + (link ? '\n' + link : '')) + '">SMS it to a technician</a></div>' +
      '<p class="fine">SMS support ' + SMS_SHOW + ' · Mon–Fri, 10am–4pm. Prices include GST.' + (R.lines.some(function (l) { const q = P[l.sku]; return q.st !== 'live' || q.p == null || q.oos; }) ? ' Anything not on sale yet or sold out stays out of the cart.' : '') + '</p>' +
      '<button type="button" class="lnk" data-act="restart">Start over</button></div>';
    return h;
  }

  /* ---------- Header, panel and buttons for the current step ---------- */
  function syncFlow() {
    const step = state.step, see = step === 'see', res = step === 'result';
    const st = stepsEstimate(), idx = st.indexOf(step);
    document.body.classList.toggle('flow-on', !see);
    document.body.classList.toggle('flow-result', res);
    $('stepLabel').textContent = res ? 'Built from your answers' : state.editing ? 'Editing your system' : 'Step ' + (idx + 1) + ' of ' + st.length;
    $('progress').innerHTML = st.map(function (x, i) { return '<span class="' + (res || i <= idx ? 'is-done' : '') + '"></span>'; }).join('');
    $('h1').textContent = STEP_TEXT[step][0];
    let sub = STEP_TEXT[step][1];
    if (step === 'setup') { const v = vehOf(); sub = v ? 'Only mounts that fit your ' + v.short + '.' : 'Mounts and cables for each camera.'; }
    $('hint').textContent = sub; $('hint').hidden = !sub;
    $('rigBlock').hidden = !see;
    $('card').hidden = !see; $('locs').hidden = !see;
    if (!see) $('inset').hidden = true;
    $('stepPane').hidden = see;
    if (!see) {
      const html = step === 'screen' ? renderScreen() : step === 'vehicle' ? renderVehicle() : step === 'revcheck' ? renderRevcheck() : step === 'whose' ? renderWhose() : step === 'setup' ? renderSetup() : step === 'switches' ? renderSwitches() : renderResult();
      $('stepPane').innerHTML = html;
    }
    if (ON_STORE) { try { history.replaceState(history.state, '', location.pathname + '?' + addressQuery()); } catch (err) { /* address bar not writable */ } }
    // the "your system" bar and the buttons
    const R = resolveAc(), T = totals(R), tl = totalLabel(R);
    const cams = camList();
    const sum = [cams.length ? viewsText(cams) : '', state.watch ? SCR[state.watch].toLowerCase() : '', vehOf() ? vehOf().model : (state.vehicle === 'none' && state.watch !== 'monitor' ? 'universal' : '')].filter(Boolean).join(' · ');
    $('sysbar').hidden = see || res || !sum;
    $('sysVal').textContent = sum;
    $('sysTot').textContent = R.ready && R.lines.length ? tl : '';
    $('back').hidden = see;
    $('back').textContent = state.editing ? 'Cancel' : 'Back';
    const go = $('go'), cart = $('cart');
    cart.hidden = !res; go.hidden = res;
    if (res) {
      if (T.buy.length) {
        cart.href = (LIVE ? '' : SITE) + '/cart/' + T.buy.join(',');
        cart.textContent = T.all ? 'Add to cart · ' + money(T.t) : 'Add what’s on sale · ' + money(T.buyT);
        if (LIVE) cart.removeAttribute('target'); else cart.target = '_blank';
      }
      else { cart.href = 'sms:' + SMS_NUM; cart.textContent = 'SMS a technician'; cart.removeAttribute('target'); }
    } else if (!see) {
      const last = idx === st.length - 1;
      go.disabled = !complete(step);
      go.textContent = state.editing ? 'Back to your system' : last ? 'See your system' + (R.ready && R.lines.length ? ' · ' + tl : '') : 'Next';
    }
  }
  function onPane(e) {
    const b = e.target.closest('[data-act]');
    if (!b) return;
    const act = b.dataset.act, v = b.dataset.val;
    if (['screen', 'veh', 'rev', 'whose', 'field', 'sw', 'tools'].indexOf(act) >= 0 || (act === 'model' && b.dataset.veh)) track('builder_answer', { step: state.step, question: act === 'field' ? b.dataset.field : act, answer: act === 'model' ? b.dataset.veh : act === 'tools' ? String(!state.addTools) : v });
    if ((act === 'rev' && v === 'unsure') || act === 'sms') track(act === 'sms' ? 'builder_sms' : 'builder_unsure', { step: state.step });
    if (act === 'screen') { state.watch = v; syncUI(); auto(); }
    else if (act === 'make') { state.make = v; state.openModel = null; syncUI(); }
    else if (act === 'model') {
      if (b.dataset.veh) { state.vehicle = b.dataset.veh; state.openModel = v; syncUI(); auto(); }
      else { state.openModel = state.openModel === v ? null : v; syncUI(); }
    }
    else if (act === 'veh') { state.vehicle = v; if (v !== 'none') state.openModel = VMAP[v].make + '|' + VMAP[v].model; syncUI(); auto(); }
    else if (act === 'rev') { setRevcheck(v); syncUI(); if (v !== 'unsure') auto(); }
    else if (act === 'whose') { state.whose = v; syncUI(); auto(); }
    else if (act === 'field') { const f = b.dataset.field; setField(f, v === 'true' ? true : v === 'false' ? false : v); if (three) { if (f === 'twinColour') three.recolour(); else three.sync(); } syncUI(); }
    else if (act === 'sw') { state.swSize = v; syncUI(); }
    else if (act === 'box') { state.box[v] = !state.box[v]; syncUI(); }
    else if (act === 'tools') { state.addTools = !state.addTools; syncUI(); }
    else if (act === 'edit') { if (v === 'see') { state.editing = false; goStep('see'); } else editStep(v); }
    else if (act === 'restart') restart();
    else if (act === 'copy' || act === 'copylink') {
      const text = act === 'copy' ? partsText(resolveAc()) : shareLink();
      // the clipboard API can be blocked inside an embedded page, so fall back to a hidden text box
      const fallback = function () {
        try { const ta = document.createElement('textarea'); ta.value = text; ta.setAttribute('readonly', ''); ta.style.cssText = 'position:fixed;left:-9999px;top:0'; document.body.appendChild(ta); ta.select(); document.execCommand('copy'); ta.remove(); } catch (err) { /* nothing more to try */ }
      };
      try { if (navigator.clipboard && navigator.clipboard.writeText) navigator.clipboard.writeText(text).catch(fallback); else fallback(); } catch (err) { fallback(); }
      copied = act === 'copy' ? 'list' : 'link'; syncUI(); clearTimeout(copyTimer); copyTimer = setTimeout(function () { copied = false; syncUI(); }, 1800);
    }
  }
  /* ---------- Tracking: builder events for GA4 / Google Ads (dataLayer) and Shopify customer events ---------- */
  function track(name, data) {
    try {
      (window.dataLayer = window.dataLayer || []).push(Object.assign({ event: name }, data || {}));
      if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') window.Shopify.analytics.publish(name, data || {});
    } catch (err) { /* tracking must never break the builder */ }
  }

  /* ---------- A build lives in the page address, so it can be shared, texted to support or picked up from a vehicle page ----------
     e.g. /pages/build-your-system?cams=reverse-pro,front-pro&screen=factory&vehicle=lc70-24 */
  const CAM_IDS = ['reverse', 'front', 'tray', 'side', 'under', 'trailer'];
  function buildParams(forShare) {
    const q = new URLSearchParams();
    q.set('cams', CAM_IDS.filter(function (id) { return state.selected.has(id); }).map(function (id) { return id + '-' + state.type[id]; }).join(','));
    if (state.selected.has('side')) q.set('sides', state.sides);
    if (state.selected.has('under')) q.set('under', state.unders);
    if (state.towing !== 'none') { q.set('tow', state.towing); q.set('pos', state.trailerPos); q.set('coil', state.coil); if (state.tsides !== 'none') q.set('tsides', state.tsides); }
    q.set('body', state.body);
    if (state.watch) q.set('screen', state.watch);
    if (state.vehicle) q.set('vehicle', state.vehicle);
    if (state.revcheck) q.set('rev', state.revcheck);
    if (state.whose) q.set('who', state.whose);
    if (state.rearMount !== 'kit') q.set('rm', state.rearMount);
    if (state.frontMount) q.set('fm', state.frontMount);
    if (state.trayMount !== 'pro') q.set('tm', state.trayMount);
    if (state.hitchBrackets) q.set('hb', '1');
    if (state.twinColour !== 'BLACK') q.set('col', state.twinColour);
    const lens = Object.keys(state.lens).map(function (k) { return k + ':' + state.lens[k]; });
    if (lens.length) q.set('len', lens.join(','));
    if (state.swSize) q.set('sw', state.swSize);
    if (state.addTools) q.set('tools', '1');
    if (state.paint && state.paint !== 'sand') q.set('paint', state.paint);
    if (forShare || state.step === 'result') q.set('step', 'result');
    return q.toString();
  }
  // The address bar keeps anything that isn't ours (utm_*, fbclid, gclid, preview_theme_id) so ad tracking and theme previews survive.
  const URL_KEYS = ['cams', 'sides', 'under', 'tow', 'pos', 'coil', 'tsides', 'body', 'screen', 'vehicle', 'rev', 'who', 'rm', 'fm', 'tm', 'hb', 'col', 'len', 'sw', 'tools', 'paint', 'step'];
  function addressQuery() {
    let keep;
    try { keep = new URLSearchParams(location.search); } catch (err) { keep = new URLSearchParams(); }
    URL_KEYS.forEach(function (k) { keep.delete(k); });
    const other = keep.toString(), mine = buildParams();
    return other ? other + '&' + mine : mine;
  }
  // A link only makes sense once the builder is on the website. One shared from a theme preview opens that preview:
  // Shopify drops preview_theme_id from the address once its preview cookie is set, so fall back to the theme the page runs on.
  function shareLink() {
    if (!ON_STORE) return '';
    let pv = null;
    try { pv = new URLSearchParams(location.search).get('preview_theme_id'); } catch (err) { /* old browser */ }
    const th = window.Shopify && window.Shopify.theme;
    if (!pv && th && th.id && th.role && th.role !== 'main') pv = String(th.id);
    return location.origin + location.pathname + '?' + (pv ? 'preview_theme_id=' + encodeURIComponent(pv) + '&' : '') + buildParams(true);
  }
  function restoreFromURL() {
    let q;
    try { q = new URLSearchParams(location.search); } catch (err) { return; }
    if (!q.get('cams') && !q.get('vehicle')) return;
    const one = function (v, list) { return list.indexOf(v) >= 0 ? v : null; };
    if (q.get('cams')) {
      state.selected = new Set();
      q.get('cams').split(',').forEach(function (x) {
        const a = x.split('-'), id = a[0], ty = a[1];
        if (CAM_IDS.indexOf(id) < 0) return;
        state.selected.add(id);
        if (ty && (id === 'side' || id === 'under' ? SIDE_TYPES : TYPE_KEYS).indexOf(ty) >= 0) state.type[id] = ty;
      });
    }
    state.sides = one(q.get('sides'), ['L', 'R', 'both']) || state.sides;
    state.unders = one(q.get('under'), ['B', 'F', 'both']) || state.unders;
    const tow = q.get('tow');
    if (tow && trailerById[tow]) {
      const t = trailerById[tow];
      state.towing = tow; state.trailer = tow; state.trailerPos = t.def; state.coil = t.coil || 'S';
      if (!q.get('cams') || !/trailer-/.test(q.get('cams'))) state.type.trailer = t.type;
      state.trailerPos = one(q.get('pos'), t.pos) || state.trailerPos;
      state.coil = one(q.get('coil'), ['S', 'M', 'L']) || state.coil;
      state.tsides = one(q.get('tsides'), ['L', 'R', 'both']) || 'none';
    } else if (state.selected.has('trailer')) { state.towing = state.trailer; }
    if (bodyById[q.get('body')]) state.body = q.get('body');
    state.watch = one(q.get('screen'), ['factory', 'aftermarket', 'monitor']);
    const v = q.get('vehicle');
    if (v === 'none' || VMAP[v]) { state.vehicle = v; if (VMAP[v]) { state.make = VMAP[v].make; state.openModel = VMAP[v].make + '|' + VMAP[v].model; } }
    state.revcheck = one(q.get('rev'), ['yes', 'no', 'unsure']);
    if (state.revcheck === 'no' && state.selected.has('reverse')) state.revAdded = true;
    state.whose = one(q.get('who'), ['ac', 'own']);
    state.rearMount = one(q.get('rm'), ['kit', 'traybracket', 'tailgate']) || 'kit';
    state.frontMount = one(q.get('fm'), ['grille', 'jimny', 'pro']);
    state.trayMount = one(q.get('tm'), ['pro', 'traybracket']) || 'pro';
    state.hitchBrackets = q.get('hb') === '1';
    state.twinColour = one(q.get('col'), ['BLACK', 'WHITE']) || 'BLACK';
    state.paint = one(q.get('paint'), Object.keys(PAINT)) || 'sand';
    (q.get('len') || '').split(',').forEach(function (x) { const a = x.split(':'); if (a[0] && one(a[1], ['5', '10', '15'])) state.lens[a[0]] = a[1]; });
    state.swSize = one(q.get('sw'), ['22', '32', '37']);
    state.addTools = q.get('tools') === '1';
    if (q.get('step') === 'result' && stepsFor().every(complete)) state.step = 'result';
  }

  /* ---------- On the website: live prices, stock and variant ids from the store, and a real add to cart ---------- */
  function loadLive() {
    if (!ON_STORE || !window.fetch) return;
    const map = {};
    let n = 1;
    const done = function () {
      if (!Object.keys(map).length) return;
      Object.keys(P).forEach(function (sku) {
        const m = map[sku], p = P[sku];
        if (m && m.p > 0) { p.st = 'live'; p.p = m.p; p.id = m.id; p.h = m.h; p.oos = !m.ok; }
        else { p.st = 'draft'; if (m) p.p = null; }
      });
      LIVE = true;
      syncUI();
    };
    const page = function () {
      fetch('/products.json?limit=250&page=' + n, { credentials: 'same-origin' }).then(function (r) { return r.ok ? r.json() : null; }).then(function (j) {
        if (!j || !j.products) { done(); return; }
        j.products.forEach(function (pr) { (pr.variants || []).forEach(function (v) { if (v.sku) map[v.sku] = { id: v.id, p: Math.round(parseFloat(v.price) * 100) / 100, ok: !!v.available, h: pr.handle }; }); });
        if (j.products.length === 250 && n < 8) { n += 1; page(); } else done();
      }).catch(done);
    };
    page();
  }
  function cartItems(T) { return T.buy.map(function (x) { const a = x.split(':'); return { id: +a[0], quantity: +a[1] }; }); }
  function onCart(e) {
    const R = resolveAc(), T = totals(R);
    if (!T.buy.length) return;   // the button is an SMS link instead
    track('builder_add_to_cart', { skus: R.lines.map(function (l) { return l.sku; }), value: T.buyT, items: T.buy.length });
    if (!LIVE) return;           // prototype: the link opens a cart on autochimp.com.au
    e.preventDefault();
    fetch('/cart/add.js', { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items: cartItems(T) }) })
      .then(function (r) { if (!r.ok) throw new Error('cart ' + r.status); location.href = '/cart'; })
      .catch(function () { location.href = '/cart/' + T.buy.join(','); });
  }
  function initFlow() {
    $('cart').addEventListener('click', onCart);
    loadLive();
  }
  try { restoreFromURL(); } catch (err) { if (window.console) console.warn('Build link not read', err); }

  // for checking builds from the browser console
  window.__flow = { state: state, resolve: resolveAc, totals: totals, stepsFor: stepsFor, camList: camList, P: P, VEH: VEH, partsText: function () { return partsText(resolveAc()); } };

  /* ---------- 3D ---------- */
  function initThree() {
    const T = window.THREE;
    const canvas = $('stage');
    let renderer;
    try {
      renderer = new T.WebGLRenderer({ canvas: canvas, antialias: true, alpha: true, powerPreference: 'high-performance' });
    } catch (err) { return null; }
    const DPR = Math.min(window.devicePixelRatio || 1, 2);
    renderer.setPixelRatio(DPR);
    renderer.outputEncoding = T.sRGBEncoding;
    renderer.toneMapping = T.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.05;
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = T.PCFSoftShadowMap;
    renderer.localClippingEnabled = true;
    renderer.setClearColor(0x000000, 0);

    const scene = new T.Scene();
    const camera = new T.PerspectiveCamera(30, 1, 0.05, 260);
    camera.layers.enable(1);
    camera.layers.enable(3);
    const C = function (hex) { return new T.Color(hex).convertSRGBToLinear(); };
    function dirFromPitch(deg, back) { return [0, -Math.sin(deg * RAD), (back ? -1 : 1) * Math.cos(deg * RAD)]; }
    function norm(v) { const l = Math.hypot(v[0], v[1], v[2]); return [v[0] / l, v[1] / l, v[2] / l]; }

    /* Lights */
    const hemi = new T.HemisphereLight(0xffffff, 0xb6bfbf, 0.85);
    scene.add(hemi);
    const sun = new T.DirectionalLight(0xffffff, 1.5);
    sun.position.set(5.5, 11, 6.5);
    sun.target.position.set(0, 0, -3.6);
    scene.add(sun.target);
    sun.castShadow = true;
    const bigShadow = window.innerWidth >= 900;
    sun.shadow.mapSize.set(bigShadow ? 2048 : 1024, bigShadow ? 2048 : 1024);
    const sc = sun.shadow.camera;
    sc.left = -10.5; sc.right = 10.5; sc.top = 10.5; sc.bottom = -10.5; sc.near = 1; sc.far = 44;
    sun.shadow.bias = -0.0004;
    sun.shadow.normalBias = 0.02;
    scene.add(sun);
    const fill = new T.DirectionalLight(0xffffff, 0.35);
    fill.position.set(-6, 5, -8);
    scene.add(fill);
    const LIGHT_DAY = { hemi: 0.85, sun: 1.5, fill: 0.35 };
    // Infrared illuminator for the Heavy Duty night view (only switched on while drawing that view)
    const irSpot = new T.SpotLight(0xffffff, 0, 20, 64 * RAD, 0.6, 1.7);
    scene.add(irSpot); scene.add(irSpot.target);

    /* Studio reflections */
    function makeEnv(r, dark) {
      const pm = new T.PMREMGenerator(r);
      const es = new T.Scene();
      const geo = new T.SphereGeometry(20, 32, 16);
      const pos = geo.attributes.position;
      const cols = [];
      const k = dark ? 0.02 : 1;
      const top = new T.Color(1, 1, 1).multiplyScalar(k), hor = new T.Color(0.70, 0.73, 0.73).multiplyScalar(k), bot = new T.Color(0.26, 0.28, 0.28).multiplyScalar(k), tmp = new T.Color();
      for (let i = 0; i < pos.count; i++) {
        const y = pos.getY(i) / 20;
        if (y >= 0) tmp.copy(hor).lerp(top, Math.pow(y, 0.6)); else tmp.copy(hor).lerp(bot, Math.pow(-y, 0.5));
        cols.push(tmp.r, tmp.g, tmp.b);
      }
      geo.setAttribute('color', new T.Float32BufferAttribute(cols, 3));
      es.add(new T.Mesh(geo, new T.MeshBasicMaterial({ vertexColors: true, side: T.BackSide })));
      if (!dark) {
        [[10, 4, 0, 14, 6, 3.2], [6, 6, -14, 6, -6, 1.6], [6, 6, 14, 5, 4, 1.8]].forEach(function (p) {
          const m = new T.Mesh(new T.PlaneGeometry(p[0], p[1]), new T.MeshBasicMaterial({ color: new T.Color(p[5], p[5], p[5]), side: T.DoubleSide }));
          m.position.set(p[2], p[3], p[4]); m.lookAt(0, 0, 0); es.add(m);
        });
      }
      const tex = pm.fromScene(es, 0.04).texture;
      pm.dispose();
      return tex;
    }
    const envMain = makeEnv(renderer, false);
    scene.environment = envMain;

    /* Materials */
    const M = {
      body: new T.MeshStandardMaterial({ color: C('#C8B896'), roughness: 0.5, metalness: 0, envMapIntensity: 0.55 }),
      trim: new T.MeshStandardMaterial({ color: C('#3B4043'), roughness: 0.62, metalness: 0.05, envMapIntensity: 0.45 }),
      glass: new T.MeshStandardMaterial({ color: C('#2C343A'), roughness: 0.07, metalness: 0.1, envMapIntensity: 1.1 }),
      tyre: new T.MeshStandardMaterial({ color: C('#2A2D2F'), roughness: 0.92, envMapIntensity: 0.25, side: T.DoubleSide }),
      rim: new T.MeshStandardMaterial({ color: C('#C4C9CB'), roughness: 0.38, metalness: 0.35, envMapIntensity: 0.8 }),
      alloy: new T.MeshStandardMaterial({ color: C('#D3D7D9'), roughness: 0.34, metalness: 0.4, envMapIntensity: 0.9 }),
      alloyDark: new T.MeshStandardMaterial({ color: C('#B9BEC1'), roughness: 0.36, metalness: 0.4, envMapIntensity: 0.9 }),
      steel: new T.MeshStandardMaterial({ color: C('#2F3437'), roughness: 0.45, metalness: 0.3, envMapIntensity: 0.7 }),
      lamp: new T.MeshStandardMaterial({ color: C('#F4F6F6'), roughness: 0.15, emissive: C('#FFFFFF'), emissiveIntensity: 0.12 }),
      tail: new T.MeshStandardMaterial({ color: C('#A83A31'), roughness: 0.3, emissive: C('#A83A31'), emissiveIntensity: 0.15 }),
      dark: new T.MeshStandardMaterial({ color: C('#24282A'), roughness: 0.7 }),
      cargo: new T.MeshStandardMaterial({ color: C('#A3AAAC'), roughness: 0.6 }),
      canopy: new T.MeshStandardMaterial({ color: C('#1D2023'), roughness: 0.5, metalness: 0.25, envMapIntensity: 0.7, transparent: true }),
      canopyDoor: new T.MeshStandardMaterial({ color: C('#2A2E31'), roughness: 0.45, metalness: 0.25, envMapIntensity: 0.7, transparent: true }),
      canopyGlass: new T.MeshStandardMaterial({ color: C('#2C343A'), roughness: 0.07, metalness: 0.1, envMapIntensity: 1.1, transparent: true }),
      camBody: new T.MeshStandardMaterial({ color: C('#1C2022'), roughness: 0.32, metalness: 0.2 }),
      camLens: new T.MeshStandardMaterial({ color: C('#0E1214'), roughness: 0.05, metalness: 0.3, envMapIntensity: 1.4 }),
      camRing: new T.MeshStandardMaterial({ color: C('#046E82'), roughness: 0.3, emissive: C('#046E82'), emissiveIntensity: 0.4 }),
      irLed: new T.MeshStandardMaterial({ color: C('#3A1614'), roughness: 0.2, metalness: 0.1 }),
      coil: new T.MeshStandardMaterial({ color: C('#1E2224'), roughness: 0.45, metalness: 0.05, envMapIntensity: 0.8 }),
      prop: new T.MeshStandardMaterial({ color: C('#C9CED0'), roughness: 0.7 }),
      propBand: new T.MeshStandardMaterial({ color: C('#F3F4F4'), roughness: 0.5 })
    };
    // Trailer materials fade in and out together
    const TM = {
      body: M.body.clone(), trim: M.trim.clone(), glass: M.glass.clone(), tyre: M.tyre.clone(), rim: M.rim.clone(),
      tail: M.tail.clone(), door: M.body.clone(), galv: M.alloy.clone(), dark: M.dark.clone()
    };
    TM.body.color = C('#ECEDEA');
    TM.door.color = C('#DCDFDC');
    TM.galv.color = C('#BFC4C6');
    Object.keys(TM).forEach(function (k) { TM[k].transparent = true; });

    /* Geometry helpers */
    function rrectShape(w, h, r) {
      const s = new T.Shape(), x = -w / 2, y = -h / 2;
      r = Math.max(0.0001, Math.min(r, w / 2, h / 2));
      s.moveTo(x + r, y); s.lineTo(x + w - r, y); s.quadraticCurveTo(x + w, y, x + w, y + r);
      s.lineTo(x + w, y + h - r); s.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
      s.lineTo(x + r, y + h); s.quadraticCurveTo(x, y + h, x, y + h - r);
      s.lineTo(x, y + r); s.quadraticCurveTo(x, y, x + r, y);
      return s;
    }
    function rbox(w, h, d, r) {
      r = Math.min(r === undefined ? 0.02 : r, w * 0.45, h * 0.45, d * 0.45);
      const depth = Math.max(0.0005, d - 2 * r);
      const g = new T.ExtrudeGeometry(rrectShape(w - 2 * r, h - 2 * r, r * 0.8), { depth: depth, bevelEnabled: true, bevelThickness: r, bevelSize: r, bevelSegments: 2, curveSegments: 4, steps: 1 });
      g.translate(0, 0, -depth / 2);
      return g;
    }
    /* Extrude a side profile (shape x = world Z, shape y = world Y) across the width. */
    function profileX(shape, width, bevel) {
      const depth = Math.max(0.001, width - 2 * bevel);
      const g = new T.ExtrudeGeometry(shape, { depth: depth, bevelEnabled: true, bevelThickness: bevel, bevelSize: bevel, bevelSegments: 3, curveSegments: 20, steps: 1 });
      g.rotateY(-Math.PI / 2);
      g.translate(depth / 2, 0, 0);
      return g;
    }
    function v3(p) { return new T.Vector3(p[0], p[1], p[2]); }
    function tube(pts, r) {
      const curve = new T.CatmullRomCurve3(pts.map(v3));
      return new T.TubeGeometry(curve, pts.length > 2 ? 40 : 4, r, 10, false);
    }
    /* Straight bars with tight bends, for a square-shouldered bullbar */
    function barTube(pts, r, bend) {
      const P = pts.map(v3), path = new T.CurvePath();
      let prev = P[0].clone();
      for (let i = 1; i < P.length - 1; i++) {
        const a = P[i - 1], b = P[i], c = P[i + 1];
        const d1 = b.clone().sub(a), d2 = c.clone().sub(b);
        const rr = Math.min(bend, d1.length() / 2, d2.length() / 2);
        const p1 = b.clone().addScaledVector(d1.normalize(), -rr), p2 = b.clone().addScaledVector(d2.normalize(), rr);
        path.add(new T.LineCurve3(prev, p1));
        path.add(new T.QuadraticBezierCurve3(p1, b.clone(), p2));
        prev = p2;
      }
      path.add(new T.LineCurve3(prev, P[P.length - 1].clone()));
      return new T.TubeGeometry(path, 90, r, 10, false);
    }
    function tyreGeo(R, W) {
      const r0 = R * 0.63, hw = W / 2, k = W * 0.24, pts = [];
      pts.push(new T.Vector2(r0, -hw * 0.9));
      pts.push(new T.Vector2(r0 + (R - k - r0) * 0.5, -hw));
      for (let i = 0; i <= 6; i++) { const t = -Math.PI / 2 + (i / 6) * (Math.PI / 2); pts.push(new T.Vector2(R - k + k * Math.cos(t), -hw + k + k * Math.sin(t))); }
      for (let i = 0; i <= 6; i++) { const t = (i / 6) * (Math.PI / 2); pts.push(new T.Vector2(R - k + k * Math.cos(t), hw - k + k * Math.sin(t))); }
      pts.push(new T.Vector2(r0 + (R - k - r0) * 0.5, hw));
      pts.push(new T.Vector2(r0, hw * 0.9));
      const g = new T.LatheGeometry(pts, 48);
      g.rotateZ(Math.PI / 2);
      return g;
    }
    function makeWheel(R, W, mats) {
      const g = new T.Group();
      g.add(new T.Mesh(tyreGeo(R, W), mats.tyre));
      const rimG = new T.CylinderGeometry(R * 0.63, R * 0.63, W * 0.8, 36); rimG.rotateZ(Math.PI / 2);
      g.add(new T.Mesh(rimG, mats.rim));
      const hubG = new T.CylinderGeometry(R * 0.2, R * 0.2, W * 0.86, 20); hubG.rotateZ(Math.PI / 2);
      g.add(new T.Mesh(hubG, mats.trim));
      for (let i = 0; i < 6; i++) {
        const a = (i / 6) * Math.PI * 2;
        const hG = new T.CylinderGeometry(R * 0.075, R * 0.075, W * 0.82, 14); hG.rotateZ(Math.PI / 2);
        const h = new T.Mesh(hG, mats.trim);
        h.position.set(0, Math.cos(a) * R * 0.4, Math.sin(a) * R * 0.4);
        g.add(h);
      }
      g.traverse(function (o) { if (o.isMesh) { o.castShadow = true; o.receiveShadow = true; } });
      return g;
    }
    function archFlare(cz, cy, rIn, rOut, yCut, width) {
      const s = new T.Shape();
      const aOut = Math.asin((yCut - cy) / rOut), aIn = Math.asin((yCut - cy) / rIn);
      s.moveTo(cz + rOut * Math.cos(Math.PI - aOut), yCut);
      s.absarc(cz, cy, rOut, Math.PI - aOut, aOut, true);
      s.lineTo(cz + rIn * Math.cos(aIn), yCut);
      s.absarc(cz, cy, rIn, aIn, Math.PI - aIn, false);
      return profileX(s, width, 0.015);
    }
    /* Wheel arches cut into a side profile, travelling front-wards from the rear. */
    function archPath(s, zs, cy, r, bot) {
      const a = Math.asin((bot - cy) / r), dx = r * Math.cos(a);
      s.lineTo(zs[0] - dx, bot);
      if (zs.length === 1) { s.absarc(zs[0], cy, r, Math.PI - a, a, true); return; }
      s.absarc(zs[0], cy, r, Math.PI - a, Math.PI / 2, true);
      s.lineTo(zs[1], cy + r);
      s.absarc(zs[1], cy, r, Math.PI / 2, a, true);
    }
    function blobTexture() {
      const c = document.createElement('canvas'); c.width = c.height = 128;
      const g = c.getContext('2d');
      const grd = g.createRadialGradient(64, 64, 0, 64, 64, 64);
      grd.addColorStop(0, 'rgba(0,0,0,0.55)'); grd.addColorStop(0.55, 'rgba(0,0,0,0.22)'); grd.addColorStop(1, 'rgba(0,0,0,0)');
      g.fillStyle = grd; g.fillRect(0, 0, 128, 128);
      return new T.CanvasTexture(c);
    }
    const blobTex = blobTexture();
    function blob(parent, w, d, z, op) {
      const m = new T.Mesh(new T.PlaneGeometry(w, d), new T.MeshBasicMaterial({ map: blobTex, color: 0x000000, transparent: true, depthWrite: false, opacity: op, toneMapped: false }));
      m.rotation.x = -Math.PI / 2; m.position.set(0, 0.003, z); m.layers.set(3); m.renderOrder = 1;
      parent.add(m);
      return m;
    }
    function maker(group, list) {
      return function (geo, mat, x, y, z, opt) {
        opt = opt || {};
        const m = new T.Mesh(geo, mat);
        m.position.set(x, y, z);
        m.castShadow = opt.cast !== false;
        m.receiveShadow = true;
        group.add(m);
        if (opt.occ !== false) list.push(m);
        return m;
      };
    }
    function sidePanel(add, mat, w, h, r, z, y, s, halfW) {
      const g = new T.ShapeGeometry(rrectShape(w, h, r));
      g.rotateY(s * Math.PI / 2);
      return add(g, mat, s * (halfW + 0.003), y, z, { cast: false });
    }

    /* ---------- Stand-in vehicles: generic shapes laid out like a 70 Series family ---------- */
    const aF = Math.asin(0.30 / 0.53);
    function buildVehicle(bodyId) {
      const g = new T.Group(), occ = [];
      const add = maker(g, occ);
      const wagon = bodyId === 'wagon', single = bodyId === 'single';
      const rearAxle = wagon ? -1.30 : -1.63;
      const cabBack = single ? -0.75 : wagon ? -2.30 : -1.28;
      const ghBack = single ? -0.69 : wagon ? -2.25 : -1.22;

      // Lower body
      const lb = new T.Shape();
      lb.moveTo(2.23, 0.70);
      lb.lineTo(2.23, 1.15);
      lb.quadraticCurveTo(2.23, 1.27, 2.11, 1.29);
      lb.lineTo(0.79, 1.34);
      if (wagon) {
        lb.lineTo(cabBack + 0.06, 1.37);
        lb.quadraticCurveTo(cabBack, 1.37, cabBack, 1.31);
        lb.lineTo(cabBack, 0.76);
        lb.quadraticCurveTo(cabBack, 0.70, cabBack + 0.06, 0.70);
        archPath(lb, [rearAxle], 0.40, 0.53, 0.70);
      } else if (single) {
        lb.lineTo(cabBack, 1.36);
        lb.lineTo(cabBack, 0.70);
      } else {
        const aR1 = Math.atan2(0.398, 0.35);
        lb.lineTo(cabBack, 1.36);
        lb.lineTo(cabBack, 0.40 + 0.53 * Math.sin(aR1));
        lb.absarc(rearAxle, 0.40, 0.53, aR1, aF, true);
      }
      lb.lineTo(1.55 - 0.53 * Math.cos(aF), 0.70);
      lb.absarc(1.55, 0.40, 0.53, Math.PI - aF, aF, true);
      add(profileX(lb, 1.84, 0.05), M.body, 0, 0, 0);

      // Glasshouse
      const gh = new T.Shape();
      gh.moveTo(0.75, 1.36);
      gh.lineTo(0.47, 1.90);
      gh.quadraticCurveTo(0.44, 1.95, 0.35, 1.95);
      gh.lineTo(ghBack + 0.10, 1.95);
      gh.quadraticCurveTo(ghBack, 1.95, ghBack, 1.86);
      gh.lineTo(ghBack, 1.36);
      add(profileX(gh, 1.70, 0.04), M.body, 0, 0, 0);

      // Glass
      const nz = 0.8877, ny = 0.4603;
      const ws = add(new T.ShapeGeometry(rrectShape(1.48, 0.46, 0.05)), M.glass, 0, 1.63 + ny * 0.043, 0.61 + nz * 0.043, { cast: false });
      ws.rotation.x = -Math.asin(ny);
      let wins;
      if (single) wins = [[[0.63, 1.45], [0.415, 1.86], [-0.55, 1.87], [-0.60, 1.82], [-0.60, 1.45]]];
      else if (wagon) wins = [[[0.63, 1.45], [0.415, 1.86], [-0.30, 1.87], [-0.30, 1.45]], [[-0.40, 1.45], [-0.40, 1.87], [-1.20, 1.87], [-1.20, 1.45]], [[-1.30, 1.45], [-1.30, 1.87], [-2.08, 1.87], [-2.13, 1.82], [-2.13, 1.45]]];
      else wins = [[[0.63, 1.45], [0.415, 1.86], [-0.30, 1.87], [-0.30, 1.45]], [[-0.40, 1.45], [-0.40, 1.87], [-1.10, 1.87], [-1.14, 1.82], [-1.14, 1.45]]];
      [1, -1].forEach(function (s) {
        wins.forEach(function (pts) {
          const sh = new T.Shape();
          pts.forEach(function (p, i) { const u = s > 0 ? -p[0] : p[0]; if (i) sh.lineTo(u, p[1]); else sh.moveTo(u, p[1]); });
          const geo = new T.ShapeGeometry(sh);
          geo.rotateY(s * Math.PI / 2);
          add(geo, M.glass, s * 0.853, 0, 0, { cast: false });
        });
      });
      if (wagon) {
        [1, -1].forEach(function (s) {
          const rwin = add(new T.ShapeGeometry(rrectShape(0.62, 0.36, 0.05)), M.glass, s * 0.36, 1.66, ghBack - 0.043, { cast: false });
          rwin.rotation.y = Math.PI;
        });
      } else {
        const rwin = add(new T.ShapeGeometry(rrectShape(1.2, 0.34, 0.05)), M.glass, 0, 1.66, ghBack - 0.043, { cast: false });
        rwin.rotation.y = Math.PI;
      }

      // Front end
      add(rbox(0.92, 0.30, 0.05, 0.02), M.trim, 0, 1.06, 2.28);
      [0.99, 1.06, 1.13].forEach(function (y) { add(rbox(0.84, 0.022, 0.03, 0.008), M.rim, 0, y, 2.31, { cast: false }); });
      [1, -1].forEach(function (s) {
        add(rbox(0.30, 0.16, 0.05, 0.025), M.lamp, s * 0.66, 1.10, 2.28);
        add(rbox(0.16, 0.05, 0.04, 0.015), M.lamp, s * 0.68, 0.97, 2.285, { cast: false });
      });
      // Bullbar: square shoulders, straight bars
      add(rbox(1.96, 0.26, 0.22, 0.05), M.trim, 0, 0.62, 2.45);
      add(rbox(0.50, 0.12, 0.02, 0.01), M.rim, 0, 0.61, 2.568, { cast: false });
      add(barTube([[-0.90, 0.78, 2.40], [-0.89, 1.31, 2.43], [0.89, 1.31, 2.43], [0.90, 0.78, 2.40]], 0.034, 0.06), M.trim, 0, 0, 0);
      [1, -1].forEach(function (s) {
        add(rbox(0.20, 0.30, 0.30, 0.05), M.trim, s * 0.90, 0.66, 2.37);
        add(tube([[s * 0.30, 0.74, 2.45], [s * 0.30, 1.31, 2.43]], 0.032), M.trim, 0, 0, 0);
      });
      add(tube([[-0.30, 1.04, 2.445], [0.30, 1.04, 2.445]], 0.026), M.trim, 0, 0, 0);

      // Flares, mirrors, seams, handles
      [1, -1].forEach(function (s) {
        add(archFlare(1.55, 0.40, 0.47, 0.60, 0.66, 0.10), M.trim, s * 0.95, 0, 0);
        if (wagon) add(archFlare(rearAxle, 0.40, 0.47, 0.60, 0.66, 0.10), M.trim, s * 0.95, 0, 0);
        add(rbox(0.16, 0.035, 0.05, 0.012), M.trim, s * 0.99, 1.43, 0.60);
        add(rbox(0.21, 0.21, 0.09, 0.03), M.trim, s * 1.135, 1.47, 0.585);
        add(rbox(0.18, 0.18, 0.012, 0.02), M.glass, s * 1.135, 1.47, 0.538, { cast: false });
        const seams = single ? [0.72, -0.62] : wagon ? [0.72, -0.35, -1.25] : [0.72, -0.35, -1.20];
        seams.forEach(function (z) { add(rbox(0.006, 0.62, 0.012, 0.002), M.trim, s * 0.921, 1.05, z, { cast: false }); });
        const handles = single ? [-0.45] : wagon ? [0.02, -1.02] : [0.02, -0.98];
        handles.forEach(function (z) { add(rbox(0.014, 0.035, 0.14, 0.006), M.trim, s * 0.925, 1.22, z, { cast: false }); });
      });

      // Snorkel on the driver's side
      add(tube([[-0.93, 1.28, 1.07], [-0.975, 1.50, 0.87], [-0.975, 1.80, 0.65], [-0.965, 1.98, 0.57]], 0.042), M.trim, 0, 0, 0);
      add(rbox(0.11, 0.10, 0.17, 0.03), M.trim, -0.965, 2.02, 0.61);

      // Chassis, axles, wheels
      const railBack = wagon ? -2.30 : -3.0;
      [1, -1].forEach(function (s) { add(rbox(0.08, 0.20, 2.25 - railBack, 0.01), M.dark, s * 0.42, 0.56, (2.25 + railBack) / 2, { occ: false }); });
      [1.55, rearAxle].forEach(function (z) {
        const ax = new T.CylinderGeometry(0.055, 0.055, 1.5, 14); ax.rotateZ(Math.PI / 2);
        add(ax, M.dark, 0, 0.40, z, { occ: false });
        add(new T.SphereGeometry(0.14, 18, 12), M.dark, z > 0 ? -0.08 : 0.08, 0.40, z, { occ: false });
        [1, -1].forEach(function (sx) {
          const w = makeWheel(0.40, 0.27, M);
          w.position.set(sx * 0.80, 0.40, z);
          g.add(w);
          w.traverse(function (o) { if (o.isMesh) occ.push(o); });
        });
      });

      // Suspension, for the underbody cameras: leaf packs and shocks at the back, coils and shocks up front
      const springGeo = function (r, h, turns, wire) {
        const pts = [];
        for (let i = 0; i <= turns * 16; i++) { const t = i / (turns * 16), a = t * turns * Math.PI * 2; pts.push(new T.Vector3(Math.cos(a) * r, t * h - h / 2, Math.sin(a) * r)); }
        return new T.TubeGeometry(new T.CatmullRomCurve3(pts), turns * 16, wire, 6, false);
      };
      [1, -1].forEach(function (s) {
        add(rbox(0.07, 0.05, 1.20, 0.01), M.steel, s * 0.42, 0.47, rearAxle, { occ: false, cast: false });
        add(rbox(0.065, 0.03, 0.85, 0.008), M.steel, s * 0.42, 0.43, rearAxle, { occ: false, cast: false });
        [rearAxle + 0.16, 1.55 - 0.16].forEach(function (z) {
          add(new T.CylinderGeometry(0.028, 0.028, 0.38, 10), M.alloy, s * 0.56, 0.62, z, { occ: false, cast: false }).rotation.z = s * 0.18;
        });
        add(springGeo(0.075, 0.30, 6, 0.012), M.alloy, s * 0.48, 0.63, 1.55, { occ: false, cast: false });
      });
      let towZ, rearBarZ;
      if (wagon) {
        // Rear: barn doors, plate, lights, bar and hitch
        add(rbox(0.012, 0.62, 0.006, 0.002), M.trim, 0, 1.05, cabBack - 0.052, { cast: false });
        add(rbox(0.012, 0.44, 0.006, 0.002), M.trim, 0, 1.63, ghBack - 0.042, { cast: false });
        add(rbox(0.50, 0.14, 0.015, 0.005), M.rim, 0, 0.93, cabBack - 0.055, { cast: false });
        [1, -1].forEach(function (s) { add(rbox(0.10, 0.30, 0.04, 0.015), M.tail, s * 0.84, 1.08, cabBack - 0.05, { cast: false }); });
        rearBarZ = cabBack - 0.13;
        towZ = cabBack - 0.48;
        add(rbox(1.80, 0.14, 0.14, 0.03), M.dark, 0, 0.66, rearBarZ);
        add(rbox(0.08, 0.08, 0.30, 0.01), M.dark, 0, 0.62, cabBack - 0.28);
        add(rbox(0.07, 0.06, 0.18, 0.01), M.dark, 0, 0.62, towZ + 0.06);
        blob(g, 2.9, 5.8, -0.05, 0.5);
      } else {
        const trayFront = single ? -0.88 : -1.36, trayRear = -3.22, trayLen = trayFront - trayRear, trayMid = (trayFront + trayRear) / 2;
        add(rbox(1.98, 0.10, trayLen, 0.015), M.steel, 0, 1.03, trayMid);
        add(rbox(0.95, 0.14, trayLen - 0.36, 0.01), M.dark, 0, 0.91, trayMid + 0.14, { occ: false });
        if (bodyId !== 'canopy') {
          [1, -1].forEach(function (s) { add(rbox(0.04, 0.30, trayLen, 0.01), M.steel, s * 0.97, 1.23, trayMid); });
          add(rbox(1.98, 0.30, 0.04, 0.01), M.steel, 0, 1.23, trayRear);
          const hb = trayFront - 0.04;
          [1, -1].forEach(function (s) { add(rbox(0.05, 0.92, 0.05, 0.01), M.steel, s * 0.965, 1.54, hb); });
          add(rbox(1.98, 0.05, 0.05, 0.01), M.steel, 0, 1.99, hb);
          add(rbox(1.94, 0.30, 0.03, 0.008), M.steel, 0, 1.23, hb);
          add(rbox(1.94, 0.035, 0.035, 0.008), M.steel, 0, 1.38, hb);
          for (let x = -0.8; x <= 0.81; x += 0.16) add(rbox(0.022, 0.60, 0.022, 0.006), M.steel, x, 1.68, hb);
        }
        if (bodyId === 'toolbox') {
          add(rbox(1.94, 0.62, 0.70, 0.02), M.steel, 0, 1.39, -1.76);
          [1, -1].forEach(function (s) {
            add(rbox(0.006, 0.46, 0.60, 0.004), M.trim, s * 0.972, 1.38, -1.76, { cast: false });
            add(rbox(0.012, 0.03, 0.14, 0.006), M.rim, s * 0.978, 1.50, -1.76, { cast: false });
          });
          add(rbox(1.95, 0.012, 0.012, 0.004), M.trim, 0, 1.62, -2.112, { cast: false });
        } else if (bodyId === 'canopy') {
          add(rbox(1.98, 0.88, 1.86, 0.03), M.canopy, 0, 1.52, -2.29);
          [1, -1].forEach(function (s) {
            [-1.84, -2.74].forEach(function (z) {
              add(rbox(0.006, 0.66, 0.82, 0.004), M.canopyDoor, s * 0.992, 1.52, z, { cast: false });
              add(rbox(0.012, 0.03, 0.14, 0.006), M.trim, s * 0.997, 1.36, z, { cast: false });
            });
          });
          add(rbox(1.70, 0.70, 0.006, 0.004), M.canopyDoor, 0, 1.52, -3.226, { cast: false });
          // inside: walls only show in the camera's own view; the gear also shows through when the canopy goes see-through
          const cwall = new T.Mesh(new T.BoxGeometry(1.90, 0.82, 1.78), new T.MeshStandardMaterial({ color: C('#3A3F43'), roughness: 0.85, side: T.BackSide }));
          cwall.position.set(0, 1.50, -2.29); cwall.layers.set(2); g.add(cwall);
          const gear = function (geo, mat, x, y, z) { const m = new T.Mesh(geo, mat); m.position.set(x, y, z); g.add(m); return m; };
          gear(rbox(1.50, 0.28, 1.15, 0.02), M.steel, 0, 1.22, -2.62);
          [0.36, -0.36].forEach(function (x) { gear(rbox(0.62, 0.012, 0.012, 0.004), M.rim, x, 1.27, -3.205); });
          gear(rbox(0.48, 0.42, 0.74, 0.04), M.lamp, 0.42, 1.57, -2.55);
          gear(rbox(0.50, 0.25, 0.36, 0.06), M.tail, -0.38, 1.49, -2.75);
          gear(rbox(1.40, 0.18, 0.34, 0.02), M.cargo, 0, 1.18, -1.62);
          [-0.62, -0.42].forEach(function (x) { gear(rbox(0.17, 0.45, 0.34, 0.02), M.prop, x, 1.50, -1.66); });
          const cw = add(new T.ShapeGeometry(rrectShape(1.1, 0.24, 0.04)), M.canopyGlass, 0, 1.74, -3.232, { cast: false });
          cw.rotation.y = Math.PI;
        } else {
          add(rbox(0.70, 0.35, 0.45, 0.03), M.cargo, 0.45, 1.255, trayFront - 0.49);
          add(rbox(0.17, 0.45, 0.34, 0.03), M.cargo, -0.70, 1.305, trayFront - 0.44);
          add(rbox(0.17, 0.45, 0.34, 0.03), M.cargo, -0.50, 1.305, trayFront - 0.44);
        }
        [1, -1].forEach(function (s) {
          add(rbox(0.36, 0.03, 0.70, 0.01), M.steel, s * 0.80, 0.93, single ? -1.63 : -1.73);
          add(rbox(0.34, 0.40, 0.02, 0.005), M.dark, s * 0.80, 0.68, -2.12);
          add(rbox(0.20, 0.09, 0.04, 0.015), M.tail, s * 0.78, 0.92, -3.25, { cast: false });
        });
        rearBarZ = -3.18;
        towZ = -3.54;
        add(rbox(1.30, 0.10, 0.10, 0.02), M.dark, 0, 0.66, rearBarZ);
        add(rbox(0.08, 0.08, 0.30, 0.01), M.dark, 0, 0.62, -3.34);
        add(rbox(0.07, 0.06, 0.18, 0.01), M.dark, 0, 0.62, -3.48);
        blob(g, 2.9, 6.6, -0.3, 0.5);
      }
      add(new T.CylinderGeometry(0.02, 0.02, 0.05, 10), M.rim, 0, 0.66, towZ);
      add(new T.SphereGeometry(0.045, 16, 12), M.rim, 0, 0.70, towZ);

      // pos: point on the surface, n: surface normal, dir: where the lens points
      const mounts = {
        front: { pos: [0, 0.70, 2.562], n: [0, 0, 1], dir: dirFromPitch(15, false) },
        sideL: { pos: [1.135, 1.365, 0.585], n: [0, -1, 0], dir: norm([0.25, -0.5, -0.83]) },
        sideR: { pos: [-1.135, 1.365, 0.585], n: [0, -1, 0], dir: norm([-0.25, -0.5, -0.83]) },
        underB: wagon ? { pos: [0, 0.65, rearAxle - 0.62], n: [0, -1, 0], dir: dirFromPitch(22, false), under: true } : { pos: [0, 0.84, -2.62], n: [0, -1, 0], dir: dirFromPitch(22, false), under: true },
        underF: { pos: [0, 0.49, 2.40], n: [0, -1, 0], dir: dirFromPitch(14, true), under: true },
        reverse: wagon ? { pos: [0, 1.10, cabBack - 0.051], n: [0, 0, -1], dir: dirFromPitch(28, true) } : { pos: [0, 0.98, -3.205], n: [0, -1, 0], dir: dirFromPitch(32, true) },
        tray: bodyId === 'toolbox' ? { pos: [0, 1.62, -2.112], n: [0, 0, -1], dir: dirFromPitch(38, true) }
          : bodyId === 'canopy' ? { pos: [0, 1.91, -1.52], n: [0, -1, 0], dir: dirFromPitch(34, true), inside: true, aboveY: 2.25 }
          : wagon ? null
            : { pos: [0, 1.985, (single ? -0.88 : -1.36) - 0.04 - 0.026], n: [0, 0, -1], dir: dirFromPitch(32, true) }
      };
      const boxes = {
        whole: [[-1.05, 0, wagon ? -3.0 : -3.75], [1.05, 2.15, 2.6]],
        front: [[-1.0, 0.35, 1.0], [1.0, 1.5, 2.62]],
        sideL: [[0.55, 0.6, -1.05], [1.4, 1.75, 1.05]],
        sideR: [[-1.4, 0.6, -1.05], [-0.55, 1.75, 1.05]],
        underB: wagon ? [[-0.9, 0.05, -2.6], [0.9, 0.95, -0.9]] : [[-0.9, 0.05, -3.25], [0.9, 1.0, -1.2]],
        underF: [[-0.9, 0.05, 1.1], [0.9, 0.85, 2.65]],
        reverse: wagon ? [[-1.0, 0.45, -3.0], [1.0, 1.9, -1.6]] : [[-1.0, 0.45, -3.7], [1.0, 1.45, -2.0]],
        tray: bodyId === 'toolbox' ? [[-1.0, 0.95, -3.3], [1.0, 2.0, -1.4]] : single ? [[-1.0, 0.95, -3.3], [1.0, 2.2, -0.7]] : [[-1.0, 0.95, -3.3], [1.0, 2.2, -1.2]]
      };
      // Simple boxes standing in for the body, used to hide hotspots that are behind the vehicle
      const B = function (a, b) { return new T.Box3(new T.Vector3(a[0], a[1], a[2]), new T.Vector3(b[0], b[1], b[2])); };
      const proxies = [
        B([-0.92, 0.65, 0.75], [0.92, 1.39, 2.28]),
        B([-0.92, 0.65, cabBack - 0.05], [0.92, 1.41, 0.79]),
        B([-0.85, 1.36, ghBack - 0.04], [0.85, 1.99, 0.75]),
        B([-0.98, 0.49, 2.34], [0.98, 0.75, 2.56])
      ];
      [1.55, rearAxle].forEach(function (z) { [1, -1].forEach(function (sx) { proxies.push(B([sx * 0.80 - 0.14, 0, z - 0.40], [sx * 0.80 + 0.14, 0.80, z + 0.40])); }); });
      if (!wagon) {
        proxies.push(B([-0.99, 0.86, -3.24], [0.99, bodyId === 'canopy' ? 1.97 : 1.08, single ? -0.88 : -1.36]));
        if (bodyId === 'toolbox') proxies.push(B([-0.97, 1.08, -2.11], [0.97, 1.70, -1.41]));
      }
      return { group: g, occ: occ, proxies: proxies, mounts: mounts, boxes: boxes, towZ: towZ, rearBarZ: rearBarZ };
    }


    /* ---------- Trailers: coupling at z = 0, body runs towards -z. One builder per style in the trailer chart. ---------- */
    const IM = {
      wall: new T.MeshStandardMaterial({ color: C('#D9DCD8'), roughness: 0.85, side: T.BackSide }),
      floor: new T.MeshStandardMaterial({ color: C('#6E6A62'), roughness: 0.95 }),
      ply: new T.MeshStandardMaterial({ color: C('#B79D78'), roughness: 0.8 }),
      rubber: new T.MeshStandardMaterial({ color: C('#2E3133'), roughness: 0.9 }),
      horse: new T.MeshStandardMaterial({ color: C('#7A5A43'), roughness: 0.75 }),
      horse2: new T.MeshStandardMaterial({ color: C('#4D3A2D'), roughness: 0.75 }),
      hay: new T.MeshStandardMaterial({ color: C('#B8A35A'), roughness: 0.95 }),
      box: new T.MeshStandardMaterial({ color: C('#B48E62'), roughness: 0.85 })
    };
    function sideMount(x, y, z, s) { return { pos: [x, y, z], n: [s, 0, 0], dir: norm([s * 0.12, -0.2, -0.97]), wedge: true }; }
    function rearMount(y, z, pitch) { return { pos: [0, y, z], n: [0, 0, -1], dir: dirFromPitch(pitch, true) }; }
    function buildTrailer(tid) {
      const t = trailerById[tid];
      const g = new T.Group(), occ = [];
      const add = maker(g, occ);
      const L = t.L, W = t.W, kind = t.kind;
      const zf = -t.bar, zr = zf - L, zm = (zf + zr) / 2;
      let H = t.H || 1.05;
      const mounts = {};
      let proxy, spread = 0.45, interior = null;
      function wheelsAt(zs, R, Wt, x) {
        zs.forEach(function (z) {
          [1, -1].forEach(function (s) {
            const w = makeWheel(R, Wt, TM);
            w.position.set(s * x, R, z);
            g.add(w);
            w.traverse(function (o) { if (o.isMesh) occ.push(o); });
          });
        });
      }
      function drawbar(zBody, sp, y0) {
        spread = sp;
        y0 = y0 || 0.50;
        [1, -1].forEach(function (s) { add(tube([[s * sp, y0, zBody], [0, 0.64, -0.12]], 0.045), TM.trim, 0, 0, 0); });
        add(rbox(0.13, 0.11, 0.24, 0.03), TM.trim, 0, 0.72, -0.04);
      }
      function jockey() {
        // jockey wheel on the driver's side, clear of the Suzi coil on the passenger side
        add(new T.CylinderGeometry(0.03, 0.03, 0.55, 10), TM.trim, -0.24, 0.40, -0.47);
        const jw = new T.CylinderGeometry(0.085, 0.085, 0.06, 18); jw.rotateZ(Math.PI / 2);
        add(jw, TM.tyre, -0.24, 0.085, -0.47);
      }
      function lights(y, z, x) {
        [1, -1].forEach(function (s) { add(rbox(0.18, 0.07, 0.03, 0.01), TM.tail, s * x, y, z, { cast: false }); });
      }
      function bumper(y, z, w) { add(rbox(w, 0.10, 0.10, 0.015), TM.trim, 0, y, z); }
      function interiorBox(x0, x1, y0, y1, z0, z1) {
        // walls only show in the camera's own view; the load (horses, boxes) also shows through the see-through body
        interior = new T.Group();
        const box = new T.Mesh(new T.BoxGeometry(x1 - x0, y1 - y0, z1 - z0), IM.wall);
        box.position.set((x0 + x1) / 2, (y0 + y1) / 2, (z0 + z1) / 2);
        box.layers.set(2);
        interior.add(box);
        const fl = new T.Mesh(new T.PlaneGeometry(x1 - x0, z1 - z0), IM.floor);
        fl.rotation.x = -Math.PI / 2; fl.position.set((x0 + x1) / 2, y0 + 0.002, (z0 + z1) / 2);
        interior.add(fl);
        g.add(interior);
        return interior;
      }

      if (kind === 'box' || kind === 'cage') {
        const deck = kind === 'cage' ? 0.60 : 0.55, sideH = kind === 'cage' ? 0.42 : 0.40, top = deck + sideH;
        const axZ = t.axles === 2 ? [zm - 0.42, zm + 0.42] : [zm];
        const wx = W / 2 + 0.16;
        add(rbox(W, 0.06, L, 0.01), TM.galv, 0, deck, zm);
        add(rbox(W - 0.2, 0.08, L, 0.01), TM.dark, 0, deck - 0.08, zm, { occ: false });
        [1, -1].forEach(function (s) {
          add(rbox(0.03, sideH, L, 0.008), TM.galv, s * W / 2, deck + sideH / 2, zm);
          add(rbox(0.05, 0.04, L, 0.01), TM.galv, s * W / 2, top + 0.01, zm);
          add(rbox(0.26, 0.03, t.axles === 2 ? 1.70 : 0.80, 0.01), TM.galv, s * wx, deck + 0.11, zm);
        });
        [zf, zr].forEach(function (z) {
          add(rbox(W, sideH, 0.03, 0.008), TM.galv, 0, deck + sideH / 2, z);
          add(rbox(W, 0.04, 0.05, 0.01), TM.galv, 0, top + 0.01, z);
        });
        lights(deck - 0.06, zr - 0.02, W / 2 - 0.14);
        axZ.forEach(function (z) { const ax = new T.CylinderGeometry(0.04, 0.04, wx * 2, 12); ax.rotateZ(Math.PI / 2); add(ax, TM.dark, 0, 0.30, z, { occ: false }); });
        wheelsAt(axZ, 0.30, 0.18, wx);
        drawbar(zf, 0.45);
        jockey();
        H = top + 0.05;
        if (kind === 'cage') {
          const ct = 1.86;
          H = ct + 0.04;
          [[W / 2, zf], [-W / 2, zf], [W / 2, zr], [-W / 2, zr], [W / 2, zm], [-W / 2, zm]].forEach(function (p) {
            add(rbox(0.04, ct - top, 0.04, 0.008), TM.dark, p[0], (ct + top) / 2, p[1]);
          });
          [1, -1].forEach(function (s) {
            add(rbox(0.04, 0.04, L, 0.008), TM.dark, s * W / 2, ct, zm);
            add(rbox(0.025, 0.025, L, 0.006), TM.dark, s * W / 2, (ct + top) / 2, zm);
            for (let z = zr + 0.2; z < zf - 0.1; z += 0.22) add(rbox(0.012, ct - top, 0.012, 0.003), TM.dark, s * W / 2, (ct + top) / 2, z, { cast: false, occ: false });
          });
          [zf, zr].forEach(function (z) {
            add(rbox(W, 0.04, 0.04, 0.008), TM.dark, 0, ct, z);
            add(rbox(W, 0.025, 0.025, 0.006), TM.dark, 0, (ct + top) / 2, z);
            for (let x = -W / 2 + 0.2; x < W / 2 - 0.1; x += 0.22) add(rbox(0.012, ct - top, 0.012, 0.003), TM.dark, x, (ct + top) / 2, z, { cast: false, occ: false });
          });
          // roof rack bars
          [zf + 0.3, zm, zr + 0.3].forEach(function (z) { add(rbox(W + 0.1, 0.03, 0.05, 0.008), TM.dark, 0, ct + 0.035, z); });
          mounts.high = rearMount(ct - 0.02, zr - 0.021, 30);
          mounts.sideL = sideMount(W / 2 + 0.021, 1.45, zf - 0.05, 1);
          mounts.sideR = sideMount(-W / 2 - 0.021, 1.45, zf - 0.05, -1);
        } else {
          mounts.high = rearMount(top - 0.09, zr - 0.016, 26);
          mounts.sideL = sideMount(W / 2 + 0.016, top - 0.12, zf - 0.12, 1);
          mounts.sideR = sideMount(-W / 2 - 0.016, top - 0.12, zf - 0.12, -1);
        }
        mounts.low = rearMount(deck - 0.08, zr - 0.001, 40);
        proxy = new T.Box3(new T.Vector3(-W / 2, deck - 0.05, zr - 0.02), new T.Vector3(W / 2, kind === 'cage' ? H : top + 0.02, zf + 0.02));
        blob(g, W + 0.8, L + 1.2, zm - 0.1, 0.42);
      } else if (kind === 'boat') {
        const R = 0.33, wx = W / 2 - 0.12, axZ = zm - 0.55;
        [1, -1].forEach(function (s) {
          add(rbox(0.07, 0.10, L, 0.01), TM.galv, s * 0.45, 0.46, zm);
          add(rbox(0.30, 0.03, 0.85, 0.01), TM.galv, s * wx, R + 0.36, axZ);
          add(rbox(0.06, 0.06, L * 0.62, 0.01), TM.dark, s * 0.32, 0.56, zm - 0.3, { occ: false });
          add(rbox(0.05, 0.75, 0.05, 0.01), TM.galv, s * (W / 2 - 0.05), 0.85, zr + 0.06);
        });
        [zf + 0.25, zm, zr + 0.3].forEach(function (z) { add(rbox(1.0, 0.06, 0.06, 0.01), TM.galv, 0, 0.46, z, { occ: false }); });
        add(rbox(W, 0.08, 0.06, 0.01), TM.galv, 0, 0.50, zr - 0.02);
        lights(0.50, zr - 0.06, W / 2 - 0.16);
        const ax = new T.CylinderGeometry(0.04, 0.04, wx * 2, 12); ax.rotateZ(Math.PI / 2);
        add(ax, TM.dark, 0, R, axZ, { occ: false });
        wheelsAt([axZ], R, 0.18, wx);
        // winch post
        add(rbox(0.07, 0.62, 0.07, 0.01), TM.galv, 0, 0.78, zf + 0.12);
        add(rbox(0.16, 0.12, 0.14, 0.02), TM.dark, 0, 1.02, zf + 0.12);
        // aluminium boat: plan-view shape extruded, sitting on the bunks
        const len = L - 0.35, bw = 1.85, bs = new T.Shape();
        bs.moveTo(-bw / 2, 0); bs.lineTo(-bw / 2, len * 0.66);
        bs.quadraticCurveTo(-bw / 2, len * 0.92, 0, len);
        bs.quadraticCurveTo(bw / 2, len * 0.92, bw / 2, len * 0.66);
        bs.lineTo(bw / 2, 0); bs.lineTo(-bw / 2, 0);
        const hull = new T.ExtrudeGeometry(bs, { depth: 0.56, bevelEnabled: true, bevelThickness: 0.05, bevelSize: 0.05, bevelSegments: 2, curveSegments: 14 });
        hull.rotateX(-Math.PI / 2);
        add(hull, [TM.dark, TM.galv], 0, 0.64, zr + 0.22, { occ: true }).scale.set(1, 1, -1);
        // open top: gunwale rim around the outline, seats across
        const rim = new T.CatmullRomCurve3(bs.getSpacedPoints(90).slice(0, -1).map(function (p) { return new T.Vector3(p.x * 1.02, 1.255, zr + 0.22 + p.y * 1.01); }), true);
        add(new T.TubeGeometry(rim, 120, 0.03, 6, true), TM.trim, 0, 0, 0, { cast: false });
        [0.25, 0.55].forEach(function (f) { add(rbox(bw - 0.1, 0.04, 0.26, 0.01), TM.galv, 0, 1.30, zr + 0.22 + len * f, { cast: false }); });
        add(rbox(0.5, 0.42, 0.45, 0.04), TM.body, 0.35, 1.45, zr + 0.22 + len * 0.42);
        const ws = add(new T.ShapeGeometry(rrectShape(0.48, 0.22, 0.04)), TM.glass, 0.35, 1.76, zr + 0.22 + len * 0.42 + 0.20, { cast: false });
        ws.rotation.x = -0.35;
        add(rbox(0.32, 0.55, 0.30, 0.05), TM.dark, 0, 1.18, zr + 0.05);
        add(rbox(0.10, 0.55, 0.10, 0.02), TM.dark, 0, 0.82, zr + 0.02);
        H = 1.72;
        drawbar(zf + 0.05, 0.40);
        jockey();
        mounts.low = rearMount(0.50, zr - 0.051, 34);
        mounts.sideL = sideMount(wx + 0.151, R + 0.30, axZ + 0.42, 1);
        mounts.sideR = sideMount(-wx - 0.151, R + 0.30, axZ + 0.42, -1);
        proxy = new T.Box3(new T.Vector3(-bw / 2, 0.62, zr + 0.15), new T.Vector3(bw / 2, 1.25, zf - 0.1));
        blob(g, W + 0.8, L + 0.8, zm, 0.42);
      } else {
        // Closed bodies: enclosed trailer, horse float, camper, caravans
        const van = kind === 'van', off = !!t.offroad;
        const bot = kind === 'camper' ? 0.62 : off ? 0.80 : kind === 'van' ? 0.62 : 0.55;
        const R = kind === 'camper' || off ? 0.40 : kind === 'float' ? 0.34 : 0.36;
        const axC = kind === 'float' ? zf - L * 0.55 : zr + L * 0.45;
        const axles = t.axles === 2 ? [axC - 0.45, axC + 0.45] : [axC];
        const bev = van ? 0.06 : 0.05;
        const s = new T.Shape();
        if (kind === 'camper') {
          s.moveTo(zf, bot); s.lineTo(zf, H - 0.06); s.quadraticCurveTo(zf, H, zf - 0.06, H);
          s.lineTo(zr + 0.06, H); s.quadraticCurveTo(zr, H, zr, H - 0.06); s.lineTo(zr, bot);
        } else if (kind === 'float') {
          s.moveTo(zf, bot); s.lineTo(zf, 1.55); s.quadraticCurveTo(zf, 2.10, zf - 0.55, H);
          s.lineTo(zr + 0.12, H); s.quadraticCurveTo(zr, H, zr, H - 0.12); s.lineTo(zr, bot);
        } else if (kind === 'enclosed') {
          s.moveTo(zf, bot); s.lineTo(zf, H - 0.30); s.quadraticCurveTo(zf, H, zf - 0.30, H);
          s.lineTo(zr + 0.06, H); s.quadraticCurveTo(zr, H, zr, H - 0.06); s.lineTo(zr, bot);
        } else {
          s.moveTo(zf, bot); s.lineTo(zf, H - 0.55); s.quadraticCurveTo(zf, H, zf - 0.55, H);
          s.lineTo(zr + 0.20, H); s.quadraticCurveTo(zr, H, zr, H - 0.20); s.lineTo(zr, bot);
        }
        if (kind !== 'camper') archPath(s, axles, R, R + 0.10, bot);
        add(profileX(s, W, bev), kind === 'camper' ? TM.dark : TM.body, 0, 0, 0);
        const rz = zr - bev;
        add(rbox(Math.min(1.6, W - 0.3), 0.10, L, 0.01), TM.trim, 0, bot - 0.08, zm, { occ: false });
        if (kind === 'camper') {
          wheelsAt(axles, R, 0.26, W / 2 + 0.04);
          [1, -1].forEach(function (sx) { add(rbox(0.22, 0.04, 0.90, 0.01), TM.dark, sx * (W / 2 + 0.05), R + 0.42, axC); });
          // galvanised lid: the bevel puts the body's top at H + bev, so the lid sits on top of that (it used to share that face
          // with the body, and the two flickered against each other as the view moved)
          add(rbox(W + 0.04, 0.06, L + 2 * bev + 0.04, 0.02), TM.galv, 0, H + bev + 0.02, zm);
          add(rbox(1.40, 0.48, 0.50, 0.03), TM.dark, 0, 0.86, zf + 0.42);
          [0.35, -0.35].forEach(function (x) { add(rbox(0.16, 0.42, 0.10, 0.02), TM.trim, x, 0.80, zf + 0.12); });
          bumper(0.45, zr - 0.10, W - 0.2);
          const sp = makeWheel(0.34, 0.22, TM); sp.rotation.y = Math.PI / 2; sp.position.set(-0.50, 0.92, zr - 0.20); g.add(sp);
          lights(0.62, zr - 0.02, W / 2 - 0.14);
          drawbar(zf, 0.42);
          mounts.high = rearMount(H - 0.08, rz - 0.001, 30);
          mounts.low = rearMount(0.45, zr - 0.151, 40);
          mounts.sideL = sideMount(W / 2 + 0.001, 1.08, zf - 0.22, 1);
          mounts.sideR = sideMount(-W / 2 - 0.001, 1.08, zf - 0.22, -1);
        } else {
          wheelsAt(axles, R, off ? 0.27 : 0.22, W / 2 - 0.12);
          [1, -1].forEach(function (sx) {
            add(rbox(0.006, 0.05, L - 0.3, 0.002), TM.trim, sx * (W / 2 + 0.003), van ? 1.30 : 1.20, zm, { cast: false });
            add(rbox(0.22, 0.08, 0.02, 0.008), TM.tail, sx * (W / 2 - 0.25), bot + 0.22, rz - 0.01, { cast: false });
          });
          bumper(bot - 0.12, zr - 0.06, W - 0.25);
          if (van) {
            [1, -1].forEach(function (sx) {
              sidePanel(add, TM.glass, 0.80, 0.42, 0.06, zf - 0.85, H - 0.85, sx, W / 2);
              sidePanel(add, TM.glass, 1.05, 0.42, 0.06, zr + 1.20, H - 0.85, sx, W / 2);
            });
            sidePanel(add, TM.door, 0.62, 1.70, 0.06, zm + 0.4, bot + 0.97, 1, W / 2 + 0.001);
            sidePanel(add, TM.glass, 0.32, 0.36, 0.05, zm + 0.4, bot + 1.45, 1, W / 2 + 0.003);
            add(new T.ShapeGeometry(rrectShape(1.30, 0.32, 0.06)), TM.glass, 0, H - 0.75, zf + 0.064, { cast: false });
            const bw = add(new T.ShapeGeometry(rrectShape(0.95, 0.30, 0.05)), TM.glass, 0, H - 0.85, rz - 0.003, { cast: false });
            bw.rotation.y = Math.PI;
            add(rbox(Math.min(1.7, W - 0.4), 0.48, 0.42, 0.03), off ? TM.dark : TM.body, 0, 0.86, zf + 0.42);
            if (!off && t.axles === 1) {
              [0.16, -0.16].forEach(function (x) { add(new T.CylinderGeometry(0.13, 0.13, 0.5, 18), TM.galv, x, 0.95, zf + 0.85); });
              add(rbox(0.8, 0.30, 0.02, 0.01), TM.dark, 0, 0.75, zf + 0.62);
            }
            if (off) {
              [0.55, -0.55].forEach(function (x) { add(rbox(0.18, 0.45, 0.12, 0.02), TM.trim, x, 0.95, zf + 0.75); });
              const sp = makeWheel(0.38, 0.26, TM); sp.rotation.y = Math.PI / 2; sp.position.set(-0.55, 1.45, rz - 0.15); g.add(sp);
            }
            drawbar(zf, 0.55, bot - 0.12);
            mounts.high = rearMount(H - 0.24, rz - 0.001, 28);
            mounts.low = rearMount(bot - 0.12, zr - 0.111, 40);
            mounts.sideL = sideMount(W / 2 + 0.001, 1.55, zf - 0.40, 1);
            mounts.sideR = sideMount(-W / 2 - 0.001, 1.55, zf - 0.40, -1);
          } else {
            // enclosed trailer and horse float: rear doors, interior for the inside camera
            const dw = W - 0.30;
            add(rbox(0.012, H - bot - 0.25, 0.006, 0.002), TM.trim, 0, (H + bot) / 2, rz - 0.003, { cast: false });
            add(rbox(dw, 0.014, 0.006, 0.002), TM.trim, 0, H - 0.14, rz - 0.003, { cast: false });
            add(rbox(dw, 0.014, 0.006, 0.002), TM.trim, 0, bot + 0.08, rz - 0.003, { cast: false });
            [1, -1].forEach(function (sx) { add(rbox(0.014, H - bot - 0.22, 0.006, 0.002), TM.trim, sx * dw / 2, (H + bot) / 2, rz - 0.003, { cast: false }); });
            if (kind === 'float') {
              [1, -1].forEach(function (sx) {
                sidePanel(add, TM.glass, 0.55, 0.30, 0.05, zf - 1.05, 1.95, sx, W / 2);
                sidePanel(add, TM.glass, 0.55, 0.30, 0.05, zf - 1.75, 1.95, sx, W / 2);
              });
            } else sidePanel(add, TM.door, 0.70, 1.30, 0.04, zf - 0.55, bot + 0.75, 1, W / 2 + 0.001);
            drawbar(zf - 0.05, 0.55);
            const ib = interiorBox(-W / 2 + 0.05, W / 2 - 0.05, bot + 0.04, H - 0.07, zr + 0.05, zf - (kind === 'float' ? 0.35 : 0.06));
            if (kind === 'float') {
              const div = new T.Mesh(rbox(0.04, 1.05, L - 0.9, 0.01), IM.ply); div.position.set(0, bot + 0.70, zm - 0.05); ib.add(div);
              const breast = new T.Mesh(rbox(W - 0.1, 0.06, 0.06, 0.02), IM.rubber); breast.position.set(0, bot + 1.10, zf - 0.75); ib.add(breast);
              [0.43, -0.43].forEach(function (x, i) {
                const hm = i ? IM.horse2 : IM.horse, hg = new T.Group();
                const body = new T.Mesh(new T.SphereGeometry(1, 20, 14), hm); body.scale.set(0.26, 0.34, 0.82); body.position.set(0, 1.28, 0); hg.add(body);
                const neck = new T.Mesh(new T.CylinderGeometry(0.11, 0.17, 0.62, 12), hm); neck.position.set(0, 1.62, 0.72); neck.rotation.x = 0.62; hg.add(neck);
                const head = new T.Mesh(new T.SphereGeometry(1, 16, 12), hm); head.scale.set(0.10, 0.12, 0.27); head.position.set(0, 1.86, 1.02); head.rotation.x = 0.75; hg.add(head);
                [[0.12, 0.5], [-0.12, 0.5], [0.12, -0.55], [-0.12, -0.55]].forEach(function (l) {
                  const leg = new T.Mesh(new T.CylinderGeometry(0.045, 0.04, 0.95, 8), hm); leg.position.set(l[0], 0.55 + bot - 0.05, l[1]); hg.add(leg);
                });
                const net = new T.Mesh(new T.SphereGeometry(0.17, 12, 10), IM.hay); net.position.set(0.12, 1.58, 1.38); hg.add(net);
                hg.position.set(x, 0, zf - 1.95); ib.add(hg);
              });
              mounts.inside = { pos: [0, H - 0.07, zf - 0.75], n: [0, -1, 0], dir: dirFromPitch(34, true), inside: true };
            } else {
              [[0.35, 0.45, zr + 0.5, 0.6, 0.5, 0.5], [-0.4, 0.35, zr + 0.45, 0.5, 0.42, 0.4], [-0.38, 0.3, zr + 0.95, 0.45, 0.32, 0.4], [0.3, 0.25, zf - 0.6, 0.6, 0.6, 0.5]].forEach(function (b) {
                const m = new T.Mesh(rbox(b[3], b[4], b[5], 0.02), IM.box); m.position.set(b[0], bot + 0.04 + b[4] / 2, b[2]); ib.add(m);
              });
              mounts.inside = rearMount(H - 0.16, zf - 0.061, 30);
              mounts.inside.inside = true;
            }
            mounts.high = rearMount(H - (kind === 'float' ? 0.24 : 0.17), rz - 0.001, 28);
            mounts.low = rearMount(bot - 0.12, zr - 0.111, 40);
            mounts.sideL = sideMount(W / 2 + 0.001, kind === 'float' ? 1.45 : 1.40, zf - 0.32, 1);
            mounts.sideR = sideMount(-W / 2 - 0.001, kind === 'float' ? 1.45 : 1.40, zf - 0.32, -1);
          }
        }
        jockey();
        proxy = new T.Box3(new T.Vector3(-W / 2, bot - 0.05, zr - 0.06), new T.Vector3(W / 2, H + 0.06, zf + 0.06));
        blob(g, W + 0.8, L + 0.6, zm, 0.45);
      }
      // Drawbar socket for the coil, on the passenger-side A-frame tube
      const u = Math.max(0, Math.min(0.95, (t.socket - 0.12) / (Math.abs(zf) - 0.12)));
      const socket = [spread * u + 0.07, 0.64 - 0.14 * u + 0.02, -t.socket];
      add(rbox(0.07, 0.06, 0.08, 0.012), TM.dark, socket[0], socket[1], socket[2], { cast: false });
      const hw = W / 2 + (kind === 'box' || kind === 'cage' || kind === 'boat' ? 0.35 : 0.1);
      const sideBox = function (sx) {
        const x0 = sx > 0 ? W / 2 - 0.55 : -W / 2 - 0.75, x1 = sx > 0 ? W / 2 + 0.75 : -W / 2 + 0.55;
        return [[x0, 0.3, zf - 2.4], [x1, Math.min(H, 2.1), zf + 0.45]];
      };
      return {
        group: g, occ: occ, proxies: [proxy], mounts: mounts, socket: socket, H: H, kind: kind, zf: zf, zr: zr, W: W, interior: interior,
        focusBox: [[-hw, 0.3, zr - 0.25], [hw, H + 0.08, zr + Math.min(2.4, L)]],
        insideBox: [[-W / 2 - 0.05, 0.4, zr - 0.05], [W / 2 + 0.05, H + 0.05, zf + 0.05]],
        sideBoxes: { L: sideBox(1), R: sideBox(-1) },
        wholeBox: [[-hw, 0, zr - 0.3], [hw, H, 0]]
      };
    }

    /* ---------- Car park for the camera views (inset only) ---------- */
    const groundTex = (function () {
      const S = 1024, c = document.createElement('canvas'); c.width = c.height = S;
      const g = c.getContext('2d');
      g.fillStyle = '#7F8689'; g.fillRect(0, 0, S, S);
      for (let i = 0; i < 14000; i++) {
        const v = 105 + Math.floor(Math.random() * 60);
        g.fillStyle = 'rgba(' + v + ',' + (v + 4) + ',' + (v + 4) + ',0.22)';
        g.fillRect(Math.random() * S, Math.random() * S, 2, 2);
      }
      const px = function (X) { return (X + 24) / 48 * S; };
      const pz = function (Z) { return (24 - 6 + Z) / 48 * S; };
      g.strokeStyle = 'rgba(246,247,247,0.95)'; g.lineWidth = 3;
      [-4.95, -1.65, 1.65, 4.95].forEach(function (X) { g.beginPath(); g.moveTo(px(X), pz(3.4)); g.lineTo(px(X), pz(-3.6)); g.stroke(); });
      g.beginPath(); g.moveTo(px(-4.95), pz(3.4)); g.lineTo(px(4.95), pz(3.4)); g.stroke();
      g.setLineDash([20, 16]);
      g.beginPath(); g.moveTo(px(-24), pz(-15)); g.lineTo(px(24), pz(-15)); g.stroke();
      const tx = new T.CanvasTexture(c);
      tx.encoding = T.sRGBEncoding;
      tx.anisotropy = 8;
      return tx;
    })();
    const ground = new T.Mesh(new T.PlaneGeometry(48, 48), new T.MeshStandardMaterial({ map: groundTex, roughness: 0.95, envMapIntensity: 0.3 }));
    ground.rotation.x = -Math.PI / 2; ground.position.set(0, 0, -6); ground.receiveShadow = true; ground.layers.set(2);
    scene.add(ground);
    const farGround = new T.Mesh(new T.PlaneGeometry(400, 400), new T.MeshStandardMaterial({ color: C('#A29C7F'), roughness: 1, envMapIntensity: 0.2 }));
    farGround.rotation.x = -Math.PI / 2; farGround.position.y = -0.06; farGround.receiveShadow = true; farGround.layers.set(2);
    scene.add(farGround);
    const SKY = '#C3CFD4', NIGHT = '#06090B';
    const sky = (function () {
      const geo = new T.SphereGeometry(140, 32, 16);
      const pos = geo.attributes.position, cols = [], hor = C(SKY), zen = C('#EAF0F2'), tmp = new T.Color();
      for (let i = 0; i < pos.count; i++) { const y = Math.max(0, pos.getY(i) / 140); tmp.copy(hor).lerp(zen, Math.pow(y, 0.7)); cols.push(tmp.r, tmp.g, tmp.b); }
      geo.setAttribute('color', new T.Float32BufferAttribute(cols, 3));
      const m = new T.Mesh(geo, new T.MeshBasicMaterial({ vertexColors: true, side: T.BackSide, fog: false }));
      m.layers.set(2);
      return m;
    })();
    scene.add(sky);
    const insetFog = new T.Fog(C(SKY), 30, 240);
    const floor = new T.Mesh(new T.PlaneGeometry(90, 90), new T.ShadowMaterial({ opacity: 0.16 }));
    floor.rotation.x = -Math.PI / 2; floor.receiveShadow = true; floor.layers.set(3);
    scene.add(floor);


    /* ---------- Scenery for the camera views (inset only, layer 2) ----------
       Kept simple so the picture reads at a glance: one main thing and one smaller thing each way, then the horizon.
       In front: a gum tree and a rock. Beside: a parked car. Behind (moves back with the trailer): a person and a wheelie bin.
       Horizon: low scrub and soft hills. */
    const W2 = {
      trunk: new T.MeshStandardMaterial({ color: C('#BDB2A0'), roughness: 0.9 }),
      leaf: new T.MeshStandardMaterial({ color: C('#62724F'), roughness: 0.9, flatShading: true }),
      rock: new T.MeshStandardMaterial({ color: C('#8C8577'), roughness: 0.95, flatShading: true }),
      binBody: new T.MeshStandardMaterial({ color: C('#2F5A3A'), roughness: 0.6 }),
      binLid: new T.MeshStandardMaterial({ color: C('#E2B72C'), roughness: 0.5 }),
      shirt: new T.MeshStandardMaterial({ color: C('#E0723A'), roughness: 0.8 }),
      jeans: new T.MeshStandardMaterial({ color: C('#3C4B5E'), roughness: 0.85 }),
      skin: new T.MeshStandardMaterial({ color: C('#B98A6A'), roughness: 0.7 }),
      hat: new T.MeshStandardMaterial({ color: C('#4A3B2C'), roughness: 0.85 }),
      carBlue: new T.MeshStandardMaterial({ color: C('#46698C'), roughness: 0.35, metalness: 0.3 }),
      treeLine: new T.MeshLambertMaterial({ color: C('#7C8A70'), side: T.DoubleSide }),
      hills: new T.MeshLambertMaterial({ color: C('#98A6A3'), side: T.DoubleSide })
    };
    function insetOnly(o, cast) {
      o.traverse(function (m) { if (m.isMesh) { m.layers.set(2); m.castShadow = cast !== false; m.receiveShadow = true; } });
      return o;
    }
    function part(g, geo, mat, x, y, z) { const m = new T.Mesh(geo, mat); m.position.set(x, y, z); g.add(m); return m; }
    function put(o, parent, x, z, ry, cast) { o.position.x = x; o.position.z = z; o.rotation.y = ry || 0; insetOnly(o, cast); (parent || scene).add(o); return o; }
    const behind = new T.Group();
    scene.add(behind);

    /* Gum trees, instanced: pale trunk and a full, grey-green canopy of five blobs each */
    const CROWN = [[0, 1.0, 0, 1.0], [0.9, 0.82, 0.35, 0.8], [-0.85, 0.86, -0.4, 0.84], [0.3, 0.7, -0.75, 0.7], [-0.45, 0.72, 0.8, 0.66]];
    function gumTrees(spots, parent) {
      const trunkGeo = new T.CylinderGeometry(0.15, 0.3, 1, 8); trunkGeo.translate(0, 0.5, 0);
      const trunks = new T.InstancedMesh(trunkGeo, W2.trunk, spots.length);
      const blobs = new T.InstancedMesh(new T.IcosahedronGeometry(1, 0), W2.leaf, spots.length * CROWN.length);
      const m = new T.Matrix4(), q = new T.Quaternion(), e = new T.Euler(), p = new T.Vector3(), sc = new T.Vector3();
      spots.forEach(function (s, i) {
        const H = s[2];
        m.compose(p.set(s[0], 0, s[1]), q.setFromEuler(e.set(0, i * 1.7, 0.06 * ((i % 3) - 1))), sc.set(1, H, 1));
        trunks.setMatrixAt(i, m);
        CROWN.forEach(function (b, k) {
          const r = H * 0.27 * b[3];
          m.compose(p.set(s[0] + b[0] * H * 0.17, H * b[1], s[1] + b[2] * H * 0.17), q.setFromEuler(e.set(0.3 * k, i + k * 2.1, 0)), sc.set(r * 1.3, r * 0.72, r * 1.2));
          blobs.setMatrixAt(i * CROWN.length + k, m);
        });
      });
      [trunks, blobs].forEach(function (o) {
        o.instanceMatrix.needsUpdate = true; o.frustumCulled = false;
        // no shadow: out ahead it sits past the edge of the sun's shadow map, which left stray grey dashes on the ground
        o.layers.set(2); o.castShadow = false; o.receiveShadow = true;
        (parent || scene).add(o);
      });
    }
    /* A ring round the whole scene with a bumpy top edge: the tree line, and hills further out */
    function skyline(radius, base, amps, freqs, seed, mat, segs) {
      const pos = [];
      const h = function (a) { let v = base; freqs.forEach(function (f, i) { v += amps[i] * Math.sin(a * f + seed * (i + 1)); }); return v; };
      for (let i = 0; i < segs; i++) {
        const a0 = i / segs * Math.PI * 2, a1 = (i + 1) / segs * Math.PI * 2;
        const x0 = Math.cos(a0) * radius, z0 = Math.sin(a0) * radius, x1 = Math.cos(a1) * radius, z1 = Math.sin(a1) * radius;
        const h0 = h(a0), h1 = h(a1);
        pos.push(x0, -1, z0, x1, -1, z1, x1, h1, z1, x0, -1, z0, x1, h1, z1, x0, h0, z0);
      }
      const geo = new T.BufferGeometry();
      geo.setAttribute('position', new T.Float32BufferAttribute(pos, 3));
      geo.computeVertexNormals();
      const ring = new T.Mesh(geo, mat);
      ring.position.set(0, 0, -4); ring.layers.set(2); ring.frustumCulled = false;
      scene.add(ring);
    }
    skyline(74, 2.6, [0.55, 0.4, 0.3, 0.2], [9, 17, 31, 47], 1.3, W2.treeLine, 240);
    skyline(128, 12, [6, 4, 2.5], [2, 3, 7], 0.4, W2.hills, 180);

    function rockGroup(r, sx, sy, sz, ry) {
      const g = new T.Group();
      const m = part(g, new T.IcosahedronGeometry(r, 0), W2.rock, 0, r * sy * 0.5, 0);
      m.scale.set(sx, sy, sz); m.rotation.y = ry || 0;
      return g;
    }
    function person() {
      const g = new T.Group();
      [0.1, -0.1].forEach(function (x) { part(g, new T.CylinderGeometry(0.075, 0.065, 0.86, 10), W2.jeans, x, 0.43, 0); });
      part(g, new T.CylinderGeometry(0.2, 0.17, 0.62, 12), W2.shirt, 0, 1.17, 0);
      [0.26, -0.26].forEach(function (x) { part(g, new T.CylinderGeometry(0.055, 0.05, 0.6, 8), W2.shirt, x, 1.14, 0).rotation.z = x > 0 ? 0.12 : -0.12; });
      part(g, new T.SphereGeometry(0.115, 14, 10), W2.skin, 0, 1.62, 0);
      part(g, new T.CylinderGeometry(0.23, 0.23, 0.02, 18), W2.hat, 0, 1.70, 0);
      part(g, new T.CylinderGeometry(0.11, 0.12, 0.1, 14), W2.hat, 0, 1.75, 0);
      return g;
    }
    function wheelieBin() {
      const g = new T.Group();
      part(g, rbox(0.58, 0.92, 0.70, 0.05), W2.binBody, 0, 0.5, 0);
      part(g, rbox(0.62, 0.06, 0.75, 0.02), W2.binLid, 0, 0.99, 0);
      [0.22, -0.22].forEach(function (x) { const wg = new T.CylinderGeometry(0.1, 0.1, 0.06, 12); wg.rotateZ(Math.PI / 2); part(g, wg, M.tyre, x, 0.1, -0.33); });
      return g;
    }
    function hatchback(mat) {
      const g = new T.Group();
      part(g, rbox(1.76, 0.62, 4.05, 0.12), mat, 0, 0.6, 0);
      part(g, rbox(1.56, 0.5, 2.3, 0.14), M.glass, 0, 1.13, -0.25);
      part(g, rbox(1.5, 0.06, 2.0, 0.03), mat, 0, 1.39, -0.3);
      [[0.8, 1.3], [-0.8, 1.3], [0.8, -1.28], [-0.8, -1.28]].forEach(function (w) {
        const wg = new T.CylinderGeometry(0.31, 0.31, 0.22, 16); wg.rotateZ(Math.PI / 2);
        part(g, wg, M.tyre, w[0], 0.31, w[1]);
      });
      part(g, rbox(1.4, 0.09, 0.04, 0.02), M.lamp, 0, 0.74, 2.03);
      part(g, rbox(1.4, 0.09, 0.04, 0.02), M.tail, 0, 0.8, -2.03);
      return g;
    }

    // In front: one gum tree out ahead and a rock closer in
    gumTrees([[-4.2, 15, 7.6]]);
    put(rockGroup(0.42, 1.3, 0.8, 1.1, 0.4), null, 1.3, 4.6);
    // Beside: a car parked in the next bay on the passenger side
    put(hatchback(W2.carBlue), null, 3.3, -0.3);
    // Underneath: a couple of small rocks for the underbody cameras
    [[0.32, 0.65, 0.09], [-0.2, 2.0, 0.08]].forEach(function (r) {
      put(rockGroup(r[2], 1.3, 0.7, 1.1, r[0] * 5), null, r[0], r[1], 0, false);
    });
    // Behind (this group sits just behind the vehicle, or behind the trailer when there is one): a person and a wheelie bin
    put(person(), behind, 0.8, -3.0);
    put(wheelieBin(), behind, -1.4, -2.3, 0.2);

    /* ---------- Camera models ----------
       Daniel's Shapr3D exports, simplified: each has a fixed bracket and a head that tilts about its own pivot.
       META matches the extras baked into each GLB, so the stand-ins line up before the files arrive. */
    const META = {
      pro: { pivot: [0, -0.00115, -0.0104], lenses: [[0, 0.009, 0.0164]], b: [0, 0, -1], attach: [0.00088, -0.01282, -0.01385] },
      hd: { pivot: [0, -0.00845, 0.00065], lenses: [[0.0015, -0.0084, 0.0177]], b: [0, 1, 0], attach: [0.0033, 0.03128, -0.0088] },
      twin: { pivot: [0, 0.0036, 0.00025], lenses: [[-0.024, 0.0036, 0.0166], [0.024, 0.0036, 0.0166]], b: [0, -1, 0], attach: [-0.00114, -0.04238, 0.00003] },
      soon: { pivot: [0, 0, -0.006], lenses: [[0, 0, 0.026]], b: [0, 0, -1], attach: [0, 0, -0.022] }
    };
    // on the website the theme passes its own links for the model files
    const MODEL_FILES = (window.AC_CB && window.AC_CB.models) || { pro: 'models/pro-cam.json', hd: 'models/hd-cam.json', twin: 'models/twin-cam.json' };
    const models = {};
    const CM = {
      body: new T.MeshStandardMaterial({ color: C('#1A1D1F'), roughness: 0.4, metalness: 0.25, envMapIntensity: 1.0 }),
      metal: new T.MeshStandardMaterial({ color: C('#C6CBCF'), roughness: 0.28, metalness: 0.9, envMapIntensity: 1.0 }),
      logo: new T.MeshStandardMaterial({ color: C('#E9EBEC'), roughness: 0.5 }),
      lens: new T.MeshStandardMaterial({ color: C('#06080A'), roughness: 0.05, metalness: 0.6, envMapIntensity: 1.6 }),
      lensring: new T.MeshStandardMaterial({ color: C('#2B2F32'), roughness: 0.25, metalness: 0.5, envMapIntensity: 1.1 }),
      led: new T.MeshStandardMaterial({ color: C('#8E9295'), roughness: 0.18, metalness: 0.1, envMapIntensity: 1.2 }),
      panel: new T.MeshStandardMaterial({ color: C('#25292C'), roughness: 0.55, metalness: 0.2 }),
      green: new T.MeshStandardMaterial({ color: C('#1E8A4A'), roughness: 0.3, emissive: C('#1E8A4A'), emissiveIntensity: 0.35 }),
      mount: new T.MeshStandardMaterial({ color: C('#202427'), roughness: 0.55, metalness: 0.1 }),
      soon: new T.MeshStandardMaterial({ color: C('#2B3033'), roughness: 0.35, metalness: 0.2 }),
      // the white Twin camera
      bodyWhite: new T.MeshStandardMaterial({ color: C('#E9EAE7'), roughness: 0.42, metalness: 0.05, envMapIntensity: 0.8 }),
      panelWhite: new T.MeshStandardMaterial({ color: C('#D6D8D5'), roughness: 0.5, metalness: 0.05 })
    };
    function paintTwin(obj) {
      if (state.twinColour !== 'WHITE') return obj;
      obj.traverse(function (o) { if (o.isMesh) { if (o.material === CM.body) o.material = CM.bodyWhite; else if (o.material === CM.panel) o.material = CM.panelWhite; } });
      return obj;
    }
    /* Simple stand-ins in the same frame as the real models (metres, lens along +Z) */
    function standIn(type) {
      const root = new T.Group(), head = new T.Group(), br = new T.Group();
      const info = META[type], pv = v3(info.pivot);
      head.position.copy(pv);
      root.add(br, head);
      function hb(geo, mat, x, y, z) { const m = new T.Mesh(geo, mat); m.position.set(x - pv.x, y - pv.y, z - pv.z); head.add(m); return m; }
      function bb(geo, mat, x, y, z) { const m = new T.Mesh(geo, mat); m.position.set(x, y, z); br.add(m); return m; }
      function lens(x, y, z, r) { const g = new T.CylinderGeometry(r, r, 0.004, 20); g.rotateX(Math.PI / 2); hb(g, CM.lens, x, y, z); }
      if (type === 'pro') {
        hb(rbox(0.036, 0.022, 0.024, 0.004), CM.body, 0, 0.0085, 0.004);
        lens(0, 0.009, 0.016, 0.006);
        bb(rbox(0.034, 0.016, 0.003, 0.002), CM.body, 0, -0.011, -0.0125);
      } else if (type === 'hd') {
        const cy = new T.CylinderGeometry(0.026, 0.026, 0.068, 24); cy.rotateZ(Math.PI / 2);
        hb(cy, CM.body, 0, -0.0085, 0.0006);
        lens(0.0015, -0.0084, 0.0175, 0.012);
        bb(rbox(0.086, 0.003, 0.042, 0.001), CM.body, 0.003, 0.0298, -0.0088);
        [1, -1].forEach(function (s) { bb(rbox(0.003, 0.04, 0.02, 0.001), CM.body, s * 0.039, 0.011, 0); });
      } else if (type === 'twin') {
        hb(rbox(0.09, 0.05, 0.05, 0.008), CM.body, 0, 0.0036, 0);
        hb(rbox(0.1, 0.004, 0.06, 0.002), CM.body, 0, 0.031, 0.004);
        lens(-0.024, 0.0036, 0.0255, 0.008); lens(0.024, 0.0036, 0.0255, 0.008);
        bb(rbox(0.1, 0.003, 0.045, 0.001), CM.body, 0, -0.041, 0);
        [1, -1].forEach(function (s) { bb(rbox(0.003, 0.045, 0.03, 0.001), CM.body, s * 0.05, -0.019, 0); });
      } else {
        // generic side camera: the real one is still to come
        hb(rbox(0.030, 0.026, 0.046, 0.008), CM.soon, 0, 0, 0.002);
        lens(0, 0, 0.026, 0.0075);
        bb(rbox(0.034, 0.034, 0.004, 0.002), CM.soon, 0, 0, -0.020);
        bb(rbox(0.010, 0.010, 0.014, 0.003), CM.soon, 0, 0, -0.012);
      }
      return { obj: root, head: head };
    }
    function cameraModel(type) {
      const m = models[type];
      if (m) {
        const obj = m.root.clone();
        if (type === 'twin') paintTwin(obj);
        return { obj: obj, head: obj.getObjectByName('head'), info: m.info };
      }
      const s = standIn(type);
      if (type === 'twin') paintTwin(s.obj);
      return { obj: s.obj, head: s.head, info: META[type] };
    }

    /* ---------- Camera heads and their view fans ---------- */
    const UP = new T.Vector3(0, 1, 0), XA = new T.Vector3(1, 0, 0);
    /* Aim frame: the bracket goes flat on the surface (or on a wedge for side cameras) and the head tilts to the aim. */
    function aimFrame(mt) {
      const a = v3(mt.dir).normalize();
      const n = mt.wedge ? new T.Vector3(a.x, 0, a.z).normalize() : v3(mt.n).normalize();
      const X = new T.Vector3().crossVectors(n, a);
      if (X.lengthSq() < 1e-6) X.crossVectors(UP, n);
      X.normalize();
      if (new T.Vector3().crossVectors(a, X).dot(UP) < 0) X.negate();
      return { a: a, n: n, X: X };
    }
    /* Rotation taking the model's X axis to X and its bracket face direction b onto -n (into the surface) */
    function bracketQuat(b, X, n) {
      const bm = v3(b), M = new T.Matrix4().makeBasis(XA.clone(), bm, new T.Vector3().crossVectors(XA, bm));
      const ny = n.clone().negate(), W = new T.Matrix4().makeBasis(X.clone(), ny, new T.Vector3().crossVectors(X, ny));
      return new T.Quaternion().setFromRotationMatrix(W.multiply(M.transpose()));
    }
    /* A small angled block so a side camera can face back along the body. Returns the centre of its outer face. */
    function wedge(group, nS, nF) {
      const w = 0.085, h = 0.08;
      const ex = new T.Vector3().crossVectors(UP, nF).normalize();
      const k = Math.abs(ex.dot(nS)) * w / 2 + 0.018;
      const F = nS.clone().multiplyScalar(k);
      const P = [[-1, -1], [1, -1], [1, 1], [-1, 1]].map(function (c) { return F.clone().addScaledVector(ex, c[0] * w / 2).addScaledVector(UP, c[1] * h / 2); });
      const B = P.map(function (p) { return p.clone().addScaledVector(nS, -p.dot(nS) - 0.012); });
      const pos = [], ctr = F.clone().multiplyScalar(0.5);
      function tri(a, b, c) {
        const nrm = new T.Vector3().subVectors(b, a).cross(new T.Vector3().subVectors(c, a));
        const mid = a.clone().add(b).add(c).multiplyScalar(1 / 3).sub(ctr);
        if (nrm.dot(mid) < 0) { const t = b; b = c; c = t; }
        pos.push(a.x, a.y, a.z, b.x, b.y, b.z, c.x, c.y, c.z);
      }
      function quad(a, b, c, d) { tri(a, b, c); tri(a, c, d); }
      quad(P[0], P[1], P[2], P[3]);
      for (let i = 0; i < 4; i++) { const j = (i + 1) % 4; quad(B[i], B[j], P[j], P[i]); }
      const geo = new T.BufferGeometry();
      geo.setAttribute('position', new T.Float32BufferAttribute(pos, 3));
      geo.computeVertexNormals();
      group.add(new T.Mesh(geo, CM.mount));
      return F;
    }
    const gradTex = (function () {
      const c = document.createElement('canvas'); c.width = 4; c.height = 256;
      const g = c.getContext('2d');
      const grd = g.createLinearGradient(0, 0, 0, 256);
      grd.addColorStop(0, '#ffffff'); grd.addColorStop(0.4, '#9a9a9a'); grd.addColorStop(1, '#000000');
      g.fillStyle = grd; g.fillRect(0, 0, 4, 256);
      return new T.CanvasTexture(c);
    })();
    const groundClip = [new T.Plane(new T.Vector3(0, 1, 0), -0.004)];
    const fanCache = {};
    /* A fan with the lens's real horizontal angle (can exceed 90° either side of centre, unlike a cone). */
    function fanGeo(hDeg, vDeg) {
      const key = hDeg + '_' + vDeg;
      if (fanCache[key]) return fanCache[key];
      const h = hDeg * RAD, v = vDeg * RAD, NH = 44, NV = 10, pos = [], uv = [];
      function P(az, el) { return [Math.sin(az) * Math.cos(el), Math.sin(el), Math.cos(az) * Math.cos(el)]; }
      function tri(a, b) { pos.push(0, 0, 0, a[0], a[1], a[2], b[0], b[1], b[2]); uv.push(0.5, 1, 0.5, 0, 0.5, 0); }
      for (let i = 0; i < NH; i++) {
        const a0 = -h / 2 + h * i / NH, a1 = -h / 2 + h * (i + 1) / NH;
        tri(P(a0, v / 2), P(a1, v / 2)); tri(P(a1, -v / 2), P(a0, -v / 2));
      }
      for (let j = 0; j < NV; j++) {
        const e0 = -v / 2 + v * j / NV, e1 = -v / 2 + v * (j + 1) / NV;
        tri(P(-h / 2, e1), P(-h / 2, e0)); tri(P(h / 2, e0), P(h / 2, e1));
      }
      const geo = new T.BufferGeometry();
      geo.setAttribute('position', new T.Float32BufferAttribute(pos, 3));
      geo.setAttribute('uv', new T.Float32BufferAttribute(uv, 2));
      geo.computeVertexNormals();
      fanCache[key] = geo;
      return geo;
    }

    /* ---------- Scene state ---------- */
    const vehicles = {}, trailers = {};
    let veh = null, trl = null, hitch = null, hitchKey = '';
    const units = {};
    let cvShow = state.towing !== 'none' ? 1 : 0, cvAnim = null, xray = 0, xrayTo = 0, vxray = 0, vxrayTo = 0;
    /* The canopy goes see-through while you look at the camera inside it */
    function applyVehXray() {
      [M.canopy, M.canopyDoor, M.canopyGlass].forEach(function (m) { m.opacity = 1 - 0.62 * vxray; m.depthWrite = vxray < 0.01; });
    }

    function useVehicle(id) {
      if (veh) scene.remove(veh.group);
      veh = vehicles[id] || (vehicles[id] = buildVehicle(id));
      scene.add(veh.group);
    }
    function useTrailer(id) {
      if (trl) scene.remove(trl.group);
      trl = trailers[id] || (trailers[id] = buildTrailer(id));
      scene.add(trl.group);
      applyTrailer();
    }
    function mountFor(uid) {
      if (uid === 'trailer') return trl.mounts[state.trailerPos] || trl.mounts.high || trl.mounts.low;
      if (uid === 'tsideL') return trl.mounts.sideL;
      if (uid === 'tsideR') return trl.mounts.sideR;
      return veh.mounts[uid];
    }
    function onTrailer(uid) { return uid === 'trailer' || uid === 'tsideL' || uid === 'tsideR'; }
    function buildUnit(uid) {
      const old = units[uid];
      if (old) { old.root.parent && old.root.parent.remove(old.root); }
      const mt = mountFor(uid);
      if (!mt) { units[uid] = null; return; }
      const type = typeFor(uid);
      const fr = aimFrame(mt);
      const root = new T.Group();
      root.position.copy(v3(mt.pos));
      const mount = new T.Group();
      root.add(mount);
      const face = mt.wedge ? wedge(mount, v3(mt.n).normalize(), fr.n) : new T.Vector3();
      const cam = cameraModel(type);
      const info = cam.info, s = CAM_SCALE;
      const q = bracketQuat(info.b, fr.X, fr.n);
      const off = v3(info.attach).multiplyScalar(-s).applyQuaternion(q).add(face);
      cam.obj.position.copy(off); cam.obj.quaternion.copy(q); cam.obj.scale.setScalar(s);
      const Zw = new T.Vector3(0, 0, 1).applyQuaternion(q);
      const theta0 = Math.atan2(new T.Vector3().crossVectors(Zw, fr.a).dot(fr.X), Zw.dot(fr.a));
      cam.obj.traverse(function (o) { if (o.isMesh) { o.castShadow = false; o.receiveShadow = false; } });
      mount.add(cam.obj);
      const aim = new T.Group();
      root.add(aim);
      const fans = [];
      const scaleL = uid === 'sideL' || uid === 'sideR' ? 0.8 : uid === 'tsideL' || uid === 'tsideR' ? 0.9 : mt.under ? 0.55 : mt.inside ? 0.75 : 1;
      TYPES[type].fov.forEach(function (fov, i) {
        const narrow = fov < 90;
        const Lf = (type === 'pro' ? 1.9 : narrow ? 3.0 : 2.2) * scaleL;
        const vDeg = narrow ? 26 : 42;
        const mat = new T.MeshBasicMaterial({ color: C(narrow ? '#07708A' : '#0A8FA8'), transparent: true, opacity: 0, alphaMap: gradTex, side: T.DoubleSide, depthWrite: false, toneMapped: false, clippingPlanes: groundClip });
        const fan = new T.Mesh(fanGeo(fov, vDeg), mat);
        fan.layers.set(1);
        fan.renderOrder = 2 + i;
        fan.userData = { L: Lf, op: narrow ? 0.30 : 0.24 };
        aim.add(fan);
        fans.push(fan);
      });
      const v = old ? old.v : (unitOn(uid) ? 1 : 0);
      const u = units[uid] = {
        root: root, mount: mount, fans: fans, aim: aim, head: cam.head, info: info, q: q, off: off, theta0: theta0,
        pitch0: Math.asin(Math.max(-1, Math.min(1, -fr.a.y))) / RAD, tilt: 0, pitch: 0,
        lenses: [], lensMid: new T.Vector3(), dir: fr.a.clone(), up: new T.Vector3(0, 1, 0), n: v3(mt.n).normalize(),
        type: type, inside: !!mt.inside, under: !!mt.under, aboveY: mt.aboveY || 0, v: v, anim: old ? old.anim : null
      };
      aimUnit(u, state.tilt[uid] || 0);
      // centre and size of the camera itself, for the ring that marks it when it's the one you're looking at
      root.updateMatrixWorld(true);
      const bb = new T.Box3().setFromObject(cam.obj);
      u.ctr = bb.getCenter(new T.Vector3()).sub(root.position);
      u.rad = bb.getSize(new T.Vector3()).length() / 2;
      (onTrailer(uid) ? trl.group : scene).add(root);
      applyUnit(u);
    }
    /* Tilt: the bracket stays put and the head turns about its own pivot, so only the up-down angle changes */
    const PITCH_MIN = -5, PITCH_MAX = 80;
    function clampTilt(u, deg) { return Math.max(PITCH_MIN - u.pitch0, Math.min(PITCH_MAX - u.pitch0, deg || 0)); }
    function aimUnit(u, deg) {
      u.tilt = clampTilt(u, deg);
      const th = u.theta0 + u.tilt * RAD, s = CAM_SCALE;
      if (u.head) u.head.rotation.x = th;
      const rx = new T.Quaternion().setFromAxisAngle(XA, th), pv = v3(u.info.pivot);
      u.lenses = u.info.lenses.map(function (L) { return v3(L).sub(pv).applyQuaternion(rx).add(pv).multiplyScalar(s).applyQuaternion(u.q).add(u.off); });
      u.lensMid = u.lenses.reduce(function (acc, p) { return acc.add(p); }, new T.Vector3()).multiplyScalar(1 / u.lenses.length);
      const aimQ = u.q.clone().multiply(rx);
      u.aim.position.copy(u.lensMid); u.aim.quaternion.copy(aimQ);
      u.dir = new T.Vector3(0, 0, 1).applyQuaternion(aimQ);
      u.up = new T.Vector3(0, 1, 0).applyQuaternion(aimQ);
      u.pitch = Math.asin(Math.max(-1, Math.min(1, -u.dir.y))) / RAD;
    }
    function dirAt(u, deg) {
      const rx = new T.Quaternion().setFromAxisAngle(XA, u.theta0 + clampTilt(u, deg) * RAD);
      return new T.Vector3(0, 0, 1).applyQuaternion(u.q.clone().multiply(rx));
    }
    function buildAllUnits() { UNITS.forEach(function (u) { buildUnit(u.id); }); }
    function rebuildType(type) { UNITS.forEach(function (u) { if (units[u.id] && units[u.id].type === type) buildUnit(u.id); }); }
    function clamp01(x) { return x < 0 ? 0 : x > 1 ? 1 : x; }
    function applyUnit(u) {
      if (!u) return;
      const v = u.v, c = clamp01(v);
      u.mount.scale.setScalar(Math.max(0.0001, v));
      u.fans.forEach(function (f) {
        const s = Math.max(0.0001, c) * f.userData.L;
        f.scale.set(s, s, s);
        f.material.opacity = f.userData.op * c;
      });
      u.root.visible = v > 0.002;
    }
    function setUnit(uid, on) {
      const u = units[uid];
      if (!u) return;
      const to = on ? 1 : 0;
      if ((u.anim ? u.anim.to : u.v) === to) return;
      u.anim = { from: u.v, to: to, start: performance.now(), dur: reduced ? 1 : (on ? 520 : 240), delay: on ? (onTrailer(uid) && cvShow < 1 ? 420 : 120) : 0 };
    }
    function stepUnits(now) {
      let any = false;
      UNITS.forEach(function (c) {
        const u = units[c.id], a = u && u.anim;
        if (!a) return;
        any = true;
        let t = (now - a.start - a.delay) / a.dur;
        if (t < 0) return;
        t = Math.min(1, t);
        let e;
        if (a.to > a.from) { const c1 = 1.70158, c3 = c1 + 1; e = 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2); } else e = t * t;
        u.v = a.from + (a.to - a.from) * e;
        if (t >= 1) { u.v = a.to; u.anim = null; }
        applyUnit(u);
      });
      return any;
    }

    /* Trailer in and out; see-through body when the camera is inside */
    const XRAY = ['body', 'door', 'glass'];
    function applyTrailer() {
      if (!trl) return;
      trl.group.position.z = veh.towZ + (1 - cvShow) * -2.4;
      // the scenery behind sits just behind the vehicle, or behind the trailer when there is one
      const vr = veh.rearBarZ - 0.1;
      behind.position.z = vr + cvShow * (Math.min(vr, veh.towZ + trl.zr) - vr);
      Object.keys(TM).forEach(function (k) {
        const see = XRAY.indexOf(k) >= 0 ? 1 - 0.58 * xray : k === 'trim' ? 1 - 0.3 * xray : 1;
        TM[k].opacity = cvShow * see;
        TM[k].depthWrite = see > 0.99;
      });
      trl.group.visible = cvShow > 0.002;
      if (hitch) hitch.visible = cvShow > 0.999;
    }
    function setTrailerShow(on) {
      const to = on ? 1 : 0;
      if ((cvAnim ? cvAnim.to : cvShow) === to) return;
      cvAnim = { from: cvShow, to: to, start: performance.now(), dur: reduced ? 1 : (on ? 750 : 380) };
    }
    function stepTrailer(now) {
      let moved = false;
      if (cvAnim) {
        const t = Math.min(1, (now - cvAnim.start) / cvAnim.dur);
        const e = cvAnim.to > cvAnim.from ? 1 - Math.pow(1 - t, 3) : t * t;
        cvShow = cvAnim.from + (cvAnim.to - cvAnim.from) * e;
        if (t >= 1) { cvShow = cvAnim.to; cvAnim = null; }
        moved = true;
      }
      if (xray !== xrayTo) {
        const d = reduced ? 1 : 0.09;
        xray = xray < xrayTo ? Math.min(xrayTo, xray + d) : Math.max(xrayTo, xray - d);
        moved = true;
      }
      if (vxray !== vxrayTo) {
        const d = reduced ? 1 : 0.09;
        vxray = vxray < vxrayTo ? Math.min(vxrayTo, vxray + d) : Math.max(vxrayTo, vxray - d);
        applyVehXray();
        moved = true;
      }
      if (moved) applyTrailer();
      return moved;
    }

    /* ---------- Across the hitch: Suzi coil, or the Pro HD's plug break point ---------- */
    function buildHitch() {
      const mode = hitchMode(), ch = coilChannels();
      const key = [mode, state.coil, ch, state.body, state.trailer].join('|');
      if (key === hitchKey && hitch) return;
      hitchKey = key;
      if (hitch) { scene.remove(hitch); hitch.traverse(function (o) { if (o.geometry) o.geometry.dispose(); }); hitch = null; }
      if (!mode) return;
      const A = new T.Vector3(0.30, 0.70, veh.rearBarZ - 0.07);
      const B = new T.Vector3(trl.socket[0], trl.socket[1] + 0.02, veh.towZ + trl.socket[2]);
      const gap = A.distanceTo(B);
      const grp = new T.Group();
      function plugAt(p, dir, len, r) {
        const g = new T.Group();
        const body = new T.Mesh(new T.CylinderGeometry(r, r, len, 16), M.camBody);
        const band = new T.Mesh(new T.CylinderGeometry(r * 1.06, r * 1.06, len * 0.28, 16), CM.metal);
        band.position.y = len * 0.32;
        g.add(body, band);
        g.position.copy(p);
        g.quaternion.setFromUnitVectors(UP, dir.clone().normalize());
        g.traverse(function (o) { if (o.isMesh) o.castShadow = true; });
        grp.add(g);
      }
      if (mode === 'break') {
        const mid = A.clone().lerp(B, 0.5); mid.y -= 0.10;
        const curve = new T.CatmullRomCurve3([A, A.clone().lerp(mid, 0.5).add(new T.Vector3(0, -0.06, 0)), mid, mid.clone().lerp(B, 0.5).add(new T.Vector3(0, -0.06, 0)), B]);
        const cable = new T.Mesh(new T.TubeGeometry(curve, 60, 0.006, 6, false), M.coil);
        cable.castShadow = true;
        grp.add(cable);
        plugAt(curve.getPointAt(0.5), curve.getTangentAt(0.5), 0.11, 0.019);
        plugAt(A, curve.getTangentAt(0), 0.05, 0.015);
        plugAt(B, curve.getTangentAt(1), 0.05, 0.015);
        grp.userData.mid = curve.getPointAt(0.5).add(new T.Vector3(0, 0.08, 0));
      } else {
        const c = COILS[state.coil];
        const slack = clamp01(1 - gap / (c.gap * 1.25));
        const sag = 0.05 + 0.24 * slack;
        const mid = A.clone().lerp(B, 0.5); mid.y -= sag;
        const spine = new T.CatmullRomCurve3([A, A.clone().lerp(mid, 0.5).add(new T.Vector3(0, -sag * 0.3, 0)), mid, mid.clone().lerp(B, 0.5).add(new T.Vector3(0, -sag * 0.3, 0)), B]);
        const turns = c.turns, N = turns * 12 + 40, R = 0.032, lead = 0.16 / Math.max(0.4, spine.getLength());
        const frames = spine.computeFrenetFrames(N, false);
        const pts = [];
        for (let i = 0; i <= N; i++) {
          const t = i / N, p = spine.getPointAt(t);
          const u = clamp01((t - lead) / (1 - 2 * lead));
          const ramp = Math.min(1, u * 10, (1 - u) * 10);
          const ang = u * turns * Math.PI * 2;
          pts.push(p.clone().addScaledVector(frames.normals[i], Math.cos(ang) * R * ramp).addScaledVector(frames.binormals[i], Math.sin(ang) * R * ramp));
        }
        const helix = new T.Mesh(new T.TubeGeometry(new T.CatmullRomCurve3(pts), N * 2, ch > 1 ? 0.0075 : 0.0065, 5, false), M.coil);
        helix.castShadow = true;
        grp.add(helix);
        plugAt(A, spine.getTangentAt(0), 0.09, 0.024);
        plugAt(B, spine.getTangentAt(1).negate(), 0.09, 0.024);
        grp.userData.mid = mid.clone().add(new T.Vector3(0, 0.06, 0));
      }
      grp.userData.gap = gap;
      hitch = grp;
      scene.add(hitch);
      hitch.visible = cvShow > 0.999;
    }
    function socketGap() { return hitch ? hitch.userData.gap : 0; }

    function sync() {
      UNITS.forEach(function (u) { setUnit(u.id, unitOn(u.id)); });
      setTrailerShow(state.towing !== 'none');
      buildHitch();
      xrayTo = state.focused === 'trailer' && state.trailerPos === 'inside' && state.selected.has('trailer') ? 1 : 0;
      vxrayTo = state.focused === 'tray' && state.body === 'canopy' && state.selected.has('tray') ? 1 : 0;
    }

    useVehicle(state.body);
    useTrailer(state.trailer);
    buildAllUnits();
    buildHitch();

    /* ---------- View: orbit, glide, framing ---------- */
    const FOV = 30;
    const view = { th: 128 * RAD, ph: 72 * RAD, r: 12, target: new T.Vector3(0, 0.98, -0.3) };
    let anim = null, auto = !reduced, dirty = true;
    let region = { x: 0, y: 0, w: window.innerWidth, h: window.innerHeight };
    let focal = 800;
    const pp = { x: window.innerWidth / 2, y: window.innerHeight / 2 };
    const ANGLES = { front: [34, 68], reverse: [146, 64], tray: [138, 52], sideL: [62, 72], sideR: [-62, 72], trailer: [150, 62], inside: [122, 52], tsideL: [118, 66], tsideR: [-118, 66], underB: [98, 87], underF: [64, 86], canopy: [132, 50], hitch: [126, 58] };
    function isDesk() { return window.matchMedia('(min-width: 900px)').matches; }
    function setProjection(cx, cy) {
      const W = window.innerWidth, H = window.innerHeight;
      const fullW = 2 * Math.max(cx, W - cx), fullH = 2 * Math.max(cy, H - cy);
      camera.fov = 2 * Math.atan(fullH / 2 / focal) / RAD;
      camera.aspect = fullW / fullH;
      camera.setViewOffset(fullW, fullH, fullW / 2 - cx, fullH / 2 - cy, W, H);
      camera.updateProjectionMatrix();
    }
    function shiftBox(b, dz) { return [[b[0][0], b[0][1], b[0][2] + dz], [b[1][0], b[1][1], b[1][2] + dz]]; }
    function boxFor(id) {
      if (id === 'whole') {
        if (state.towing === 'none') return veh.boxes.whole;
        const tb = shiftBox(trl.wholeBox, veh.towZ);
        return [[Math.min(-1.05, tb[0][0]), 0, tb[0][2]], [Math.max(1.05, tb[1][0]), Math.max(2.15, tb[1][1]), 2.6]];
      }
      if (id === 'trailer') return shiftBox(state.trailerPos === 'inside' ? trl.insideBox : trl.focusBox, veh.towZ);
      if (id === 'tside') return shiftBox(trl.sideBoxes[state.focusSide], veh.towZ);
      if (id === 'side' || id === 'under') return veh.boxes[id + state.focusSide];
      if (id === 'hitch') return [[-0.75, 0.3, veh.towZ + trl.socket[2] - 0.45], [0.85, 1.35, veh.towZ + 0.75]];
      return veh.boxes[id];
    }
    // the camera's view never sits on the 3D view any more (phone: in the options; desktop: in the panel), so the car gets the whole region
    function bandFor() { return region; }
    const corner = new T.Vector3(), proj = new T.Vector3();
    function fitBox(b, th, ph, band, pad) {
      const W = window.innerWidth, H = window.innerHeight;
      pad = pad || [0.07, 0.08];
      const tgt = new T.Vector3((b[0][0] + b[1][0]) / 2, (b[0][1] + b[1][1]) / 2, (b[0][2] + b[1][2]) / 2);
      const cx = band.x + band.w / 2, cy = band.y + band.h / 2;
      const padX = Math.max(12, band.w * pad[0]), padY = Math.max(12, band.h * pad[1]);
      const savedPos = camera.position.clone(), savedQ = camera.quaternion.clone();
      setProjection(cx, cy);
      const s = Math.sin(ph);
      let lo = 0.3, hi = 120;
      for (let i = 0; i < 26; i++) {
        const r = (lo + hi) / 2;
        camera.position.set(tgt.x + r * s * Math.sin(th), tgt.y + r * Math.cos(ph), tgt.z + r * s * Math.cos(th));
        camera.lookAt(tgt);
        camera.updateMatrixWorld();
        let ok = true;
        for (let k = 0; k < 8 && ok; k++) {
          corner.set(b[k & 1][0], b[(k >> 1) & 1][1], b[(k >> 2) & 1][2]);
          proj.copy(corner).project(camera);
          const x = (proj.x + 1) / 2 * W, y = (1 - proj.y) / 2 * H;
          if (proj.z > 1 || x < band.x + padX || x > band.x + band.w - padX || y < band.y + padY || y > band.y + band.h - padY) ok = false;
        }
        if (ok) hi = r; else lo = r;
      }
      camera.position.copy(savedPos); camera.quaternion.copy(savedQ); camera.updateMatrixWorld();
      setProjection(pp.x, pp.y);
      return { th: th, ph: ph, r: hi, target: tgt, pp: { x: cx, y: cy } };
    }
    function niceAngle(th) {
      const d = ((th / RAD) % 360 + 360) % 360;
      const near = [0, 90, 180, 270, 360].some(function (a) { return Math.abs(d - a) < 18; });
      return near ? th + 30 * RAD : th;
    }
    function overviewPose() {
      const rig = state.towing !== 'none';
      const b = boxFor('whole'), ph = (rig ? 62 : 71) * RAD, pad = [0.045, 0.06];
      let th = niceAngle(view.th);
      if (rig) { const c = ((view.th / RAD) % 360 + 360) % 360; th = (Math.abs(c - 145) <= Math.abs(c - 215) ? 145 : 215) * RAD; }
      const p = fitBox(b, th, ph, region, pad);
      if (auto) { for (let a = 0; a < 360; a += 45) { const q = fitBox(b, a * RAD, ph, region, pad); if (q.r > p.r) p.r = q.r; } }
      return p;
    }
    function focusUnit(id) {
      if (PAIRS[id]) {
        const other = PAIRS[id].opts.filter(function (o) { return o[0] !== state.focusSide && o[0] !== 'both'; })[0][0];
        return units[id + state.focusSide] || units[id + other];
      }
      return units[id];
    }
    const wp = new T.Vector3();
    function unitWorld(u, local, out) { u.root.updateWorldMatrix(true, false); return out.copy(local).applyMatrix4(u.root.matrixWorld); }
    function focusPose(id) {
      const u = byId[id] && focusUnit(id);
      if (state.zoom && u) {
        u.mount.updateWorldMatrix(true, true);
        const mb = new T.Box3().setFromObject(u.mount), ms = mb.getSize(new T.Vector3());
        mb.getCenter(wp);
        const r = Math.max(ms.x, ms.y, ms.z) * 0.62 + 0.025, b = [[wp.x - r, wp.y - r, wp.z - r], [wp.x + r, wp.y + r, wp.z + r]];
        const th = Math.atan2(u.dir.x, u.dir.z) / RAD + (u.dir.x > 0.2 ? -38 : 38);
        return fitBox(b, th * RAD, (u.under ? 86 : u.inside ? 60 : 68) * RAD, bandFor(id), isDesk() ? [0.2, 0.2] : [0.08, 0.1]);
      }
      const key = PAIRS[id] ? id + state.focusSide : id === 'trailer' && state.trailerPos === 'inside' ? 'inside' : id === 'tray' && state.body === 'canopy' ? 'canopy' : id;
      const a = ANGLES[key];
      return fitBox(boxFor(id), a[0] * RAD, a[1] * RAD, bandFor(id), isDesk() ? [0.24, 0.22] : [0.07, 0.08]);
    }
    function maxPhi() {
      const c = (0.22 - view.target.y) / view.r;
      return Math.min(93 * RAD, Math.acos(Math.max(-1, Math.min(1, c))));
    }
    function applyView() {
      view.ph = Math.max(10 * RAD, Math.min(maxPhi(), view.ph));
      const s = Math.sin(view.ph);
      camera.position.set(view.target.x + view.r * s * Math.sin(view.th), view.target.y + view.r * Math.cos(view.ph), view.target.z + view.r * s * Math.cos(view.th));
      camera.lookAt(view.target);
    }
    let flyId = null;   // the camera the view is flying to (null: the whole car)
    function flyTo(pose, cb) {
      const from = { th: view.th, ph: view.ph, r: view.r, t: view.target.clone(), px: pp.x, py: pp.y };
      const dth = Math.atan2(Math.sin(pose.th - from.th), Math.cos(pose.th - from.th));
      const dist = Math.abs(dth) + Math.abs(pose.ph - from.ph) + Math.abs(Math.log(pose.r / from.r)) * 0.6 + from.t.distanceTo(pose.target) * 0.15;
      anim = {
        from: from, to: { th: from.th + dth, ph: pose.ph, r: pose.r, t: pose.target, px: pose.pp.x, py: pose.pp.y },
        start: performance.now(), dur: reduced ? 1 : 650 + 700 * Math.min(1, dist / 2.2),
        bump: Math.min(0.32, 0.12 * Math.abs(dth)) * Math.max(from.r, pose.r), cb: cb
      };
    }
    function stepAnim(now) {
      const a = anim, t = Math.min(1, (now - a.start) / a.dur);
      const e = t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
      view.th = a.from.th + (a.to.th - a.from.th) * e;
      view.ph = a.from.ph + (a.to.ph - a.from.ph) * e;
      view.r = Math.exp(Math.log(a.from.r) + (Math.log(a.to.r) - Math.log(a.from.r)) * e) + a.bump * Math.sin(Math.PI * t);
      view.target.lerpVectors(a.from.t, a.to.t, e);
      pp.x = a.from.px + (a.to.px - a.from.px) * e;
      pp.y = a.from.py + (a.to.py - a.from.py) * e;
      setProjection(pp.x, pp.y);
      if (t >= 1) { anim = null; if (a.cb) a.cb(); }
    }

    /* ---------- What-it-sees view (second renderer) ---------- */
    const insetCanvas = $('insetCanvas');
    let ir = null, envInsetDay = null, envInsetNight = null, insetOn = false, insetDirty = false, insetRect = null, insetW = 0, insetH = 0;
    const icam = new T.PerspectiveCamera(80, 1.6, 0.03, 180);
    icam.layers.set(0); icam.layers.enable(2);
    function ensureInset() {
      if (ir) return true;
      try { ir = new T.WebGLRenderer({ canvas: insetCanvas, antialias: true }); } catch (err) { ir = null; return false; }
      ir.setPixelRatio(DPR);
      ir.outputEncoding = T.sRGBEncoding;
      ir.toneMapping = T.ACESFilmicToneMapping;
      ir.toneMappingExposure = 1.1;
      ir.shadowMap.enabled = true;
      ir.shadowMap.type = T.PCFSoftShadowMap;
      envInsetDay = makeEnv(ir, false);
      envInsetNight = makeEnv(ir, true);
      return true;
    }
    function sizeInset() {
      if (!ir || !insetOn) return;
      const r = insetCanvas.getBoundingClientRect();
      if (r.width < 4) return;
      insetW = r.width; insetH = r.height;
      ir.setSize(r.width, r.height, false);
      insetDirty = true;
    }
    /* One pane per lens: Twin shows its two lenses, a pair of side cameras shows both sides */
    function insetViews() {
      const f = state.focused;
      if (!f || !byId[f]) return [];
      if (PAIRS[f]) {
        const cur = state[PAIRS[f].key], out = [];
        PAIRS[f].opts.forEach(function (o) {
          const u = units[f + o[0]];
          if (o[0] !== 'both' && u && hasSide(cur, o[0])) out.push({ u: u, fov: TYPES[u.type].fov[0], tag: o[3] });
        });
        return out;
      }
      const u = units[f];
      if (!u) return [];
      return TYPES[u.type].fov.map(function (fov) { return { u: u, fov: fov, tag: fov + '°' }; });
    }
    /* Night: the Heavy Duty and Twin light the scene with infrared (black and white).
       The Pro HD has no infrared: a muted, bluish-white low-light picture lit by the vehicle's lights and the night sky. */
    const IR_TYPES = { hd: true, twin: true }, LOWSKY = '#1B2635';
    function nightMode(type) { return state.night ? (IR_TYPES[type] ? 'ir' : 'low') : null; }
    function showInset(id) {
      if (!ensureInset()) return;
      const views = insetViews();
      if (!views.length) { hideInset(); return; }
      const c = byId[id], u0 = views[0].u, type = TYPES[u0.type], split = views.length > 1;
      const mode = nightMode(u0.type);
      let name = camName(id) + ' · ' + type.label;
      if (id === 'side' && !split) name = views[0].tag + '-side camera · ' + type.label;
      if (id === 'under' && !split) name = views[0].tag + ' axle camera · ' + type.label;
      if (id === 'tside') name = (split ? 'Trailer side cameras' : views[0].tag + '-side trailer camera') + ' · preview';
      $('insetName').textContent = name;
      const sc = scr(id), mirror = sc.mirror, flip = sc.flip && u0.type === 'pro';
      $('insetNote').textContent = (mode === 'ir' ? 'Night, infrared. ' : mode === 'low' ? 'Night, low light (no infrared). ' : '') + type.note + (mirror ? ', mirrored' : ', not mirrored') + (flip ? ', flipped upside down' : '');
      $('liveTag').textContent = mode === 'ir' ? 'Night vision · IR' : mode === 'low' ? 'Night vision' : id === 'tside' ? 'Preview' : 'What it sees';
      insetCanvas.classList.toggle('mirror', mirror);
      insetCanvas.classList.toggle('flip', flip);
      insetCanvas.classList.toggle('ir', mode === 'ir');
      insetCanvas.classList.toggle('lowlight', mode === 'low');
      // guidelines: Pro HD only, and you can turn them off
      $('guides').toggleAttribute('hidden', !(sc.guides && u0.type === 'pro') || split || (id === 'trailer' && state.trailerPos === 'inside'));
      const order = mirror ? views.slice().reverse() : views;
      $('tagA').hidden = !split; $('tagB').hidden = !split;
      if (split) {
        // the canvas is flipped when mirrored, so the labels follow what you see
        const shown = mirror ? order.slice().reverse() : order;
        $('tagA').textContent = shown[0].tag; $('tagB').textContent = shown[1].tag;
      }
      $('inset').hidden = false;
      insetOn = true;
      insetRect = floatRect();
      sizeInset();
      insetDirty = true;
    }
    function hideInset() { $('inset').hidden = true; insetOn = false; }
    /* Phone: the camera's view sits in the options, under the camera's name, next to its buttons (Daniel, 4 Nov 2026:
       over the 3D view it still covered the car; he picked "in the options panel"). Desktop: in the side panel, as before. */
    function dockInset() {
      const el = $('inset'), card = $('card'), head = card && card.querySelector('.card-head'), sheet = $('sheet');
      if (!isDesk() && head) { if (el.previousElementSibling !== head) head.after(el); }
      else if (el.nextElementSibling !== sheet) sheet.before(el);
    }
    // only a view that floats over the 3D view needs keeping clear of (none do now)
    function floatRect() { return insetOn && !isDesk() && !$('inset').closest('.card') ? $('inset').getBoundingClientRect() : null; }
    const tmpV = new T.Vector3(), tmpV2 = new T.Vector3();
    function renderInset() {
      const views = insetViews();
      if (!views.length || !ir || !insetW) return;
      if (!insetCanvas.offsetParent) return;   // tucked away with the options; the options' resize redraws it when they come back
      const mode = nightMode(views[0].u.type), low = mode === 'low';
      scene.fog = insetFog;
      if (mode) {
        sun.intensity = 0; fill.intensity = 0;
        hemi.intensity = low ? 0.3 : 0.04;
        if (low) { hemi.color.copy(C('#A7BBE2')); hemi.groundColor.copy(C('#2C3442')); }
        irSpot.intensity = low ? 0.75 : 1.25;
        irSpot.angle = (low ? 78 : 64) * RAD;
        if (low) irSpot.color.copy(C('#E4EDFF'));
        scene.environment = envInsetNight; sky.visible = false; insetFog.color.copy(C(low ? LOWSKY : NIGHT));
        ir.setClearColor(new T.Color(low ? LOWSKY : NIGHT), 1);
      } else {
        scene.environment = envInsetDay;
        ir.setClearColor(new T.Color(SKY), 1);
      }
      const order = scr(state.focused).mirror ? views.slice().reverse() : views;
      const n = order.length, gap = n > 1 ? 3 : 0, pw = (insetW - gap * (n - 1)) / n;
      ir.setScissorTest(false);
      ir.clear();
      ir.setScissorTest(n > 1);
      order.forEach(function (vw, i) {
        const u = vw.u, d = u.dir;
        unitWorld(u, u.lensMid, tmpV).addScaledVector(d, 0.02);
        icam.position.copy(tmpV);
        icam.up.copy(u.up);
        icam.lookAt(tmpV2.copy(tmpV).add(d));
        if (mode) {
          irSpot.position.copy(tmpV);
          irSpot.target.position.copy(tmpV).addScaledVector(d, 6); irSpot.target.updateMatrixWorld();
        }
        const x = i * (pw + gap);
        icam.aspect = pw / insetH;
        icam.fov = 2 * Math.atan(Math.tan(Math.min(vw.fov, 130) / 2 * RAD) / icam.aspect) / RAD;
        icam.updateProjectionMatrix();
        ir.setViewport(x, 0, pw, insetH);
        if (n > 1) ir.setScissor(x, 0, pw, insetH);
        ir.render(scene, icam);
      });
      ir.setScissorTest(false);
      scene.environment = envMain; scene.fog = null;
      if (mode) {
        hemi.intensity = LIGHT_DAY.hemi; sun.intensity = LIGHT_DAY.sun; fill.intensity = LIGHT_DAY.fill;
        hemi.color.setHex(0xffffff); hemi.groundColor.setHex(0xb6bfbf);
        irSpot.intensity = 0; irSpot.angle = 64 * RAD; irSpot.color.setHex(0xffffff);
        sky.visible = true; insetFog.color.copy(C(SKY));
      }
    }

    /* ---------- Hotspots ---------- */
    const ray = new T.Raycaster(), hitP = new T.Vector3(), shiftV = new T.Vector3();
    const hsEls = {};
    UNITS.forEach(function (c) { hsEls[c.id] = $('hs-' + c.id); });
    const coilEl = $('coilTag');
    const rings = {};
    const dotAt = {};   // where each + spot was last drawn, so a camera's name can keep clear of them on a phone
    let tipShown = false;
    /* Which way the lens points on screen, for a given tilt: where the dial's knob sits on the ring */
    const kA = new T.Vector3(), kB = new T.Vector3();
    function knobDir(u, deg) {
      unitWorld(u, u.lensMid, kA);
      const len = Math.min(0.6, 0.25 * camera.position.distanceTo(kA));
      kB.copy(kA).addScaledVector(dirAt(u, deg), len);
      kA.project(camera); kB.project(camera);
      if (kA.z > 1 || kB.z > 1) return null;
      const dx = (kB.x - kA.x) / 2 * window.innerWidth, dy = -(kB.y - kA.y) / 2 * window.innerHeight, l = Math.hypot(dx, dy);
      return l < 1.5 ? null : { x: dx / l, y: dy / l };
    }
    function tiltUnit(uid, deg) {
      const u = units[uid];
      if (!u) return;
      aimUnit(u, deg);
      state.tilt[uid] = u.tilt;
      dirty = true; insetDirty = true;
    }
    /* Drag along the ring (or up and down): move the knob the way you want the lens to point */
    function tiltDragBy(uid, dx, dy) {
      const u = units[uid], r = rings[uid];
      if (!u) return;
      const R = Math.max(r ? r.R : 40, 90);
      const ta = clampTilt(u, u.tilt - 1), tb = clampTilt(u, u.tilt + 1);
      const ka = knobDir(u, ta), kb = knobDir(u, tb);
      let d = dy * 0.45;
      if (ka && kb && tb > ta) {
        const vx = (kb.x - ka.x) * R / (tb - ta), vy = (kb.y - ka.y) * R / (tb - ta), vv = vx * vx + vy * vy;
        if (vv > 0.09) d = (dx * vx + dy * vy) / vv;
      }
      tiltUnit(uid, u.tilt + Math.max(-6, Math.min(6, d)));
    }
    $('hotspots').addEventListener('keydown', function (e) {
      const b = e.target.closest('.hs');
      if (!b || !b.classList.contains('is-ring') || (e.key !== 'ArrowUp' && e.key !== 'ArrowDown')) return;
      e.preventDefault();
      const u = units[b.dataset.id];
      if (!u) return;
      tiltUnit(b.dataset.id, u.tilt + (e.key === 'ArrowUp' ? -2 : 2));
      say(unitById[b.dataset.id].label + ' aimed ' + Math.round(u.pitch) + '° down');
    });
    function placeTag(el, p, list, W, H, flip, ring, testP) {
      const q = testP || p;
      const dist = camera.position.distanceTo(q);
      tmpV2.copy(q).sub(camera.position).normalize();
      ray.set(camera.position, tmpV2);
      let hit = false;
      for (let i = 0; i < list.length && !hit; i++) {
        if (ray.ray.intersectBox(list[i], hitP) && hitP.distanceTo(camera.position) < dist - 0.06) hit = true;
      }
      tmpV2.copy(p).project(camera);
      const x = (tmpV2.x + 1) / 2 * W, y = (1 - tmpV2.y) / 2 * H;
      const m = isDesk() ? 26 : 12;
      const underInset = insetOn && insetRect && x > insetRect.left - 16 && x < insetRect.right + 16 && y > insetRect.top - 16 && y < insetRect.bottom + 16;
      const inside = !underInset && tmpV2.z < 1 && x > region.x + m && x < region.x + region.w - m && y > region.y + m && y < region.y + region.h - m;
      el.style.transform = 'translate(' + x.toFixed(1) + 'px,' + y.toFixed(1) + 'px)';
      if (flip && ring) {
        // name goes beside the ring where it fits, otherwise under it
        const lw = el.querySelector('.hs-label').offsetWidth || 170;
        // phone: the camera's view sits in the top corner, so a name level with it stops at its edge
        let rEdge = region.x + region.w;
        if (insetOn && insetRect && y - 16 < insetRect.bottom + 6 && y + 16 > insetRect.top - 6) rEdge = Math.min(rEdge, insetRect.left - 6);
        const right = rEdge - (x + ring / 2 + 12), left = (x - ring / 2 - 12) - region.x;
        // above the ring when there's no room under it (on a phone, the panel covers the bottom of the view)
        const roomBelow = region.y + region.h - (y + ring / 2 + 44);
        let side = right >= lw ? '' : left >= lw ? 'flip' : roomBelow >= 0 ? 'below' : 'above';
        // a name under or over the ring slides left until it clears the camera's view
        const lxFor = function (s) {
          if ((s !== 'below' && s !== 'above') || !insetOn || !insetRect) return 0;
          const ly = s === 'below' ? y + ring / 2 + 8 : y - ring / 2 - 36;
          return ly < insetRect.bottom + 6 && ly + 28 > insetRect.top - 6 && x + lw / 2 > insetRect.left - 6 ? Math.max(region.x + 6 - (x - lw / 2), insetRect.left - 6 - (x + lw / 2)) : 0;
        };
        if (!isDesk()) {
          // phone: and off the + of another camera spot, so that spot can still be seen and tapped (Daniel, 4 Nov 2026)
          const box = function (s) {
            const lx = lxFor(s);
            return s === '' ? [x + ring / 2 + 8, y - 14] : s === 'flip' ? [x - ring / 2 - 8 - lw, y - 14] : [x - lw / 2 + lx, s === 'below' ? y + ring / 2 + 8 : y - ring / 2 - 36];
          };
          const clear = function (s) {
            const b = box(s);
            return !Object.keys(dotAt).some(function (k) {
              const d = dotAt[k];
              if (!d) return false;
              const cx = Math.max(b[0], Math.min(d.x, b[0] + lw)), cy = Math.max(b[1], Math.min(d.y, b[1] + 28));
              return (d.x - cx) * (d.x - cx) + (d.y - cy) * (d.y - cy) < 19 * 19;
            });
          };
          const fits = { '': right >= lw, flip: left >= lw, below: roomBelow >= 0, above: y - ring / 2 - 36 >= region.y + 4 };
          const pick = ['', 'flip', 'below', 'above'].filter(function (s) { return fits[s] && clear(s); })[0];
          if (pick !== undefined) side = pick;
        }
        el.classList.toggle('flip', side === 'flip'); el.classList.toggle('below', side === 'below'); el.classList.toggle('above', side === 'above');
        el.style.setProperty('--lx', Math.round(lxFor(side)) + 'px');
        el.classList.toggle('tip-up', (side === '' || side === 'flip') && roomBelow < 0);
      } else if (flip) { el.classList.toggle('flip', x > region.x + region.w - 170); el.classList.remove('below', 'above', 'tip-up'); }
      if (ring) el.style.setProperty('--ring', ring.toFixed(0) + 'px');
      const show = inside && !hit;
      el.classList.toggle('is-hidden', !show);
      return show ? { x: x, y: y } : null;
    }
    function updateHotspots() {
      const W = window.innerWidth, H = window.innerHeight;
      const list = veh.proxies.slice();
      if (trl.group.visible) { shiftV.set(0, 0, trl.group.position.z); trl.proxies.forEach(function (b) { list.push(b.clone().translate(shiftV)); }); }
      // the camera you're looking at goes last, so its name is placed knowing where this frame's + spots are
      const isF = function (c) { return hsEls[c.id].classList.contains('is-focus'); };
      UNITS.filter(function (c) { return !isF(c); }).concat(UNITS.filter(isF)).forEach(function (c) {
        const el = hsEls[c.id], u = units[c.id];
        rings[c.id] = null; dotAt[c.id] = null;
        if (!u) { el.classList.add('is-hidden'); return; }
        if (c.cam === 'tside' && (!state.selected.has('trailer') || cvShow < 0.98)) { el.classList.add('is-hidden'); return; }
        // past step 1 the car just shows the cameras you've picked
        if (state.step !== 'see' && !unitOn(c.id)) { el.classList.add('is-hidden'); return; }
        // the camera you're looking at: ring around the camera itself rather than a tick on top of it
        const ringed = el.classList.contains('is-focus') && unitOn(c.id) && u.v > 0.6 && !(c.id === 'trailer' && cvShow < 0.98);
        el.classList.toggle('is-ring', ringed);
        let ring = 0;
        if (c.id === 'trailer' && cvShow < 0.5) tmpV.set(0, 0.78, veh.towZ - 0.6);
        else if (ringed) {
          unitWorld(u, u.ctr, tmpV);
          tmpV2.set(0, 0, -1).applyQuaternion(camera.quaternion);
          const depth = Math.max(0.05, tmpV2.dot(tmpV.clone().sub(camera.position)));
          ring = Math.max(38, Math.min(210, 2 * u.rad * Math.min(1, u.v) * focal / depth * 0.92 + 18));
        } else {
          unitWorld(u, u.lensMid, tmpV);
          if (u.inside) tmpV.y = u.aboveY || trl.H + 0.28;
          else { tmpV.addScaledVector(u.dir, 0.10); tmpV.y += 0.06; }
        }
        // hide a ring when its camera is behind the vehicle; test just in front of the lens so a camera tucked under the tray still counts as visible
        const testP = ringed ? unitWorld(u, u.lensMid, new T.Vector3()).addScaledVector(u.dir, 0.12) : null;
        const at = placeTag(el, tmpV.clone(), u.inside || (ringed && u.under) ? [] : list, W, H, true, ring, testP);
        if (ringed) {
          const kd = knobDir(u, u.tilt) || { x: 0, y: 1 };
          el.style.setProperty('--kx', (kd.x * ring / 2).toFixed(1) + 'px');
          el.style.setProperty('--ky', (kd.y * ring / 2).toFixed(1) + 'px');
          el.style.setProperty('--ka', (Math.atan2(kd.y, kd.x) / RAD).toFixed(0) + 'deg');
          const ang = Math.round(u.pitch);
          if (el._ang !== ang) { el._ang = ang; el.querySelector('.hs-ang').textContent = ' · ' + (ang > 0 ? ang + '° down' : ang < 0 ? -ang + '° up' : 'level'); }
          if (!tipShown && at) { tipShown = true; el.classList.add('tip'); }
        }
        if (ringed && at) rings[c.id] = { x: at.x, y: at.y, r: ring / 2 + 8, R: ring / 2 };
        else if (at) dotAt[c.id] = at;
      });
      if (hitch && hitch.visible && state.step === 'see') placeTag(coilEl, hitch.userData.mid.clone(), list, W, H, false);
      else coilEl.classList.add('is-hidden');
    }

    /* The part of the screen the car is drawn in: between the header and the panel on a phone, left of the panel on a desktop */
    function measure() {
      const W = window.innerWidth, H = window.innerHeight;
      const topR = $('top').getBoundingClientRect(), sheetR = $('sheet').getBoundingClientRect(), uiR = $('ui').getBoundingClientRect();
      const panelW = document.body.classList.contains('panel-closed') ? 0 : uiR.width;   // the desktop panel can be tucked away
      // on a phone the camera's buttons get their own strip above the options, so they never sit on the camera
      const barH = !isDesk() && state.focused ? 56 : 0;
      // desktop: the 3D view sits under the site header and left of the panel
      const sb = $('siteBar'), sbB = sb ? Math.max(0, Math.round(sb.getBoundingClientRect().bottom)) : 0;
      if (isDesk()) region = { x: 0, y: sbB, w: Math.max(240, W - panelW), h: Math.max(120, H - sbB) };
      else { const y0 = Math.max(topR.bottom, sbB); region = { x: 0, y: y0, w: W, h: Math.max(120, sheetR.top - y0 - barH) }; }   // phone: the step line hides while you look at a camera
      const rs = document.documentElement.style;
      rs.setProperty('--inset-top', Math.round(topR.bottom + 6) + 'px');
      // the free part of the screen, for the buttons that sit on the 3D view
      rs.setProperty('--region-x', Math.round(region.x) + 'px');
      rs.setProperty('--region-w', Math.round(region.w) + 'px');
      // Shopify's preview bar covers the bottom of the page while a draft theme is being tested: keep the buttons above it
      const pv = isDesk() && document.getElementById('PBarNextFrameWrapper');
      const pvH = pv ? Math.round(pv.getBoundingClientRect().height) : 0;
      rs.setProperty('--region-bottom', (Math.max(0, Math.round(H - region.y - region.h - barH)) + pvH) + 'px');
      // phone: the camera's buttons sit 10px above the options, measured from the options themselves (not the window height, which iOS Safari reports differently)
      rs.setProperty('--bar-y', Math.round(sheetR.top - 10) + 'px');
      focal = (region.h / 2) / Math.tan(FOV / 2 * RAD);
    }
    function layout() {
      const W = window.innerWidth, H = window.innerHeight;
      dockInset();
      measure();
      renderer.setSize(W, H, false);
      sizeInset();
      insetRect = floatRect();
      cardMore();
      if (!anim) {
        const p = state.focused ? focusPose(state.focused) : overviewPose();
        view.r = p.r; view.target.copy(p.target); pp.x = p.pp.x; pp.y = p.pp.y;
      } else {
        // the page changed size while flying (the options and headings settle after a tap): aim at the new spot
        const p = flyId ? focusPose(flyId) : overviewPose();
        anim.to.th = anim.from.th + Math.atan2(Math.sin(p.th - anim.from.th), Math.cos(p.th - anim.from.th));
        anim.to.ph = p.ph; anim.to.r = p.r; anim.to.t = p.target; anim.to.px = p.pp.x; anim.to.py = p.pp.y;
      }
      setProjection(pp.x, pp.y);
      dirty = true;
    }

    /* ---------- Input ---------- */
    const pointers = new Map();
    let pinchDist = 0, tap = null, tiltDrag = null, panning = false, pinchMid = null;
    const panR = new T.Vector3(), panU = new T.Vector3();
    function panBy(dx, dy) {
      panR.set(1, 0, 0).applyQuaternion(camera.quaternion);
      panU.set(0, 1, 0).applyQuaternion(camera.quaternion);
      const k = view.r / focal;
      view.target.addScaledVector(panR, -dx * k).addScaledVector(panU, dy * k);
      view.target.x = Math.max(-14, Math.min(14, view.target.x));
      view.target.y = Math.max(0.1, Math.min(4, view.target.y));
      view.target.z = Math.max(-26, Math.min(14, view.target.z));
    }
    canvas.addEventListener('contextmenu', function (e) { e.preventDefault(); });
    canvas.addEventListener('mousedown', function (e) { if (e.button === 1) e.preventDefault(); });
    /* Tapping the camera inside its ring zooms in or out, the same as tapping its name */
    function ringAt(x, y, pad) {
      for (const id in rings) {
        const r = rings[id];
        if (r && Math.hypot(x - r.x, y - r.y) <= r.r + (pad || 0)) return id;
      }
      return null;
    }
    function canvasTap(x, y) { const id = ringAt(x, y, 0); if (id) tapUnit(id); }
    function stopAuto() { auto = false; }
    canvas.addEventListener('pointerdown', function (e) {
      canvas.setPointerCapture(e.pointerId);
      pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (pointers.size === 2) { const p = Array.from(pointers.values()); pinchDist = Math.hypot(p[0].x - p[1].x, p[0].y - p[1].y); }
      canvas.classList.add('dragging');
      stopAuto();
      anim = null;
      panning = pointers.size === 1 && (e.button === 1 || e.button === 2 || (e.button === 0 && e.shiftKey));
      if (e.button === 1) e.preventDefault();
      tap = pointers.size === 1 && !panning ? { t: performance.now(), moved: 0 } : null;
      tiltDrag = pointers.size === 1 && !panning ? ringAt(e.clientX, e.clientY, 8) : null;
      if (pointers.size === 2) { const q2 = Array.from(pointers.values()); pinchMid = { x: (q2[0].x + q2[1].x) / 2, y: (q2[0].y + q2[1].y) / 2 }; }
      canvas.classList.toggle('aiming', !!tiltDrag);
    });
    canvas.addEventListener('pointermove', function (e) {
      const p = pointers.get(e.pointerId);
      if (!p) return;
      const dx = e.clientX - p.x, dy = e.clientY - p.y;
      p.x = e.clientX; p.y = e.clientY;
      if (tap) tap.moved += Math.abs(dx) + Math.abs(dy);
      if (Math.abs(dx) + Math.abs(dy) > 0) markMoved();
      if (panning && pointers.size === 1) { panBy(dx, dy); dirty = true; return; }
      if (tiltDrag && pointers.size === 1) {
        tiltDragBy(tiltDrag, dx, dy);
        const tipEl = $('hs-' + tiltDrag); if (tipEl) tipEl.classList.remove('tip');
        return;
      }
      if (pointers.size === 1) { view.th -= dx * 0.0065; view.ph -= dy * 0.0055; }
      else if (pointers.size === 2) {
        const q = Array.from(pointers.values());
        const d = Math.hypot(q[0].x - q[1].x, q[0].y - q[1].y);
        if (pinchDist > 0) view.r = Math.max(0.5, Math.min(40, view.r * pinchDist / d));
        pinchDist = d;
        // two fingers also pan
        const mid = { x: (q[0].x + q[1].x) / 2, y: (q[0].y + q[1].y) / 2 };
        if (pinchMid) panBy(mid.x - pinchMid.x, mid.y - pinchMid.y);
        pinchMid = mid;
      }
      dirty = true;
    });
    function endPointer(e) {
      pointers.delete(e.pointerId);
      if (pointers.size < 2) { pinchDist = 0; pinchMid = null; }
      if (!pointers.size) {
        canvas.classList.remove('dragging');
        if (e.type === 'pointerup' && tap && tap.moved < 10 && performance.now() - tap.t < 450) canvasTap(e.clientX, e.clientY);
        else if (tiltDrag && units[tiltDrag]) say(unitById[tiltDrag].label + ' aimed ' + Math.round(units[tiltDrag].pitch) + '° down');
        tap = null; tiltDrag = null; panning = false;
        canvas.classList.remove('aiming');
      }
    }
    canvas.addEventListener('pointerup', endPointer);
    canvas.addEventListener('pointercancel', endPointer);
    canvas.addEventListener('wheel', function (e) {
      e.preventDefault();
      stopAuto(); anim = null;
      view.r = Math.max(0.5, Math.min(40, view.r * Math.exp(e.deltaY * 0.0012)));
      markMoved();
      dirty = true;
    }, { passive: false });
    canvas.addEventListener('keydown', function (e) {
      const k = e.key;
      if (k === 'ArrowLeft') view.th += 0.12; else if (k === 'ArrowRight') view.th -= 0.12;
      else if (k === 'ArrowUp') view.ph -= 0.08; else if (k === 'ArrowDown') view.ph += 0.08;
      else if (k === '+' || k === '=') view.r = Math.max(0.5, view.r * 0.9); else if (k === '-') view.r = Math.min(40, view.r * 1.1);
      else if (k === 'Escape') { overview(); return; }
      else return;
      e.preventDefault(); stopAuto(); anim = null; dirty = true; markMoved();
    });

    /* ---------- Loop ---------- */
    let last = performance.now(), firstFrame = true;
    function loop(now) {
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      let active = false;
      if (anim) { stepAnim(now); active = true; }
      else if (auto && !state.focused && !pointers.size) { view.th += dt * 0.09; active = true; }
      if (stepUnits(now)) { active = true; insetDirty = true; }
      if (stepTrailer(now)) { active = true; insetDirty = true; }
      if (active || dirty) {
        applyView();
        renderer.render(scene, camera);
        updateHotspots();
        dirty = false;
        if (firstFrame) { firstFrame = false; $('stageMsg').hidden = true; }
      }
      if (insetOn && insetDirty) { renderInset(); insetDirty = false; }
      requestAnimationFrame(loop);
    }

    /* ---------- Camera models from the GLB files published with this page ---------- */
    /* Little pictures of each camera for the type buttons: drawn once from the same models, in a short-lived renderer */
    function makeThumbs() {
      // only the real models: if one didn't load, its button just shows the name
      const types = TYPE_KEYS.filter(function (k) { return models[k]; });
      if (!types.length) return;
      let tr = null;
      const TW = 216, TH = 132;
      try { tr = new T.WebGLRenderer({ antialias: true, alpha: true, preserveDrawingBuffer: true }); } catch (err) { return; }
      try {
        tr.setPixelRatio(1);
        tr.setSize(TW, TH, false);
        tr.outputEncoding = T.sRGBEncoding;
        tr.toneMapping = T.ACESFilmicToneMapping;
        tr.toneMappingExposure = 1.2;
        tr.setClearColor(0x000000, 0);
        const ts = new T.Scene();
        const env = makeEnv(tr, false);
        ts.environment = env;
        ts.add(new T.HemisphereLight(0xffffff, 0x8f9a9a, 0.9));
        const key = new T.DirectionalLight(0xffffff, 1.7); key.position.set(2.2, 3, 2.6); ts.add(key);
        const back = new T.DirectionalLight(0xffffff, 0.6); back.position.set(-3, 1.2, -2.5); ts.add(back);
        const tc = new T.PerspectiveCamera(24, TW / TH, 0.001, 10);
        const look = new T.Vector3(0.66, 0.4, 1).normalize(), box = new T.Box3(), sph = new T.Sphere(), p = new T.Vector3();
        types.forEach(function (type) {
          const m = cameraModel(type);
          ts.add(m.obj);
          m.obj.updateMatrixWorld(true);
          box.setFromObject(m.obj).getBoundingSphere(sph);
          tc.clearViewOffset();
          tc.position.copy(sph.center).addScaledVector(look, sph.radius / Math.sin(12 * RAD));
          tc.lookAt(sph.center);
          tc.updateMatrixWorld(true);
          // fill the picture: crop the view to the camera's outline on screen, with a little margin
          let x0 = 1, x1 = -1, y0 = 1, y1 = -1;
          for (let c = 0; c < 8; c++) {
            p.set(c & 1 ? box.max.x : box.min.x, c & 2 ? box.max.y : box.min.y, c & 4 ? box.max.z : box.min.z).project(tc);
            x0 = Math.min(x0, p.x); x1 = Math.max(x1, p.x); y0 = Math.min(y0, p.y); y1 = Math.max(y1, p.y);
          }
          const s = 0.92 / Math.max((x1 - x0) / 2, (y1 - y0) / 2);
          tc.setViewOffset(TW, TH, ((x0 + x1) / 2 + 1) / 2 * TW - TW / (2 * s), (1 - (y0 + y1) / 2) / 2 * TH - TH / (2 * s), TW / s, TH / s);
          tr.render(ts, tc);
          const el = document.getElementById('pic-' + type);
          if (el) { el.style.backgroundImage = 'url(' + tr.domElement.toDataURL('image/png') + ')'; el.classList.add('is-ready'); }
          ts.remove(m.obj);
        });
        env.dispose();
      } catch (err) { if (window.console) console.warn('Camera pictures not drawn', err); }
      tr.dispose();
      if (tr.forceContextLoss) tr.forceContextLoss();
    }
    function loadModels() {
      if (!T.GLTFLoader) return;
      const loader = new T.GLTFLoader();
      let settled = 0;
      const done = function () { if (++settled === Object.keys(MODEL_FILES).length) makeThumbs(); };
      Object.keys(MODEL_FILES).forEach(function (type) {
        loader.load(MODEL_FILES[type], function (gltf) {
          const root = gltf.scene.getObjectByName(type + '-cam') || gltf.scene.children[0];
          if (!root || !root.getObjectByName('head')) { done(); return; }
          const ex = root.userData || {}, m = META[type];
          const info = { pivot: ex.pivot || m.pivot, lenses: ex.lenses || m.lenses, b: ex.b || m.b, attach: ex.attach || m.attach };
          if (root.parent) root.parent.remove(root);
          root.position.set(0, 0, 0); root.quaternion.identity(); root.scale.set(1, 1, 1);
          root.traverse(function (o) { if (o.isMesh) o.material = CM[o.material && o.material.name] || CM.body; });
          models[type] = { root: root, info: info };
          rebuildType(type);
          dirty = true; insetDirty = true;
          window.__models = (window.__models || 0) + 1;
          done();
        }, undefined, function (err) { if (window.console) console.warn('Camera model ' + type + ' did not load', err); done(); });
      });
    }

    if (PAINT[state.paint]) { M.body.color.set(PAINT[state.paint].mat); M.body.envMapIntensity = PAINT[state.paint].env; }   // a shared link can bring its own paint
    layout();
    const ro = new ResizeObserver(function () { layout(); });
    ro.observe($('top')); ro.observe($('sheet')); ro.observe(document.body);
    window.addEventListener('resize', layout);
    requestAnimationFrame(loop);

    return {
      sync: function () { sync(); dirty = true; insetDirty = true; },
      focus: function (id) {
        stopAuto();
        sync();
        measure();
        if (byId[id]) showInset(id); else hideInset();
        flyId = id;
        flyTo(focusPose(id));
        dirty = true;
      },
      overview: function () {
        sync();
        hideInset();
        measure();
        flyId = null;
        flyTo(overviewPose());
        dirty = true;
      },
      setBody: function (id) {
        useVehicle(id);
        applyTrailer();
        buildAllUnits();
        hitchKey = '';
        buildHitch();
        dirty = true; insetDirty = true;
      },
      setTrailer: function (id) {
        useTrailer(id);
        ['trailer', 'tsideL', 'tsideR'].forEach(buildUnit);
        sync();
        dirty = true; insetDirty = true;
      },
      setType: function (cam) {
        UNITS.forEach(function (u) { if (u.cam === cam) buildUnit(u.id); });
        sync();
        dirty = true; insetDirty = true;
      },
      // the vehicle's paint (the trailer keeps its own white)
      paint: function (hex, env) { M.body.color.set(hex); if (env != null) M.body.envMapIntensity = env; dirty = true; insetDirty = true; },
      // Twin cameras come in black or white: rebuild every unit that's a Twin in the new colour
      recolour: function () {
        UNITS.forEach(function (u) { if (state.type[u.cam] === 'twin') buildUnit(u.id); });
        sync();
        dirty = true; insetDirty = true;
      },
      refreshInset: function () {
        if (state.focused && byId[state.focused]) showInset(state.focused);
        dirty = true;
      },
      loadModels: loadModels,
      debug: function () { return { units: units, veh: veh, trl: trl, hitch: hitch, view: view, camera: camera, renderer: renderer, models: models, gap: socketGap() }; }
    };
  }

  function loadScript(srcs, ok, fail) {
    const src = srcs.shift();
    if (!src) { if (fail) fail(); return; }
    const s = document.createElement('script');
    s.src = src;
    s.onload = ok;
    s.onerror = function () { s.remove(); loadScript(srcs, ok, fail); };
    document.head.appendChild(s);
  }
  function boot() {
    buildUI();
    syncUI();
    function start() {
      try { three = initThree(); } catch (err) { three = null; if (window.console) console.error(err); }
      if (!three) {
        $('stageMsg').hidden = false;
        $('stageMsg').textContent = 'The 3D view couldn’t start in this browser. You can still pick your cameras below.';
        $('stageNote').hidden = true;
        document.querySelectorAll('.hs, .coil-tag').forEach(function (h) { h.hidden = true; });
        return;
      }
      window.__three = three;
      loadScript(['https://cdn.jsdelivr.net/npm/three@0.128.0/examples/js/loaders/GLTFLoader.js', 'https://unpkg.com/three@0.128.0/examples/js/loaders/GLTFLoader.js'], function () { if (three && window.THREE.GLTFLoader) three.loadModels(); });
    }
    if (window.THREE) { start(); return; }
    loadScript(['https://cdn.jsdelivr.net/npm/three@0.128.0/build/three.min.js'], start, start);
  }
  boot();
})();
